import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { performAction, performStreamingAction } from "../api/sophia";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import Timeline, { TimelineNode } from "./Timeline";
import Treemap from "./Treemap";
import TsneInlineView from "./TsneInlineView";
import ConstraintSwatches from "./ConstraintSwatches";
import { DetailsModal } from "./DetailsModal";
import { AddProductModal } from "./AddProductModal";
import { AlertModal } from "./AlertModal";
import { selectThreadAppState, executeSophiaAction, fetchThread } from "../features/thread/threadSlice";
import { getTagColor, getContrastColor } from "../utils/tagRendering";

// Ordinamento taglie e corpo del tooltip ordini vivono nel componente condiviso, cosi'
// questa pagina e Perimetro & Budget mostrano davvero la stessa cosa.
import { CommitInput } from './CommitInput';
import { ORDERS_PANEL_MAX_HEIGHT, ORDERS_PANEL_WIDTH, OrderTooltipContent, OrdersByShopPanel, PinOrdersHint, TooltipShell, compareTaglie, computeTooltipPosition, type OrderTooltipData } from './SkuOrderForecastBadges';
import { ShopListFilter, matchesShop, nazioneOfShopId, nazioniOf, useShopNazione } from './ShopPicker';
import { ForecastProductsPanel, type ForecastSimilarProductsResponse } from './ForecastProductsPanel';
import { buildSkuOrdersByShopParams, parseShopIds, type SkuOrdersShopEntry } from './ordersByShop';
import { normalizzaQuantitaTaglia } from '../utils/taglie';

const TAGLIE_META_KEYS = new Set(["cod_tipo_taglia", "codtipotaglia"]);
const isTagliaMetaKey = (key: string | number) => TAGLIE_META_KEYS.has(String(key).toLowerCase());

const normalizeCodTipoTaglia = (value?: string | number | null) => {
  if (value === undefined || value === null) return "";
  const text = String(value).trim();
  if (!text) return "";
  return /^\d+$/.test(text) ? String(Number(text)).padStart(2, "0") : text;
};

const getCodTipoTagliaLabel = (groupId: string | number) => {
  const normalized = normalizeCodTipoTaglia(groupId);
  return normalized && normalized !== "00" ? `Griglia ${normalized}` : "Griglia taglie";
};

const sortTaglieRecord = (
  value?: Record<string | number, Record<string, number>>
) => {
  if (!value || typeof value !== "object") {
    return value;
  }
  const sorted: Record<string, Record<string, number>> = {};
  Object.entries(value).forEach(([outerKey, innerDict]) => {
    if (!innerDict || typeof innerDict !== "object") {
      return;
    }
    const sortedInner = Object.fromEntries(
      Object.entries(innerDict).sort(([a], [b]) => compareTaglie(String(a), String(b)))
    );
    sorted[outerKey] = sortedInner;
  });
  return sorted;
};
interface BudgetConstraint {
  activity: number;
  capacity: number;
  slack: number;
  percentage_remaining: number;
}

interface BudgetConstraintWithItems {
  value: BudgetConstraint;
  items: BudgetConstraint;
}

export interface MetricsData {
  profit: number;
  budget?: Record<string, Record<string, number>>; // dict[str, dict[str, float]]
  mp_constraint?: Record<string, Record<string, Record<string, number>>>; // dict[str, dict[str, dict[str, float]]]
  mean_price: number;
  mean_weight?: number;
  unique_skus: number;
  total_quantity: number;
  taglie_distribution?: Record<string | number, Record<string, number>>;
  url_img_list?: string;
  sketch_url?: string;
  descrizione_colore?: string | null;
  weborder_tags?: string[];
  total_sellout_value?: number;
  sellout_per_tipo_filato?: Record<string, number>;
  real_profit?: number;
  total_sellout_value_mean?: number;
  forecast_quantity?: number;
  models_unique?: number;
  model?: string;
  order_quantity?: number;
  taglie_order?: Record<string | number, Record<string, number>>;
  order_quantity_venduta?: number;
  taglie_order_venduta?: Record<string | number, Record<string, number>>;
  linked_order_quantity?: number;
  taglie_linked_order?: Record<string | number, Record<string, number>>;
}

export interface SkuData {
  name: string;
  metrics: MetricsData;
  taglie_distribution?: Record<string, Record<string, number>>;
}

interface TaglieReferenceDistribution {
  scope_level?: string;
  cod_negozio?: string;
  des_canale?: string;
  area?: string;
  classeabc?: string;
  gender?: string;
  cod_tipo_filato?: string;
  cod_tipo_taglia?: string;
  cod_stagione_target?: number;
  storico_totale?: number;
  counts?: Record<string, number>;
  distribution?: Record<string, number>;
}

export interface TFData {
  name: string;
  all: MetricsData;
  sku: SkuData[];
  taglie_reference_distributions?: TaglieReferenceDistribution[];
}

export interface ConsegnaData {
  name: string;
  all: MetricsData;
  tf: TFData[];
}

interface BudgetConstraint {
  activity: number;
  capacity: number;
  slack: number;
  percentage_remaining: number;
}
export interface MetricsArea {
  profit: number;
  mp_constraint?: any;
  quantities: number[];
  skus: string[];
}

export interface MetricsOptimization {
  name: string;
  metrics: MetricsData;
}

export interface GeneralMetrics {
  area: MetricsArea[] | null;
  lines: Record<string, MetricsData>; // dict[str, MetricsData]
  budget?: Record<string, Record<string, number>>; // dict[str, dict[str, float]]
  mp_constraint?: any; // constraint categories plus optional _meta
}

export interface ApiResponse {
  run_id: number;
  metrics: GeneralMetrics[]; // you had GeneralMetrics[] (list)
  metrics_per_consegna_per_shop: Record<string, ConsegnaData[]>; // shop data keyed by shop_id
  shop_mapping?: Record<string, string>; // mapping of shop_id to display name (ALL CAPS)
}

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const metricNumber = (metrics: MetricsData | undefined, key: keyof MetricsData): number => {
  const value = metrics?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const mergeTaglieDistributionRecords = (
  left?: Record<string | number, Record<string, number>>,
  right?: Record<string | number, Record<string, number>>,
): Record<string, Record<string, number>> => {
  const merged: Record<string, Record<string, number>> = {};
  [left, right].forEach((distribution) => {
    if (!distribution || typeof distribution !== 'object') return;
    Object.entries(distribution).forEach(([groupId, sizes]) => {
      if (!sizes || typeof sizes !== 'object') return;
      if (!merged[groupId]) merged[groupId] = {};
      Object.entries(sizes).forEach(([size, count]) => {
        if (size.toLowerCase() === 'cod_tipo_taglia' || size.toLowerCase() === 'codtipotaglia') {
          merged[groupId][size] = count;
          return;
        }
        merged[groupId][size] = Number(merged[groupId][size] || 0) + Number(count || 0);
      });
    });
  });
  return merged;
};

const getTaglieDistributionTotal = (distribution?: Record<string | number, Record<string, number>>): number => {
  if (!distribution || typeof distribution !== 'object') return 0;
  return Object.values(distribution).reduce((total, sizes) => {
    if (!sizes || typeof sizes !== 'object') return total;
    return total + Object.entries(sizes).reduce((sum, [size, count]) => {
      if (isTagliaMetaKey(size)) return sum;
      return sum + Number(count || 0);
    }, 0);
  }, 0);
};

const zeroTaglieDistributionRecord = (
  distribution?: Record<string | number, Record<string, number>>,
): Record<string, Record<string, number>> => {
  const zeroed: Record<string, Record<string, number>> = {};
  if (!distribution || typeof distribution !== 'object') return zeroed;
  Object.entries(distribution).forEach(([groupId, sizes]) => {
    if (!sizes || typeof sizes !== 'object') return;
    zeroed[groupId] = {};
    Object.entries(sizes).forEach(([size, count]) => {
      zeroed[groupId][size] = isTagliaMetaKey(size)
        ? count
        : 0;
    });
  });
  return zeroed;
};

const zeroOptimizedSkuQuantities = (sku: SkuData): SkuData => ({
  ...sku,
  metrics: {
    ...sku.metrics,
    profit: 0,
    unique_skus: 0,
    total_quantity: 0,
    taglie_distribution: zeroTaglieDistributionRecord(sku.metrics?.taglie_distribution),
    total_sellout_value_mean: 0,
  },
});

const aggregateSkuMetrics = (skus: SkuData[]): MetricsData => {
  const totalQuantity = skus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'total_quantity'), 0);
  const profit = skus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'profit'), 0);
  const weightedMeanWeight = skus.reduce(
    (sum, sku) => sum + metricNumber(sku.metrics, 'mean_weight') * metricNumber(sku.metrics, 'total_quantity'),
    0,
  );
  const selloutMean = skus.reduce(
    (sum, sku) => sum + metricNumber(sku.metrics, 'total_sellout_value_mean') * metricNumber(sku.metrics, 'total_quantity'),
    0,
  );
  const positiveSkus = skus.filter((sku) => metricNumber(sku.metrics, 'total_quantity') > 0);
  const models = new Set(
    skus
      .map((sku) => sku.metrics?.model)
      .filter((model): model is string => typeof model === 'string' && model.length > 0),
  );

  return {
    profit,
    mean_price: totalQuantity > 0 ? profit / totalQuantity : 0,
    mean_weight: totalQuantity > 0 ? weightedMeanWeight / totalQuantity : 0,
    unique_skus: positiveSkus.length,
    total_quantity: totalQuantity,
    taglie_distribution: skus.reduce<Record<string, Record<string, number>>>(
      (acc, sku) => mergeTaglieDistributionRecords(acc, sku.metrics?.taglie_distribution),
      {},
    ),
    forecast_quantity: positiveSkus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'forecast_quantity'), 0),
    models_unique: models.size || positiveSkus.length,
    order_quantity: skus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'order_quantity'), 0),
    taglie_order: skus.reduce<Record<string, Record<string, number>>>(
      (acc, sku) => mergeTaglieDistributionRecords(acc, sku.metrics?.taglie_order),
      {},
    ),
    order_quantity_venduta: skus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'order_quantity_venduta'), 0),
    taglie_order_venduta: skus.reduce<Record<string, Record<string, number>>>(
      (acc, sku) => mergeTaglieDistributionRecords(acc, sku.metrics?.taglie_order_venduta),
      {},
    ),
    linked_order_quantity: skus.reduce((sum, sku) => sum + metricNumber(sku.metrics, 'linked_order_quantity'), 0),
    taglie_linked_order: skus.reduce<Record<string, Record<string, number>>>(
      (acc, sku) => mergeTaglieDistributionRecords(acc, sku.metrics?.taglie_linked_order),
      {},
    ),
    total_sellout_value_mean: totalQuantity > 0 ? selloutMean / totalQuantity : 0,
  };
};

const recomputeTfMetrics = (tf: TFData): TFData => ({
  ...tf,
  all: {
    ...tf.all,
    ...aggregateSkuMetrics(tf.sku),
  },
});

const recomputeConsegnaMetrics = (consegna: ConsegnaData): ConsegnaData => {
  const recomputedTfs = consegna.tf.map(recomputeTfMetrics);
  return {
    ...consegna,
    tf: recomputedTfs,
    all: {
      ...consegna.all,
      ...aggregateSkuMetrics(recomputedTfs.flatMap((tf) => tf.sku)),
    },
  };
};

const combineSkuData = (existing: SkuData, incoming: SkuData): SkuData => {
  const existingQty = metricNumber(existing.metrics, 'total_quantity');
  const incomingQty = metricNumber(incoming.metrics, 'total_quantity');
  const totalQuantity = existingQty + incomingQty;
  const profit = metricNumber(existing.metrics, 'profit') + metricNumber(incoming.metrics, 'profit');

  return {
    ...existing,
    metrics: {
      ...existing.metrics,
      ...incoming.metrics,
      profit,
      total_quantity: totalQuantity,
      unique_skus: totalQuantity > 0 ? 1 : 0,
      mean_price: totalQuantity > 0 ? profit / totalQuantity : 0,
      mean_weight: totalQuantity > 0
        ? (metricNumber(existing.metrics, 'mean_weight') * existingQty + metricNumber(incoming.metrics, 'mean_weight') * incomingQty) / totalQuantity
        : 0,
      total_sellout_value_mean: totalQuantity > 0
        ? (metricNumber(existing.metrics, 'total_sellout_value_mean') * existingQty + metricNumber(incoming.metrics, 'total_sellout_value_mean') * incomingQty) / totalQuantity
        : 0,
      forecast_quantity: Math.max(metricNumber(existing.metrics, 'forecast_quantity'), metricNumber(incoming.metrics, 'forecast_quantity')),
      order_quantity: Math.max(metricNumber(existing.metrics, 'order_quantity'), metricNumber(incoming.metrics, 'order_quantity')),
      order_quantity_venduta: Math.max(metricNumber(existing.metrics, 'order_quantity_venduta'), metricNumber(incoming.metrics, 'order_quantity_venduta')),
      linked_order_quantity: Math.max(metricNumber(existing.metrics, 'linked_order_quantity'), metricNumber(incoming.metrics, 'linked_order_quantity')),
      taglie_distribution: mergeTaglieDistributionRecords(existing.metrics?.taglie_distribution, incoming.metrics?.taglie_distribution),
      taglie_order: mergeTaglieDistributionRecords(existing.metrics?.taglie_order, incoming.metrics?.taglie_order),
      taglie_order_venduta: mergeTaglieDistributionRecords(existing.metrics?.taglie_order_venduta, incoming.metrics?.taglie_order_venduta),
      taglie_linked_order: mergeTaglieDistributionRecords(existing.metrics?.taglie_linked_order, incoming.metrics?.taglie_linked_order),
    },
  };
};

const rebuildAllConsegnaForShop = (consegnas: ConsegnaData[]): ConsegnaData[] => {
  const allIndex = consegnas.findIndex((consegna) => consegna.name.toLowerCase() === 'all');
  if (allIndex === -1) return consegnas;

  const tfByName = new Map<string, TFData>();
  consegnas
    .filter((consegna) => consegna.name.toLowerCase() !== 'all')
    .forEach((consegna) => {
      consegna.tf.forEach((tf) => {
        const aggregateTf = tfByName.get(tf.name) ?? { ...tf, all: cloneJson(tf.all), sku: [] };
        tf.sku.forEach((sku) => {
          const existingSkuIndex = aggregateTf.sku.findIndex((candidate) => candidate.name === sku.name);
          if (existingSkuIndex >= 0) {
            aggregateTf.sku[existingSkuIndex] = combineSkuData(aggregateTf.sku[existingSkuIndex], cloneJson(sku));
          } else {
            aggregateTf.sku.push(cloneJson(sku));
          }
        });
        tfByName.set(tf.name, aggregateTf);
      });
    });

  const rebuiltAll = recomputeConsegnaMetrics({
    ...consegnas[allIndex],
    tf: Array.from(tfByName.values()),
  });

  return consegnas.map((consegna, index) => (index === allIndex ? rebuiltAll : consegna));
};

/**
 * Capitalize a single shop name properly (convert ALL CAPS to Title Case)
 * e.g., "MILAN FLAGSHIP STORE" -> "Milan Flagship Store"
 */
function formatSingleShopName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get the display name for a single shop ID, using the mapping if available
 */
function getSingleShopDisplayName(shopId: string, shopMapping?: Record<string, string>): string {
  const cleanId = shopId.replace(/\|$/, '').trim();
  if (shopMapping && shopMapping[cleanId]) {
    return formatSingleShopName(shopMapping[cleanId]);
  }
  // Fallback to shop ID formatted as title case
  return formatSingleShopName(cleanId);
}

/**
 * Get the display name for a shop, using the mapping if available
 * If the shopId contains '|,|' separator, parse as list and look up each part in the mapping:
 * 'BR2|,|DME|' -> looks up BR2 and DME in mapping -> 'Brussels 2, Dame'
 */
function getShopDisplayName(shopId: string, shopMapping?: Record<string, string>): string {
  // Check if shopId contains the |,| separator pattern
  if (shopId.includes('|,|')) {
    // Split by |,| and clean up trailing |
    const shopIds = shopId
      .split('|,|')
      .map(id => id.replace(/\|$/, '').trim())  // Remove trailing | and whitespace
      .filter(id => id.length > 0);  // Remove empty entries
    
    // Look up each shop ID in the mapping and format
    return shopIds.map(id => getSingleShopDisplayName(id, shopMapping)).join(', ');
  }
  
  // Single shop ID - look up in mapping and format
  return getSingleShopDisplayName(shopId, shopMapping);
}

interface ExpandedState {
  [key: string]: boolean;
}

type SkuSortColumn =
  | 'profit'
  | 'mean_price'
  | 'mean_weight'
  | 'sellout'
  | 'models_unique'
  | 'unique_skus'
  | 'total_quantity'
  | 'forecast_quantity'
  | 'order_quantity'
  | 'linked_order_quantity';

type SortDirection = 'asc' | 'desc';

interface SkuSortState {
  column: SkuSortColumn;
  direction: SortDirection;
}

const DEFAULT_SKU_SORT: SkuSortState = { column: 'total_quantity', direction: 'desc' };

interface OptimizationResultViewProps {
  onOpenMerchandisingPlan?: (context?: { runId: number | null; selectedShopId: string | null; selectedResultTab: number }) => void;
  initialRunId?: number | null;
  initialSelectedShopId?: string | null;
  initialActiveTab?: number;
}

const TREEMAP_WIDTH_STORAGE_KEY = 'optimization-result-treemap-width';
const TREEMAP_MIN_WIDTH = 240;
const VINCOLI_MIN_WIDTH = 350;

// SPJ-1719: prezzo nel tooltip dell'immagine, nello stesso formato delle colonne prezzo.
const formatPreviewPrice = (value: number) => `€${value.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

export default function OptimizationResultView({
  onOpenMerchandisingPlan,
  initialRunId = null,
  initialSelectedShopId = null,
  initialActiveTab,
}: OptimizationResultViewProps) {
  const dispatch = useAppDispatch();
  // Add these with your other useState declarations
  // Window position and dimensions
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number }>({ x: 1150, y: 20 });
  const [cardWidth, setCardWidth] = useState<number>(620);
  const [cardHeight, setCardHeight] = useState<number>(500);

  // Drag and resize states
  const [isDraggingCard, setIsDraggingCard] = useState<boolean>(false);
  const [isResizingCard, setIsResizingCard] = useState<boolean>(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [dragStart, setDragStart] = useState<{ x: number; y: number; cardX: number; cardY: number } | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number; cardX: number; cardY: number } | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineHeight, setTimelineHeight] = useState<number | undefined>(undefined);
  const [treemapData, setTreemapData] = useState<any>(null);
  const [treemapLoading, setTreemapLoading] = useState<boolean>(false);
  const [showTsneView, setShowTsneView] = useState<boolean>(false);
  const [tsneData, setTsneData] = useState<any>(null);
  const [tsneLoading, setTsneLoading] = useState<boolean>(false);
  const [selectedBlockLabel, setSelectedBlockLabel] = useState<string>('');
  const [selectedBlockColor, setSelectedBlockColor] = useState<string>('');
  const [generalMetrics, setGeneralMetrics] = useState<any>(null);
  const [generalMetricsLoading, setGeneralMetricsLoading] = useState<boolean>(false);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const thread = useAppSelector((state) => state.thread);
  const [isManualEditMode, setIsManualEditMode] = useState<boolean>(false);
  const [isOptimizationMode, setIsOptimizationMode] = useState<boolean>(false);
  const [isConfigViewMode, setIsConfigViewMode] = useState<boolean>(false);
  const [editedData, setEditedData] = useState<ApiResponse | null>(null);
  const [constraints, setConstraints] = useState<any>(null);
  const [isLoadingConstraints, setIsLoadingConstraints] = useState<boolean>(false);
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
  const [optimizationProgress, setOptimizationProgress] = useState<number>(0);
  const [hasConstraintsBeenForked, setHasConstraintsBeenForked] = useState<boolean>(false);
  const [hasTaglieBeenForked, setHasTaglieBeenForked] = useState<boolean>(false);
  const [detailsModalSku, setDetailsModalSku] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [excludedSkus, setExcludedSkus] = useState<Set<string>>(new Set());
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState<boolean>(false);
  const [highlightedTf, setHighlightedTf] = useState<string | string[] | null>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const topContainerRef = useRef<HTMLDivElement>(null);
  const [topContainerHeight, setTopContainerHeight] = useState<number>(0);
  // Treemap "Selezione prodotti": piu' stretto e un po' piu' basso, per lasciare spazio a
  // Timeline e Vincoli nella fascia in alto (richiesta di Eugenio, era fino a 700x500).
  // La larghezza si puo' cambiare trascinando il bordo destro; resta salvata nel browser.
  const [treemapUserWidth, setTreemapUserWidth] = useState<number | null>(() => {
    try {
      const saved = Number(localStorage.getItem(TREEMAP_WIDTH_STORAGE_KEY));
      return Number.isFinite(saved) && saved > 0 ? saved : null;
    } catch {
      return null;
    }
  });
  const [treemapResizeStart, setTreemapResizeStart] = useState<{ x: number; width: number } | null>(null);
  const treemapMaxWidth = Math.max(TREEMAP_MIN_WIDTH, Math.min(1000, window.innerWidth - 200));
  const treemapWidth = treemapUserWidth !== null
    ? Math.min(Math.max(treemapUserWidth, TREEMAP_MIN_WIDTH), treemapMaxWidth)
    : Math.max(280, Math.min(480, window.innerWidth - 200));
  const treemapHeight = topContainerHeight > 0 ? Math.min(topContainerHeight, 420) : 300;
  // SPJ-1716: larghezza reale del blocco Vincoli (flex-1), misurata: i riquadri si
  // ridistribuiscono sullo spazio libero. Prima della misura vale la vecchia stima.
  // Callback ref: il blocco compare solo a dati caricati, l'osservatore si aggancia quando c'e'.
  const [vincoliMeasuredWidth, setVincoliMeasuredWidth] = useState(0);
  const vincoliObserverRef = useRef<ResizeObserver | null>(null);
  const vincoliContainerRef = useCallback((el: HTMLDivElement | null) => {
    vincoliObserverRef.current?.disconnect();
    vincoliObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setVincoliMeasuredWidth(Math.floor(entry.contentRect.width)));
    observer.observe(el);
    vincoliObserverRef.current = observer;
  }, []);
  const vincoliWidth = vincoliMeasuredWidth > 0
    ? Math.max(VINCOLI_MIN_WIDTH, vincoliMeasuredWidth)
    : Math.max(VINCOLI_MIN_WIDTH, Math.min(600, window.innerWidth - 400));
  const appState = useAppSelector(selectThreadAppState);
  const [constraintErrors, setConstraintErrors] = useState<Record<string, string>>({});
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    errorLog?: string;
    type: 'error' | 'warning' | 'info' | 'success';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'error',
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<{sku: string, consegnaIndex: number, tfIndex: number, isTf?: boolean}>>([]);
  // SPJ-888: filtro righe per caratteristiche SKU — campi separati modello / colore / tessuto (in AND)
  const [modelFilter, setModelFilter] = useState<string>('');
  const [colorFilter, setColorFilter] = useState<string>('');
  const [tipoFilatoFilter, setTipoFilatoFilter] = useState<string>('');
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);
  const [topSectionHeight, setTopSectionHeight] = useState<number>(50); // Percentage of total height
  const [isTableFullscreen, setIsTableFullscreen] = useState<boolean>(false); // SPJ-889: collassa la parte superiore, tabella a tutto schermo
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [tipoFilatoOverrides, setTipoFilatoOverrides] = useState<Array<{
    id: number;
    tipo_filato: string;
    value: number;
  }>>([]);
  const [minAllowedValueTipoFilatoOverrides, setMinAllowedValueTipoFilatoOverrides] = useState<Array<{
    id: number;
    tipo_filato: string;
    value: number;
  }>>([]);
  const [tipoFilatoEnum, setTipoFilatoEnum] = useState<string[]>([]);
  // SKU list upload states
  const [pastedSkuText, setPastedSkuText] = useState('');
  
  // Store/Area selection
  const [selectedStoreIndex, setSelectedStoreIndex] = useState<number>(0); // 0 = Area (summary), 1+ = individual stores
  const [allTabSkuTargetConsegna, setAllTabSkuTargetConsegna] = useState<Record<string, string>>({});
  const [skuSortByView, setSkuSortByView] = useState<Record<string, SkuSortState>>({});

  // Constraint explanation state
  const [constraintExplanations, setConstraintExplanations] = useState<Record<string, string>>({});

  // Order popup state (for portal rendering)
  const [orderPopup, setOrderPopup] = useState<{
    visible: boolean;
    x: number;
    y: number;
    orderQuantity: number;
    taglieOrder: Record<string | number, Record<string, number>> | undefined;
    orderQuantityVenduta: number | undefined;
    taglieOrderVenduta: Record<string | number, Record<string, number>> | undefined;
    linkedOrderQuantity: number | undefined;
    taglieLinkedOrder: Record<string | number, Record<string, number>> | undefined;
    canPin?: boolean;
  } | null>(null);
  // SPJ-1364: popup ordini fissato al click, col selettore degli altri negozi.
  const [pinnedOrders, setPinnedOrders] = useState<{ rect: DOMRect; sku: string; data: OrderTooltipData } | null>(null);
  // SPJ-1726/1728: ricerca e filtro nazione sui tab dei negozi (ottimizzazioni per area).
  const [storeTabSearch, setStoreTabSearch] = useState('');
  const [storeTabNazione, setStoreTabNazione] = useState('');
  const shopNazione = useShopNazione(thread.threadId, thread.threadData?.local_memory?.thread_state?.["current-message"] as string | undefined);
  // SPJ-1864: popup dei prodotti usati per il forecast, come nel merchandising plan.
  const [pinnedForecast, setPinnedForecast] = useState<{ rect: DOMRect; sku: string; forecast: number } | null>(null);
  // Anteprima ingrandita dell'immagine dello SKU al passaggio del mouse sulla miniatura.
  // In tabella la miniatura e' 24x40: troppo piccola per riconoscere il capo.
  const [imagePreview, setImagePreview] = useState<{
    url: string;
    label: string;
    x: number;
    y: number;
    // SPJ-1719: descrizione colore e prezzi RTL/WHS dello SKU sotto l'immagine ingrandita.
    colore?: string | null;
    rtl?: number;
    whs?: number;
  } | null>(null);
  const [imagePreviewPortalContainer] = useState(() => {
    if (typeof document === 'undefined') return null;
    const div = document.createElement('div');
    div.setAttribute('data-image-preview-portal', 'true');
    return div;
  });
  const [orderPopupPortalContainer] = useState(() => {
    if (typeof document === 'undefined') return null;
    const div = document.createElement('div');
    div.setAttribute('data-order-popup-portal', 'true');
    return div;
  });
  const hasAppliedInitialRunRef = useRef(false);
  const hasAppliedInitialStoreRef = useRef(false);
  const hasAppliedInitialTabRef = useRef(false);

  useEffect(() => {
    if (!imagePreviewPortalContainer || typeof document === 'undefined') return;
    document.body.appendChild(imagePreviewPortalContainer);

    return () => {
      if (imagePreviewPortalContainer.parentNode === document.body) {
        document.body.removeChild(imagePreviewPortalContainer);
      }
    };
  }, [imagePreviewPortalContainer]);

  useEffect(() => {
    if (!orderPopupPortalContainer || typeof document === 'undefined') return;
    document.body.appendChild(orderPopupPortalContainer);

    return () => {
      if (orderPopupPortalContainer.parentNode === document.body) {
        document.body.removeChild(orderPopupPortalContainer);
      }
    };
  }, [orderPopupPortalContainer]);

  // Cleanup effect: reset all temporary state when component unmounts
  useEffect(() => {
    return () => {
      // Reset UI state
      setCardPosition({ x: 1150, y: 20 });
      setCardWidth(620);
      setCardHeight(500);
      setIsDraggingCard(false);
      setIsResizingCard(false);
      setResizeDirection('');
      setDragStart(null);
      setResizeStart(null);
      setActiveTab(0);
      setExpanded({});
      setTopSectionHeight(50);
      setIsDragging(false);
      
      // Reset data state
      setData(null);
      setEditedData(null);
      setTreemapData(null);
      setTsneData(null);
      setGeneralMetrics(null);
      setConstraints(null);
      setTipoFilatoOverrides([]);
      setTipoFilatoEnum([]);
      
      // Reset SKU list states
      
      // Reset loading states
      setLoading(true);
      setTreemapLoading(false);
      setTsneLoading(false);
      setGeneralMetricsLoading(false);
      setIsLoadingConstraints(false);
      setIsOptimizing(false);
      
      // Reset error states
      setError(null);
      setConstraintErrors({});
      
      // Reset mode states
      setIsManualEditMode(false);
      setIsOptimizationMode(false);
      setIsConfigViewMode(false);
      setHasConstraintsBeenForked(false);
      setHasTaglieBeenForked(false);
      
      // Reset modal states
      setIsDetailsModalOpen(false);
      setDetailsModalSku(null);
      setIsAddProductModalOpen(false);
      setAlertModal({
        isOpen: false,
        title: '',
        message: '',
        type: 'error',
      });
      
      // Reset visualization states
      setShowTsneView(false);
      setSelectedBlockLabel('');
      setSelectedBlockColor('');
      setHighlightedTf(null);
      setTimelineHeight(undefined);
      setTopContainerHeight(0);
      
      // Reset optimization states
      setOptimizationProgress(0);
      setExcludedSkus(new Set());
      
      // Reset search states
      setSearchQuery('');
      setSearchResults([]);
      setCurrentSearchIndex(0);
      
      // Reset store selection
      setSelectedStoreIndex(0);
      setAllTabSkuTargetConsegna({});
    };
  }, []);


  // Get the currently selected run_id from app state
  const currentSelectedRunId = useMemo<number | null>(() => {
    const optimizationId = appState?.['optim-optimization-id'];
    return optimizationId ? Number(optimizationId) : null;
  }, [appState]);

  useEffect(() => {
    if (hasAppliedInitialRunRef.current) {
      return;
    }
    if (!initialRunId || !thread.threadId || !thread.threadData) {
      hasAppliedInitialRunRef.current = true;
      return;
    }
    if (currentSelectedRunId === initialRunId) {
      hasAppliedInitialRunRef.current = true;
      return;
    }

    hasAppliedInitialRunRef.current = true;
    void dispatch(
      executeSophiaAction({
        actionName: 'optim-select-run',
        params: {
          'optim-run-id': initialRunId,
        },
        refreshThread: true,
      }),
    ).unwrap().catch((err) => {
      console.error('Failed to restore optimization run:', err);
    });
  }, [currentSelectedRunId, dispatch, initialRunId, thread.threadData, thread.threadId]);

  // Transform history from app state to TimelineNode format
  const timelineNodes = useMemo<TimelineNode[]>(() => {
    const history = appState?.['history'];
    if (!Array.isArray(history) || history.length === 0) {
      return [];
    }

    return history.map((item: any) => ({
      id: item.run_id?.toString() || '',
      type: item.run_type === 'optimization' ? 'optimization' as const : 'manual' as const,
      timestamp: item.timestamp || new Date().toISOString().slice(0, 16).replace('T', ' '),
      description: item.description || '',
      status: (item.status as 'running' | 'succeeded' | 'failed') || 'succeeded',
      starred: item.starred || false,
    }));
  }, [appState]);

  // Extract all existing SKUs from the current data (from all shops)
  const existingSkus = useMemo<string[]>(() => {
    if (!data || !data.metrics_per_consegna_per_shop) {
      return [];
    }

    const skuSet = new Set<string>();
    
    // Collect SKUs from all shops
    Object.values(data.metrics_per_consegna_per_shop).forEach((shopConsegnas) => {
      shopConsegnas.forEach((consegna) => {
        consegna.tf.forEach((tf) => {
          tf.sku.forEach((sku) => {
            skuSet.add(sku.name);
          });
        });
      });
    });

    return Array.from(skuSet);
  }, [data]);

  // Detect if this is a multi-shop optimization (area is not null in generalMetrics or multiple shops)
  const isMultiShopOptimization = useMemo<boolean>(() => {
    // Check if generalMetrics has area data (not null and not empty)
    if (generalMetrics?.area)  {
      return true;
    }
    // Check if metrics_per_consegna_per_shop has multiple shops
    if (data?.metrics_per_consegna_per_shop && Object.keys(data.metrics_per_consegna_per_shop).length > 1) {
      return true;
    }
    return false;
  }, [generalMetrics, data]);

  // Get the list of stores from the API response
  const storesList = useMemo<Array<{ id: string; name: string; isArea: boolean }>>(() => {
    const stores: Array<{ id: string; name: string; isArea: boolean }> = [];
    
    if (!data?.metrics_per_consegna_per_shop) {
      return stores;
    }
    
    const shopIds = Object.keys(data.metrics_per_consegna_per_shop);
    
    // If multi-shop optimization, add Area as first option
    if (isMultiShopOptimization) {
      stores.push({ id: 'area', name: 'Area', isArea: true });
    }
    
    // Add individual shops from metrics_per_consegna_per_shop (exclude "area" synthetic aggregate)
    shopIds.forEach((shopId) => {
      if (shopId.toLowerCase() === 'area') return;
      stores.push({ id: shopId, name: getShopDisplayName(shopId, data.shop_mapping), isArea: false });
    });
    
    return stores;
  }, [data, isMultiShopOptimization]);

  // Determine if currently viewing the Area (summary) or a specific shop
  const isAreaView = useMemo<boolean>(() => {
    if (!isMultiShopOptimization) return false;
    return selectedStoreIndex === 0 && storesList[0]?.isArea === true;
  }, [isMultiShopOptimization, selectedStoreIndex, storesList]);

  // Get the current selected shop ID
  const loadSkuOrdersByShop = useCallback(async (sku: string): Promise<SkuOrdersShopEntry[]> => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
    if (!threadId || !messageId) throw new Error('Thread non disponibile');
    const result = await performAction({
      threadId,
      messageId,
      actionName: 'optim-sku-orders-by-shop',
      params: buildSkuOrdersByShopParams(sku, appState),
    });
    return Array.isArray((result as any)?.shops) ? (result as any).shops as SkuOrdersShopEntry[] : [];
  }, [appState, thread.threadData, thread.threadId]);

  // SPJ-1864: stessa azione del popup forecast del merchandising plan (SPJ-1724).
  const loadForecastProducts = useCallback(async (sku: string, shopIds: string[]): Promise<ForecastSimilarProductsResponse> => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
    if (!threadId || !messageId) throw new Error('Thread non disponibile');
    const result = await performAction({
      threadId,
      messageId,
      actionName: 'optim-forecast-similar-products',
      params: { 'optim-sku': sku, 'optim-shop-ids': shopIds },
    });
    return result as ForecastSimilarProductsResponse;
  }, [thread.threadData, thread.threadId]);

  const currentSelectedShop = useMemo<string | null>(() => {
    if (isAreaView) return null;
    const store = storesList[selectedStoreIndex];
    return store?.id || null;
  }, [isAreaView, selectedStoreIndex, storesList]);

  useEffect(() => {
    if (hasAppliedInitialStoreRef.current) {
      return;
    }
    if (!initialSelectedShopId) {
      hasAppliedInitialStoreRef.current = true;
      return;
    }
    if (!storesList.length) {
      return;
    }

    const targetIndex = storesList.findIndex((store) => store.id === initialSelectedShopId);
    hasAppliedInitialStoreRef.current = true;
    if (targetIndex >= 0) {
      setSelectedStoreIndex(targetIndex);
    }
  }, [initialSelectedShopId, storesList]);

  // Handler for constraint explanation requests
  const handleExplainConstraint = useCallback(async (request: {
    targetType: 'budget' | 'tipo_filato';
    targetName: string;
    constraintType?: string;
    consegna?: string;
    tipoFilato?: string;
  }) => {
    const key = `${request.targetType}_${request.targetName}`;
    if (constraintExplanations[key]) return; // already cached

    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
    if (!threadId || !messageId) return;

    try {
      await performStreamingAction({
        threadId,
        messageId,
        actionName: 'optim-explain-constraint',
        params: {
          target_type: request.targetType,
          target_name: request.targetName,
          run_id: currentSelectedRunId,
          shop_id: currentSelectedShop,
          constraint_type: request.constraintType ?? request.targetType,
          ...(request.consegna ? { consegna: request.consegna } : {}),
          ...(request.tipoFilato ? { tipo_filato: request.tipoFilato } : {}),
        },
        onDelta: (text) => {
          setConstraintExplanations(prev => ({ ...prev, [key]: text }));
        },
      });
    } catch (err) {
      console.error('Failed to get constraint explanation:', err);
      setConstraintExplanations(prev => ({ ...prev, [key]: 'Errore nella generazione della spiegazione.' }));
    }
  }, [thread, currentSelectedRunId, currentSelectedShop, constraintExplanations]);

  // Get consegna data based on whether we're viewing Area or a specific shop
  const getConsegnasForCurrentView = useCallback((sourceData: ApiResponse | null): ConsegnaData[] => {
    if (!sourceData || !sourceData.metrics_per_consegna_per_shop) return [];
    
    // If viewing Area in multi-shop mode, we don't show consegnas (Area shows summary only)
    if (isAreaView) {
      return [];
    }
    
    // If viewing a specific shop, get data from metrics_per_consegna_per_shop
    if (currentSelectedShop && sourceData.metrics_per_consegna_per_shop[currentSelectedShop]) {
      return sourceData.metrics_per_consegna_per_shop[currentSelectedShop];
    }
    
    // For single-shop case (not multi-shop), get the first (and only) shop's data
    const shopIds = Object.keys(sourceData.metrics_per_consegna_per_shop);
    if (shopIds.length > 0) {
      return sourceData.metrics_per_consegna_per_shop[shopIds[0]] || [];
    }
    
    return [];
  }, [isAreaView, currentSelectedShop]);

  // Get consegna data for the special aggregate "area" shop entry, if present
  const getAreaConsegnas = useCallback((sourceData: ApiResponse | null): ConsegnaData[] => {
    if (!sourceData?.metrics_per_consegna_per_shop) return [];
    const areaEntry = Object.entries(sourceData.metrics_per_consegna_per_shop).find(
      ([shopId]) => shopId.toLowerCase() === 'area'
    );
    return areaEntry ? areaEntry[1] : [];
  }, []);

  const getDisplayedConsegnasForActiveView = useCallback(
    (sourceData: ApiResponse | null): ConsegnaData[] => {
      if (!sourceData) return [];
      return isAreaView ? getAreaConsegnas(sourceData) : getConsegnasForCurrentView(sourceData);
    },
    [getAreaConsegnas, getConsegnasForCurrentView, isAreaView],
  );

  useEffect(() => {
    if (hasAppliedInitialTabRef.current) {
      return;
    }
    if (initialActiveTab === undefined) {
      hasAppliedInitialTabRef.current = true;
      return;
    }
    if (initialSelectedShopId && !hasAppliedInitialStoreRef.current) {
      return;
    }
    const displayedConsegnas = getDisplayedConsegnasForActiveView(data);
    if (displayedConsegnas.length === 0) {
      return;
    }
    hasAppliedInitialTabRef.current = true;
    setActiveTab(Math.max(0, Math.min(initialActiveTab, displayedConsegnas.length - 1)));
  }, [data, getDisplayedConsegnasForActiveView, initialActiveTab, initialSelectedShopId]);

  const getReorderedConsegnasForActiveView = useCallback(
    (sourceData: ApiResponse | null): ConsegnaData[] => {
      const consegnas = getDisplayedConsegnasForActiveView(sourceData);
      return [
        ...consegnas.filter((c) => c.name.toLowerCase() === 'all'),
        ...consegnas.filter((c) => c.name.toLowerCase() !== 'all'),
      ];
    },
    [getDisplayedConsegnasForActiveView],
  );

  // Parse treemap blocks for the currently selected consegna
  const treemapBlocks = useMemo(() => {
    if (!treemapData || !data || !data.metrics_per_consegna_per_shop) return [];
    
    // Get consegnas for current view
    let allConsegnas: ConsegnaData[] = [];
    if (isAreaView) {
      // Prefer dedicated "area" consegne when available, fallback to first shop
      const areaConsegnas = getAreaConsegnas(data);
      if (areaConsegnas.length > 0) {
        allConsegnas = areaConsegnas;
      } else {
        const shopIds = Object.keys(data.metrics_per_consegna_per_shop);
        if (shopIds.length > 0) {
          allConsegnas = data.metrics_per_consegna_per_shop[shopIds[0]] || [];
        }
      }
    } else if (currentSelectedShop && data.metrics_per_consegna_per_shop[currentSelectedShop]) {
      allConsegnas = data.metrics_per_consegna_per_shop[currentSelectedShop];
    } else {
      // Fallback to first shop
      const shopIds = Object.keys(data.metrics_per_consegna_per_shop);
      if (shopIds.length > 0) {
        allConsegnas = data.metrics_per_consegna_per_shop[shopIds[0]] || [];
      }
    }
    
    // Reorder: put "all" first, then the rest
    const reorderedConsegnas = [
      ...allConsegnas.filter((c) => c.name.toLowerCase() === "all"),
      ...allConsegnas.filter((c) => c.name.toLowerCase() !== "all"),
    ];
    const consegnaData = reorderedConsegnas[activeTab];
    if (!consegnaData) return [];
    
    // Map consegna names to treemapData keys
    // "all" -> "total", others stay the same
    const consegnaName = consegnaData.name.toLowerCase();
    const treemapKey = consegnaName === 'all' ? 'total' : consegnaData.name;
    
    const treemapBlocksData = treemapData?.[currentSelectedShop ?? 'area']?.[treemapKey];
    
    if (!treemapBlocksData || !Array.isArray(treemapBlocksData)) {
      return [];
    }
    
    // Transform the data format: [label, [x, y, w, h]] -> { label, x, y, width, height }
    return treemapBlocksData.map((item: any) => ({
      label: item[0],
      x: item[1][0],
      y: item[1][1],
      width: item[1][2],
      height: item[1][3],
    }));
  }, [treemapData, data, activeTab, isAreaView, currentSelectedShop, getAreaConsegnas]);

  const handleTimelineNodeClick = async (node: TimelineNode) => {
    // Don't send action if clicking on the currently selected node
    const nodeIdAsNumber = parseInt(node.id, 10);
    if (currentSelectedRunId === nodeIdAsNumber) {
      return;
    }
    
    try {
      // Call optim-select-run to set the current point in history
      await dispatch(
        executeSophiaAction({
          actionName: 'optim-select-run',
          params: {
            'optim-run-id': nodeIdAsNumber,
          },
          refreshThread: true,
        })
      ).unwrap();
      
      // Data will be refetched automatically via useEffect when thread updates
    } catch (err) {
      console.error("Failed to select timeline node:", err);
      alert(`Failed to load timeline node: ${(err as Error).message}`);
    }
  };

  const handleTimelineNodeUpdate = async (
    nodeId: string,
    updates: Partial<TimelineNode>
  ) => {
    try {
      // Call separate actions for description and starred updates
      if (updates.description !== undefined || updates.starred !== undefined) {
        await dispatch(
          executeSophiaAction({
            actionName: 'optim-update-run-description-and-starred',
            params: {
              'optim-run-id': parseInt(nodeId, 10),
              'optim-run-description': updates.description,
              'optim-run-starred': updates.starred,
            },
            refreshThread: true,
          })
        ).unwrap();
      }
    } catch (err) {
      console.error("Failed to update timeline node:", err);
      alert(`Failed to update timeline node: ${(err as Error).message}`);
    }
  };

  const handleTreemapBlockClick = async (block: { label: string }, color: string) => {
    if (!currentSelectedRunId || !thread.threadId || !data) return;

    try {
      setTsneLoading(true);
      setSelectedBlockLabel(block.label);
      setSelectedBlockColor(color);
      
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!messageId) return;

      // Resolve consegne according to current view (shop vs area)
      const reorderedConsegnas = getReorderedConsegnasForActiveView(data);
      const consegnaData = reorderedConsegnas[activeTab];
      if (!consegnaData) return;
      if (!consegnaData.tf.some((tf) => tf.name === block.label)) {
        return;
      }

      const gender = appState?.['optim-gender'] || 'Donna';
      const sottoConsegna = consegnaData.name;
      const defaultShopId = isAreaView ? 'area' : (currentSelectedShop ?? '');
      const baseParams = {
        "optim-run-id": currentSelectedRunId,
        "optim-gender": gender,
        "optim-tipo-filato": block.label,
        "optim-sotto-consegna": sottoConsegna,
      } as Record<string, unknown>;

      let result: unknown;
      try {
        result = await performAction({
          threadId,
          messageId,
          actionName: "optim-tsne-details",
          params: {
            ...baseParams,
            'optim-shop-id': defaultShopId,
          },
        });
      } catch (primaryErr) {
        if (!isAreaView) {
          throw primaryErr;
        }
        result = await performAction({
          threadId,
          messageId,
          actionName: "optim-tsne-details",
          params: {
            ...baseParams,
            'optim-shop-id': '',
          },
        });
      }

      setTsneData(result);
      setShowTsneView(true);
      
      // Expand TOTALE if not already expanded so TF rows are visible
      const totaleExpandKey = isAreaView ? `area_totale_${activeTab}` : `totale_${activeTab}`;
      if (!expanded[totaleExpandKey]) {
        setExpanded(prev => ({ ...prev, [totaleExpandKey]: true }));
      }
      
      // Scroll to the TF row in the table and highlight it
      setTimeout(() => {
        if (tableBodyRef.current) {
          const tfRow = tableBodyRef.current.querySelector(`[data-tf-name="${block.label}"]`);
          if (tfRow) {
            tfRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedTf(block.label);
            setTimeout(() => {
              setHighlightedTf(null);
            }, 2000);
          } else {
            // Try again after a delay in case expansion wasn't complete
            setTimeout(() => {
              const tfRowRetry = tableBodyRef.current?.querySelector(`[data-tf-name="${block.label}"]`);
              if (tfRowRetry) {
                tfRowRetry.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setHighlightedTf(block.label);
                setTimeout(() => setHighlightedTf(null), 2000);
              }
            }, 400);
          }
        }
      }, 300);
    } catch (err) {
      console.error("Error fetching t-SNE data:", err);
      alert(`Failed to load t-SNE visualization: ${(err as Error).message}`);
    } finally {
      setTsneLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const threadId = thread.threadId;
        const messageId = thread.threadData?.local_memory?.thread_state?.[
          "current-message"
        ] as string;
        const appState = thread.threadData?.local_memory?.app_state as any;
        const optimizationId = appState?.["optim-optimization-id"];

        if (!threadId || !messageId) {
          throw new Error("Thread o message ID non disponibile");
        }

        // If no optimization ID, call without params (will use the one from app_state on backend)
        const result = await performAction({
          threadId,
          messageId,
          actionName: "optim-get-optimization-result",
          ...(optimizationId
            ? {
                params: {
                  "optim-optimization-id": optimizationId.toString(),
                },
              }
            : {}),
        });

        setData(result as ApiResponse);
        setEditedData(result as ApiResponse);

        // Set first consegna as active tab (index 0)
        setActiveTab(0);

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        console.error("Error fetching optimization metrics:", err);
      } finally {
        setLoading(false);
      }
    };

    if (thread.threadId) {
      fetchData();
    }
  }, [thread.threadId, thread.threadData]);

  // Fetch TIPO_FILATO enum values
  useEffect(() => {
    const fetchTipoFilatoEnum = async () => {
      try {
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'global-get-page-data',
            params: { 'global-page-name': 'list-products' },
            refreshThread: false,
          }),
        ).unwrap();
        
        const schema = (payload.data as any)?.['global-page-data']?.schema;
        if (schema?.TIPO_FILATO?.enum && Array.isArray(schema.TIPO_FILATO.enum)) {
          setTipoFilatoEnum(schema.TIPO_FILATO.enum);
        }
      } catch (err) {
        console.error("Error fetching tipo filato enum:", err);
      }
    };

    if (thread.threadId && tipoFilatoEnum.length === 0) {
      fetchTipoFilatoEnum();
    }
  }, [thread.threadId, dispatch, tipoFilatoEnum.length]);

  // Initialize overrides from constraints
  useEffect(() => {
    if (constraints?.preparation?.expected_sellout_per_tipo_filato) {
      const perTipoFilato = constraints.preparation.expected_sellout_per_tipo_filato;
      const newOverrides = Object.entries(perTipoFilato).map(([tipo, value], index) => ({
        id: index + 1,
        tipo_filato: tipo,
        value: typeof value === 'number' ? value : 0.5,
      }));
      setTipoFilatoOverrides(newOverrides);
    } else {
      setTipoFilatoOverrides([]);
    }

    if (constraints?.constraints?.min_allowed_value_per_tipo_filato) {
      const perTipoFilato = constraints.constraints.min_allowed_value_per_tipo_filato;
      const newOverrides = Object.entries(perTipoFilato).map(([tipo, value], index) => ({
        id: index + 1,
        tipo_filato: tipo,
        value: typeof value === 'number' ? value : 0,
      }));
      setMinAllowedValueTipoFilatoOverrides(newOverrides);
    } else {
      setMinAllowedValueTipoFilatoOverrides([]);
    }
  }, [constraints]);


  // Reset t-SNE view when run ID or tab changes
  useEffect(() => {
    setShowTsneView(false);
    setTsneData(null);
  }, [currentSelectedRunId, activeTab]);

  // Clear constraint explanations when run changes
  useEffect(() => {
    setConstraintExplanations({});
  }, [currentSelectedRunId, currentSelectedShop, isAreaView]);

  // Fetch treemap data when run ID changes
  useEffect(() => {
    const fetchTreemapData = async () => {
      if (!currentSelectedRunId || !thread.threadId) return;

      try {
        setTreemapLoading(true);
        const threadId = thread.threadId;
        const messageId = thread.threadData?.local_memory?.thread_state?.[
          "current-message"
        ] as string;

        if (!messageId) return;

        const result = await performAction({
          threadId,
          messageId,
          actionName: "optim-treemap-blocks",
          params: {
            "optim-run-id": currentSelectedRunId,
          },
        });

        setTreemapData(result);
      } catch (err) {
        console.error("Error fetching treemap data:", err);
        setTreemapData(null);
      } finally {
        setTreemapLoading(false);
      }
    };

    fetchTreemapData();
  }, [currentSelectedRunId, thread.threadId]);

  // Fetch general metrics (constraint data) when run ID changes
  useEffect(() => {
    const fetchGeneralMetrics = async () => {
      if (!currentSelectedRunId || !thread.threadId) return;

      try {
        setGeneralMetricsLoading(true);
        const threadId = thread.threadId;
        const messageId = thread.threadData?.local_memory?.thread_state?.[
          "current-message"
        ] as string;

        if (!messageId) return;

        const result = await performAction({
          threadId,
          messageId,
          actionName: "optim-get-general-metrics",
          params: {
            "optim-run-id": currentSelectedRunId,
          },
        });
        // If result is a string (error message indicating failed run), find the most recent succeeded run
        if (typeof result === 'string') {
          const history = appState?.['history'] as Array<{ run_id: number; status?: string }> | undefined;
          if (history && history.length > 0) {
            // Find the most recent run with status !== 'failed' (default status is 'succeeded')
            const succeededRuns = history.filter((run) => (run.status || 'succeeded') === 'succeeded');
            if (succeededRuns.length > 0) {
              // Get the most recent succeeded run (last in the filtered array)
              const mostRecentSucceededRun = succeededRuns[succeededRuns.length - 1];
              const runId = mostRecentSucceededRun.run_id;
              
              // Select the succeeded run
              await dispatch(
                executeSophiaAction({
                  actionName: 'optim-select-run',
                  params: {
                    'optim-run-id': runId,
                  },
                  refreshThread: true,
                })
              ).unwrap();
              return; // Exit early, the useEffect will re-trigger with new run
            } else {
              // No succeeded runs found, show message to user
              alert('Nessuna ottimizzazione riuscita trovata. Carica un altro progetto o lancia una nuova ottimizzazione.');
              return;
            }
          }
        }

        console.log("=== GENERAL METRICS RESULT ===");
        console.log("Full result:", result);
        if (result) {
          const r = result as Record<string, unknown>;
          console.log("Has 'area'?", 'area' in r, "Is array?", Array.isArray(r.area), "Area value:", r.area);
          console.log("Has 'shop lines'?", 'lines' in r, "shop_lines value:", r.lines);
        }
        console.log("==============================");

        setGeneralMetrics(result as GeneralMetrics);
      } catch (err) {
        console.error("Error fetching general metrics:", err);
        setGeneralMetrics(null);
      } finally {
        setGeneralMetricsLoading(false);
      }
    };

    fetchGeneralMetrics();
  }, [currentSelectedRunId, thread.threadId]);

  // Measure and update timeline height when container size changes
  useEffect(() => {
    const updateTimelineHeight = () => {
      if (timelineContainerRef.current) {
        const containerHeight = timelineContainerRef.current.clientHeight;
        // Subtract some padding to account for the Timeline label and spacing
        const adjustedHeight = containerHeight - 40; // 40px for label and spacing
        setTimelineHeight(adjustedHeight > 0 ? adjustedHeight : undefined);
      }
    };

    // Initial measurement with a small delay to ensure layout is complete
    const timeoutId = setTimeout(updateTimelineHeight, 100);

    // Add window resize listener to handle page resizing
    window.addEventListener('resize', updateTimelineHeight);

    // Add resize observer to handle dynamic resizing
    const resizeObserver = new ResizeObserver(updateTimelineHeight);
    if (timelineContainerRef.current) {
      resizeObserver.observe(timelineContainerRef.current);
    }

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateTimelineHeight);
      resizeObserver.disconnect();
    };
  }, [data]); // Re-measure when data changes

  // Handle dragging and resizing for the card
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        // Existing section resize logic
        const windowHeight = window.innerHeight;
        const newHeight = (e.clientY / windowHeight) * 100;
        const constrainedHeight = Math.min(Math.max(newHeight, 20), 80);
        setTopSectionHeight(constrainedHeight);
      } else if (isDraggingCard && dragStart) {
        // Card dragging logic
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        const newX = Math.min(Math.max(dragStart.cardX + deltaX, 0), window.innerWidth - cardWidth);
        const newY = Math.min(Math.max(dragStart.cardY + deltaY, 0), window.innerHeight - 100);
        
        setCardPosition({ x: newX, y: newY });
      } else if (isResizingCard && resizeStart) {
        // Card resizing logic
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        let newWidth = cardWidth;
        let newHeight = cardHeight;
        let newX = cardPosition.x;
        let newY = cardPosition.y;
        
        // Handle different resize directions
        if (resizeDirection.includes('e')) {
          newWidth = Math.min(Math.max(resizeStart.width + deltaX, 400), window.innerWidth - cardPosition.x);
        }
        if (resizeDirection.includes('w')) {
          const maxDelta = resizeStart.width - 400;
          const constrainedDelta = Math.min(Math.max(deltaX, -resizeStart.cardX), maxDelta);
          newWidth = resizeStart.width - constrainedDelta;
          newX = resizeStart.cardX + constrainedDelta;
        }
        if (resizeDirection.includes('s')) {
          newHeight = Math.min(Math.max(resizeStart.height + deltaY, 300), window.innerHeight - cardPosition.y);
        }
        if (resizeDirection.includes('n')) {
          const maxDelta = resizeStart.height - 300;
          const constrainedDelta = Math.min(Math.max(deltaY, -resizeStart.cardY), maxDelta);
          newHeight = resizeStart.height - constrainedDelta;
          newY = resizeStart.cardY + constrainedDelta;
        }
        
        setCardWidth(newWidth);
        setCardHeight(newHeight);
        setCardPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsDraggingCard(false);
      setIsResizingCard(false);
      setDragStart(null);
      setResizeStart(null);
      setResizeDirection('');
    };

    if (isDragging || isDraggingCard || isResizingCard) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Set appropriate cursor
      let cursor = 'default';
      if (isDragging) cursor = 'ns-resize';
      else if (isDraggingCard) cursor = 'move';
      else if (isResizingCard) {
        const cursorMap: { [key: string]: string } = {
          'n': 'n-resize', 's': 's-resize', 'e': 'e-resize', 'w': 'w-resize',
          'ne': 'ne-resize', 'nw': 'nw-resize', 'se': 'se-resize', 'sw': 'sw-resize'
        };
        cursor = cursorMap[resizeDirection] || 'default';
      }
      
      document.body.style.cursor = cursor;
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, isDraggingCard, isResizingCard, dragStart, resizeStart, resizeDirection, cardWidth, cardHeight, cardPosition]);

  // Larghezza del treemap: trascinamento del bordo destro (stesso schema del divisore orizzontale).
  useEffect(() => {
    if (!treemapResizeStart) return;
    let latestWidth = treemapResizeStart.width;
    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = Math.max(TREEMAP_MIN_WIDTH, Math.min(1000, window.innerWidth - 200));
      latestWidth = Math.min(Math.max(treemapResizeStart.width + e.clientX - treemapResizeStart.x, TREEMAP_MIN_WIDTH), maxWidth);
      setTreemapUserWidth(latestWidth);
    };
    const handleMouseUp = () => {
      setTreemapResizeStart(null);
      try {
        localStorage.setItem(TREEMAP_WIDTH_STORAGE_KEY, String(Math.round(latestWidth)));
      } catch {
        // storage non disponibile: la larghezza vale solo per questa sessione
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [treemapResizeStart]);

  // Measure top container height for responsive widgets
  useEffect(() => {
    const updateTopContainerHeight = () => {
      if (topContainerRef.current) {
        const height = topContainerRef.current.clientHeight;
        // Subtract padding (py-8 = 64px total)
        setTopContainerHeight(height - 64);
      }
    };

    // Initial measurement
    const timeoutId = setTimeout(updateTopContainerHeight, 100);

    // Add window resize listener
    window.addEventListener('resize', updateTopContainerHeight);

    // Add resize observer
    const resizeObserver = new ResizeObserver(updateTopContainerHeight);
    if (topContainerRef.current) {
      resizeObserver.observe(topContainerRef.current);
    }

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateTopContainerHeight);
      resizeObserver.disconnect();
    };
  }, [data]);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper function to refetch treemap and constraints data
  const refetchVisualizationData = useCallback(async () => {
    if (!currentSelectedRunId || !thread.threadId) return;

    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.[
      "current-message"
    ] as string;

    if (!messageId) return;

    try {
      // Refetch treemap data
      setTreemapLoading(true);
      const treemapResult = await performAction({
        threadId,
        messageId,
        actionName: "optim-treemap-blocks",
        params: {
          "optim-run-id": currentSelectedRunId,
        },
      });
      setTreemapData(treemapResult);
    } catch (err) {
      console.error("Error refetching treemap data:", err);
    } finally {
      setTreemapLoading(false);
    }

    try {
      // Refetch general metrics (constraints)
      setGeneralMetricsLoading(true);
      const metricsResult = await performAction({
        threadId,
        messageId,
        actionName: "optim-get-general-metrics",
        params: {
          "optim-run-id": currentSelectedRunId,
        },
      });
      console.log("=== REFETCH GENERAL METRICS ===");
      console.log("Metrics result:", metricsResult);
      console.log("===============================");
      setGeneralMetrics(metricsResult as GeneralMetrics);
    } catch (err) {
      console.error("Error refetching general metrics:", err);
    } finally {
      setGeneralMetricsLoading(false);
    }
  }, [currentSelectedRunId, thread.threadId, thread.threadData]);

  const scrollToSku = useCallback((sku: string, consegnaIndex?: number) => {
    const targetConsegnaIndex = consegnaIndex !== undefined ? consegnaIndex : activeTab;
    
    // Find the TF (tipo filato) for this SKU in the target consegna
    const sourceData = isManualEditMode ? (editedData || data) : data;
    const reorderedConsegnasList = getReorderedConsegnasForActiveView(sourceData);
    const consegnaData = reorderedConsegnasList[targetConsegnaIndex];
    
    if (!consegnaData) {
      return;
    }

    let targetTfName: string | null = null;
    let targetTfIndex: number = -1;
    for (let i = 0; i < consegnaData.tf.length; i++) {
      const tf = consegnaData.tf[i];
      const skuMatch = tf.sku.find(s => s.name === sku);
      if (skuMatch) {
        targetTfName = tf.name;
        targetTfIndex = i;
        break;
      }
    }

    if (!targetTfName || targetTfIndex === -1) {
      return;
    }

    // Switch to target tab if different
    if (targetConsegnaIndex !== activeTab) {
      setActiveTab(targetConsegnaIndex);
    }

    // First, ensure TOTALE is expanded (so TF rows are visible)
    const totaleExpandKey = isAreaView ? `area_totale_${targetConsegnaIndex}` : `totale_${targetConsegnaIndex}`;
    const tfExpandKey = isAreaView ? `area_tf_${targetConsegnaIndex}_${targetTfIndex}` : `tf_${targetConsegnaIndex}_${targetTfIndex}`;
    
    const newExpanded = { ...expanded };
    let needsExpansion = false;
    
    if (!newExpanded[totaleExpandKey]) {
      newExpanded[totaleExpandKey] = true;
      needsExpansion = true;
    }
    if (!newExpanded[tfExpandKey]) {
      newExpanded[tfExpandKey] = true;
      needsExpansion = true;
    }
    
    if (needsExpansion) {
      setExpanded(newExpanded);
    }

    // Wait a moment for the expansion to complete, then scroll to the SKU row
    const waitTime = needsExpansion ? 500 : 100;
    
    setTimeout(() => {
      if (tableBodyRef.current) {
        const skuRow = tableBodyRef.current.querySelector(`[data-sku-name="${sku}"]`);
        
        if (skuRow) {
          skuRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedTf(sku);
          setTimeout(() => {
            setHighlightedTf(null);
          }, 2000);
        } else {
          // Try again after another delay
          setTimeout(() => {
            const skuRowRetry = tableBodyRef.current?.querySelector(`[data-sku-name="${sku}"]`);
            if (skuRowRetry) {
              skuRowRetry.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setHighlightedTf(sku);
              setTimeout(() => setHighlightedTf(null), 2000);
            }
          }, 500);
        }
      }
    }, waitTime);
  }, [isManualEditMode, editedData, data, activeTab, expanded, getReorderedConsegnasForActiveView, isAreaView]);

  const handleTsneProductClick = useCallback((sku: string) => {
    scrollToSku(sku);
  }, [scrollToSku]);

  const handleTipoFilatoClick = useCallback((tipoFilato: string) => {
    const sourceData = isManualEditMode ? (editedData || data) : data;
    const reorderedDisplayConsegnas = getReorderedConsegnasForActiveView(sourceData);
    const consegnaData = reorderedDisplayConsegnas[activeTab];

    if (!consegnaData) {
      return;
    }

    const activeMpMeta = (() => {
      if (isAreaView) {
        const areaArray = generalMetrics?.area
          ? Array.isArray(generalMetrics.area)
            ? generalMetrics.area
            : [generalMetrics.area]
          : [];
        return areaArray.find((a: MetricsArea) => a?.mp_constraint?._meta)?.mp_constraint?._meta;
      }
      if (currentSelectedShop && generalMetrics?.lines?.[currentSelectedShop]?.mp_constraint?._meta) {
        return generalMetrics.lines[currentSelectedShop].mp_constraint._meta;
      }
      return generalMetrics?.mp_constraint?._meta;
    })();

    const mappedTipoFilati = activeMpMeta?.included_tipi_filato_by_mp?.[tipoFilato];
    const targetTipoFilati = Array.from(new Set(
      (Array.isArray(mappedTipoFilati) && mappedTipoFilati.length > 0 ? mappedTipoFilati : [tipoFilato])
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
    ));

    const matchingTipoFilati = targetTipoFilati.filter((target) =>
      consegnaData.tf.some((tf) => tf.name === target)
    );

    if (matchingTipoFilati.length === 0) {
      return;
    }

    const totaleExpandKey = isAreaView ? `area_totale_${activeTab}` : `totale_${activeTab}`;

    const newExpanded = { ...expanded };
    let needsExpansion = false;

    if (!newExpanded[totaleExpandKey]) {
      newExpanded[totaleExpandKey] = true;
      needsExpansion = true;
    }

    if (needsExpansion) {
      setExpanded(newExpanded);
    }

    const waitTime = needsExpansion ? 500 : 100;
    const scrollAndHighlight = () => {
      if (!tableBodyRef.current) return false;
      const firstTarget = matchingTipoFilati[0];
      const tfRow = tableBodyRef.current.querySelector(`[data-tf-name="${firstTarget}"]`);
      if (!tfRow) return false;
      tfRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedTf(matchingTipoFilati);
      setTimeout(() => setHighlightedTf(null), 2000);
      return true;
    };

    setTimeout(() => {
      if (!scrollAndHighlight()) {
        setTimeout(scrollAndHighlight, 400);
      }
    }, waitTime);
  }, [isManualEditMode, editedData, data, activeTab, expanded, getReorderedConsegnasForActiveView, isAreaView, generalMetrics, currentSelectedShop]);

  const handleTipoFilatoClickForSearch = useCallback((tipoFilato: string, consegnaIndex?: number) => {
    const targetConsegnaIndex = consegnaIndex !== undefined ? consegnaIndex : activeTab;
    
    const sourceData = isManualEditMode ? (editedData || data) : data;
    const reorderedDisplayConsegnas = getReorderedConsegnasForActiveView(sourceData);
    const consegnaData = reorderedDisplayConsegnas[targetConsegnaIndex];
    
    if (!consegnaData) {
      return;
    }

    // Find the TF index for this tipo filato
    const tfIndex = consegnaData.tf.findIndex(tf => tf.name === tipoFilato);
    
    if (tfIndex === -1) {
      return;
    }

    // Switch to target tab if different
    if (targetConsegnaIndex !== activeTab) {
      setActiveTab(targetConsegnaIndex);
    }

    // Expand TOTALE if not already expanded so TF rows are visible
    const totaleExpandKey = isAreaView ? `area_totale_${targetConsegnaIndex}` : `totale_${targetConsegnaIndex}`;
    
    const newExpanded = { ...expanded };
    let needsExpansion = false;
    
    if (!newExpanded[totaleExpandKey]) {
      newExpanded[totaleExpandKey] = true;
      needsExpansion = true;
    }
    
    if (needsExpansion) {
      setExpanded(newExpanded);
    }

    // Wait a moment for the expansion to complete, then scroll to the TF row
    const waitTime = needsExpansion ? 500 : 100;
    
    setTimeout(() => {
      if (tableBodyRef.current) {
        const tfRow = tableBodyRef.current.querySelector(`[data-tf-name="${tipoFilato}"]`);
        
        if (tfRow) {
          tfRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the TF row
          setHighlightedTf(tipoFilato);
          setTimeout(() => setHighlightedTf(null), 2000);
        }
      }
    }, waitTime);
  }, [isManualEditMode, editedData, data, activeTab, expanded, getReorderedConsegnasForActiveView, isAreaView]);

  // Search functionality
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    const results: Array<{sku: string, consegnaIndex: number, tfIndex: number, isTf?: boolean}> = [];
    const sourceData = isManualEditMode ? (editedData || data) : data;
    const reorderedDisplayConsegnas = getReorderedConsegnasForActiveView(sourceData);

    // Search only in the current tab
    const currentConsegna = reorderedDisplayConsegnas[activeTab];
    if (currentConsegna) {
      currentConsegna.tf.forEach((tf, tfIndex) => {
        // Search in TF names (tipo filato)
        if (tf.name.toLowerCase().includes(query.toLowerCase())) {
          results.push({
            sku: tf.name, // Use TF name as identifier
            consegnaIndex: activeTab,
            tfIndex,
            isTf: true, // Mark as TF result
          });
        }
        
        // Search in SKU names
        tf.sku.forEach((sku) => {
          if (sku.name.toLowerCase().includes(query.toLowerCase())) {
            results.push({
              sku: sku.name,
              consegnaIndex: activeTab,
              tfIndex,
              isTf: false,
            });
          }
        });
      });
    }

    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    // Scroll to first result
    if (results.length > 0) {
      const firstResult = results[0];
      if (firstResult.isTf) {
        // Scroll to TF row
        handleTipoFilatoClickForSearch(firstResult.sku, firstResult.consegnaIndex);
      } else {
        // Scroll to SKU row
        scrollToSku(firstResult.sku, firstResult.consegnaIndex);
      }
    }
  }, [isManualEditMode, editedData, data, activeTab, scrollToSku, handleTipoFilatoClickForSearch, getReorderedConsegnasForActiveView]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    const result = searchResults[nextIndex];
    
    if (result.isTf) {
      // Scroll to TF row
      handleTipoFilatoClickForSearch(result.sku, result.consegnaIndex);
    } else {
      // Scroll to SKU row
      scrollToSku(result.sku, result.consegnaIndex);
    }
  }, [searchResults, currentSearchIndex, scrollToSku, handleTipoFilatoClickForSearch]);

  const handlePreviousResult = useCallback(() => {
    if (searchResults.length === 0) return;
    
    const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentSearchIndex(prevIndex);
    const result = searchResults[prevIndex];
    
    if (result.isTf) {
      // Scroll to TF row
      handleTipoFilatoClickForSearch(result.sku, result.consegnaIndex);
    } else {
      // Scroll to SKU row
      scrollToSku(result.sku, result.consegnaIndex);
    }
  }, [searchResults, currentSearchIndex, scrollToSku, handleTipoFilatoClickForSearch]);

  const handleManualEditClick = async () => {
    // Toggle manual edit mode
    if (isManualEditMode) {
      // Disable manual edit mode
      setIsManualEditMode(false);
      setHasTaglieBeenForked(false);
    } else {
      // Enable manual edit mode
      setIsManualEditMode(true);
      setIsOptimizationMode(false);
      // Clone data immediately when entering edit mode
      if (data && !editedData) {
        setEditedData(JSON.parse(JSON.stringify(data)));
      }
      
      // Check if current run is already manual (yellow node)
      const currentNode = timelineNodes.find(
        node => parseInt(node.id, 10) === currentSelectedRunId
      );
      const isCurrentRunManual = currentNode?.type === 'manual';
      
      // Only fork if current run is NOT manual and we haven't forked yet
      if (!isCurrentRunManual && !hasTaglieBeenForked) {
        try {
          setHasTaglieBeenForked(true);

          // Fork the run and refresh thread to get the new run in history
          await dispatch(
            executeSophiaAction({
              actionName: 'optim-fork-run',
              params: {},
              refreshThread: true,
            })
          ).unwrap();

          // After fork, the new run should be automatically selected by the backend
          // and will appear in the timeline. The useEffect that fetches data will
          // trigger automatically when the thread updates.
        } catch (err) {
          console.error("Error forking run:", err);
          alert("Error during run fork");
          setHasTaglieBeenForked(false);
          setIsManualEditMode(false);
        }
      } else if (isCurrentRunManual) {
        // Current run is already manual, no need to fork
        setHasTaglieBeenForked(true);
      }
    }
  };

  const handleOptimizationClick = async () => {
    onOpenMerchandisingPlan?.({
      runId: currentSelectedRunId,
      selectedShopId: currentSelectedShop,
      selectedResultTab: activeTab,
    });
  };

  const handleConfigViewClick = async () => {
    // Toggle config view mode
    if (isConfigViewMode) {
      // Close config view
      setIsConfigViewMode(false);
      setConstraints(null);
    } else {
      // Open config view
      setIsConfigViewMode(true);
      setIsManualEditMode(false);
      setIsOptimizationMode(false);
      
      try {
        const threadId = thread.threadId;
        const messageId = thread.threadData?.local_memory?.thread_state?.[
          "current-message"
        ] as string;

        if (!threadId || !messageId) {
          console.error("Thread or message ID not available");
          return;
        }

        // Load current config without forking
        const configResult = await performAction({
          threadId,
          messageId,
          actionName: "optim-get-config-details",
          params: {},
        });

        setConstraints(configResult);
        
        // Load excluded SKUs from config (SKUs with lb=0 and ub=0)
        const lbConstraints = (configResult as any)?.constraints?.lb_quantity || {};
        const ubConstraints = (configResult as any)?.constraints?.ub_quantity || {};
        const excluded = new Set<string>();
        Object.keys(lbConstraints).forEach(sku => {
          if (lbConstraints[sku] === 0 && ubConstraints[sku] === 0) {
            excluded.add(sku);
          }
        });
        setExcludedSkus(excluded);
      } catch (err) {
        console.error("Error loading config:", err);
        alert("Error loading configuration");
      }
    }
  };

  const updateConstraintsWithOverrides = useCallback(async (overrides: Array<{ id: number; tipo_filato: string; value: number }>) => {
    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
    if (!updatedConstraints.preparation) {
      updatedConstraints.preparation = {};
    }
    
    // Only add expected_sellout_per_tipo_filato if there are valid overrides
    const validOverrides = overrides.filter(o => o.tipo_filato && o.tipo_filato !== '');
    if (validOverrides.length > 0) {
      updatedConstraints.preparation.expected_sellout_per_tipo_filato = {};
      validOverrides.forEach(override => {
        updatedConstraints.preparation.expected_sellout_per_tipo_filato[override.tipo_filato] = override.value;
      });
    } else {
      delete updatedConstraints.preparation.expected_sellout_per_tipo_filato;
    }
    
    setConstraints(updatedConstraints);
    
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
      if (threadId && messageId) {
        await performAction({
          threadId,
          messageId,
          actionName: "optim-update-config",
          params: {
            "optim-config-data": updatedConstraints,
          },
        });
      }
    } catch (err) {
      console.error("Error updating per-tipo-filato overrides:", err);
    }
  }, [constraints, thread.threadId, thread.threadData]);

  const updateMinAllowedValuePerTipoFilatoOverrides = useCallback(async (overrides: Array<{ id: number; tipo_filato: string; value: number }>) => {
    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
    if (!updatedConstraints.constraints) {
      updatedConstraints.constraints = {};
    }

    const validOverrides = overrides.filter((override) => override.tipo_filato && override.tipo_filato !== '');
    if (validOverrides.length > 0) {
      updatedConstraints.constraints.min_allowed_value_per_tipo_filato = {};
      validOverrides.forEach((override) => {
        updatedConstraints.constraints.min_allowed_value_per_tipo_filato[override.tipo_filato] = override.value;
      });
    } else {
      delete updatedConstraints.constraints.min_allowed_value_per_tipo_filato;
    }

    setConstraints(updatedConstraints);

    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string;
      if (threadId && messageId) {
        await performAction({
          threadId,
          messageId,
          actionName: 'optim-update-config',
          params: {
            'optim-config-data': updatedConstraints,
          },
        });
      }
    } catch (err) {
      console.error('Error updating min_allowed_value_per_tipo_filato overrides:', err);
    }
  }, [constraints, thread.threadId, thread.threadData]);

  const handleSaveManualEdit = async () => {
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!threadId || !messageId || !editedData) {
        throw new Error("Thread, message ID or edited data not available");
      }

      // Call optim-update-run with edited data for the current shop
      // Get the current shop's consegna data from editedData
      const editedConsegnas = getConsegnasForCurrentView(editedData);
      
      await performAction({
        threadId,
        messageId,
        actionName: "optim-update-run",
        params: {
          "optim-run-data": editedData.metrics_per_consegna_per_shop,
        },
      });
      await dispatch(fetchThread()).unwrap();

      // Reload data by calling optim-get-optimization-result
      const result = await performAction({
        threadId,
        messageId,
        actionName: "optim-get-optimization-result",
        params: {},
      });

      setData(result as ApiResponse);
      setEditedData(result as ApiResponse);
      setIsManualEditMode(false);
      setHasTaglieBeenForked(false); // Reset for next time

      // Refetch treemap and constraints since run data has changed
      await refetchVisualizationData();
    } catch (err) {
      console.error("Error saving changes:", err);
      alert("Error saving changes");
    }
  };

  const handleTagliaChange = (
    consegnaIndex: number,
    tfIndex: number,
    skuIndex: number,
    groupId: string,
    size: string,
    newValue: number,
    targetConsegnaName?: string,
    tfName?: string,
    skuName?: string
  ) => {
    // Only allow changes in manual edit mode
    if (!isManualEditMode) {
      return;
    }

    // Rete di sicurezza oltre a quella sull'input: qualunque chiamante passi un valore
    // negativo lo vede diventare zero, cosi' nel piano non ci finisce mai.
    newValue = normalizzaQuantitaTaglia(newValue);

    // Update editedData directly (fork already done in handleManualEditClick)
    if (!editedData || !editedData.metrics_per_consegna_per_shop) return;

    // Get the shop ID for the current view
    const shopId = currentSelectedShop || Object.keys(editedData.metrics_per_consegna_per_shop)[0];
    if (!shopId || !editedData.metrics_per_consegna_per_shop[shopId]) return;

    const newData = JSON.parse(JSON.stringify(editedData)) as ApiResponse;
    if (!newData.metrics_per_consegna_per_shop || !newData.metrics_per_consegna_per_shop[shopId]) return;
    
    let resolvedConsegnaIndex = consegnaIndex;
    let resolvedTfIndex = tfIndex;
    let resolvedSkuIndex = skuIndex;

    if (targetConsegnaName) {
      const targetIdx = newData.metrics_per_consegna_per_shop[shopId].findIndex(
        (consegna) => consegna.name === targetConsegnaName
      );
      if (targetIdx === -1) return;
      resolvedConsegnaIndex = targetIdx;

    }

    if (tfName) {
      const targetTfIdx = newData.metrics_per_consegna_per_shop[shopId][resolvedConsegnaIndex].tf.findIndex(
        (tf) => tf.name === tfName
      );
      if (targetTfIdx === -1) return;
      resolvedTfIndex = targetTfIdx;
    }

    if (skuName) {
      const targetSkuIdx = newData.metrics_per_consegna_per_shop[shopId][resolvedConsegnaIndex].tf[resolvedTfIndex].sku.findIndex(
        (sku) => sku.name === skuName
      );
      if (targetSkuIdx === -1) return;
      resolvedSkuIndex = targetSkuIdx;
    }

    const targetSku =
      newData.metrics_per_consegna_per_shop[shopId][resolvedConsegnaIndex].tf[resolvedTfIndex].sku[resolvedSkuIndex];

    if (
      targetSku.metrics.taglie_distribution &&
      targetSku.metrics.taglie_distribution[groupId]
    ) {
      targetSku.metrics.taglie_distribution[groupId][size] = newValue;
    }

    setEditedData(newData);
  };

  // SPJ-1718: cancellazione rapida di uno SKU dal risultato, in modifica manuale. Lo toglie dal
  // negozio selezionato in tutte le consegne e ricalcola i totali; si salva con "Salva" come le
  // altre modifiche (optim-update-run riscrive la run senza quelle righe).
  const handleRemoveSkuFromShop = useCallback((skuName: string) => {
    if (!isManualEditMode || isAreaView || !editedData?.metrics_per_consegna_per_shop || !skuName) {
      return;
    }
    const shopId = currentSelectedShop || Object.keys(editedData.metrics_per_consegna_per_shop)[0];
    if (!shopId || !editedData.metrics_per_consegna_per_shop[shopId]) {
      return;
    }

    const nextData = cloneJson(editedData);
    const shopConsegnas = nextData.metrics_per_consegna_per_shop[shopId]
      .map((consegna) => (
        consegna.name.toLowerCase() === 'all'
          ? consegna
          : {
              ...consegna,
              tf: consegna.tf
                .map((tf) => ({ ...tf, sku: tf.sku.filter((sku) => sku.name !== skuName) }))
                .filter((tf) => tf.sku.length > 0),
            }
      ));
    nextData.metrics_per_consegna_per_shop[shopId] = rebuildAllConsegnaForShop(
      shopConsegnas.map((consegna) => (
        consegna.name.toLowerCase() === 'all' ? consegna : recomputeConsegnaMetrics(consegna)
      )),
    );
    setEditedData(nextData);
    setSearchResults([]);
    setCurrentSearchIndex(0);
  }, [isManualEditMode, isAreaView, editedData, currentSelectedShop]);

  // SPJ-1714: modifica dalla tab All con le righe esplose per consegna. Cambia una taglia nella
  // consegna indicata; se lo SKU li' non c'e' lo crea (copia a zero di una consegna che lo ha),
  // aggiorna la quantita' dello SKU e ricalcola i totali, tab All compresa.
  const handleAllTabConsegnaTagliaChange = useCallback((
    targetConsegnaName: string,
    tfName: string,
    skuName: string,
    groupId: string,
    size: string,
    rawValue: number | string,
  ) => {
    if (!isManualEditMode || isAreaView || !editedData?.metrics_per_consegna_per_shop) return;
    const shopId = currentSelectedShop || Object.keys(editedData.metrics_per_consegna_per_shop)[0];
    if (!shopId || !editedData.metrics_per_consegna_per_shop[shopId]) return;
    const newValue = normalizzaQuantitaTaglia(rawValue);

    const nextData = cloneJson(editedData);
    const shopConsegnas = nextData.metrics_per_consegna_per_shop[shopId];
    const targetConsegna = shopConsegnas.find((consegna) => consegna.name === targetConsegnaName);
    if (!targetConsegna || targetConsegna.name.toLowerCase() === 'all') return;

    let targetTf = targetConsegna.tf.find((tf) => tf.name === tfName);
    let targetSku = targetTf?.sku.find((sku) => sku.name === skuName);
    if (!targetSku) {
      // modello: lo stesso SKU in un'altra consegna del negozio, azzerato
      let templateTf: TFData | undefined;
      let templateSku: SkuData | undefined;
      for (const consegna of shopConsegnas) {
        if (consegna.name.toLowerCase() === 'all') continue;
        const tf = consegna.tf.find((candidate) => candidate.name === tfName);
        const sku = tf?.sku.find((candidate) => candidate.name === skuName);
        if (tf && sku) {
          templateTf = tf;
          templateSku = sku;
          break;
        }
      }
      if (!templateTf || !templateSku) return;
      if (!targetTf) {
        targetTf = {
          name: tfName,
          all: cloneJson(templateTf.all),
          sku: [],
          taglie_reference_distributions: templateTf.taglie_reference_distributions,
        };
        targetConsegna.tf.push(targetTf);
      }
      targetSku = zeroOptimizedSkuQuantities(cloneJson(templateSku));
      targetTf.sku.push(targetSku);
    }

    const distribution = targetSku.metrics.taglie_distribution || {};
    if (!distribution[groupId]) distribution[groupId] = {};
    distribution[groupId][size] = newValue;
    targetSku.metrics.taglie_distribution = distribution;
    const totalQuantity = getTaglieDistributionTotal(distribution);
    const meanPrice = metricNumber(targetSku.metrics, 'mean_price');
    targetSku.metrics.total_quantity = totalQuantity;
    targetSku.metrics.unique_skus = totalQuantity > 0 ? 1 : 0;
    if (meanPrice > 0) {
      targetSku.metrics.profit = meanPrice * totalQuantity;
    }

    nextData.metrics_per_consegna_per_shop[shopId] = rebuildAllConsegnaForShop(
      shopConsegnas.map((consegna) => (
        consegna.name.toLowerCase() === 'all' ? consegna : recomputeConsegnaMetrics(consegna)
      )),
    );
    setEditedData(nextData);
  }, [isManualEditMode, isAreaView, editedData, currentSelectedShop]);

  const handleMoveSkuToConsegna = useCallback((
    sourceConsegnaName: string,
    targetConsegnaName: string,
    tfName: string,
    skuName: string,
  ) => {
    if (!isManualEditMode || isAreaView || !editedData?.metrics_per_consegna_per_shop) {
      return;
    }
    if (!sourceConsegnaName || !targetConsegnaName || sourceConsegnaName === targetConsegnaName) {
      return;
    }

    const shopId = currentSelectedShop || Object.keys(editedData.metrics_per_consegna_per_shop)[0];
    if (!shopId || !editedData.metrics_per_consegna_per_shop[shopId]) {
      return;
    }

    const nextData = cloneJson(editedData);
    const shopConsegnas = nextData.metrics_per_consegna_per_shop[shopId];
    const sourceConsegna = shopConsegnas.find((consegna) => consegna.name === sourceConsegnaName);
    const targetConsegna = shopConsegnas.find((consegna) => consegna.name === targetConsegnaName);
    if (!sourceConsegna || !targetConsegna || sourceConsegna.name.toLowerCase() === 'all' || targetConsegna.name.toLowerCase() === 'all') {
      return;
    }

    const sourceTf = sourceConsegna.tf.find((tf) => tf.name === tfName);
    if (!sourceTf) {
      return;
    }
    const sourceSkuIndex = sourceTf.sku.findIndex((sku) => sku.name === skuName);
    if (sourceSkuIndex === -1) {
      return;
    }

    const sourceSku = sourceTf.sku[sourceSkuIndex];
    const movingSku = cloneJson(sourceSku);
    if (getTaglieDistributionTotal(movingSku.metrics?.taglie_distribution) <= 0) {
      return;
    }

    sourceTf.sku[sourceSkuIndex] = zeroOptimizedSkuQuantities(sourceSku);

    let targetTf = targetConsegna.tf.find((tf) => tf.name === tfName);
    if (!targetTf) {
      targetTf = {
        name: tfName,
        all: cloneJson(sourceTf.all),
        sku: [],
        taglie_reference_distributions: sourceTf.taglie_reference_distributions,
      };
      targetConsegna.tf.push(targetTf);
    }

    const existingTargetSkuIndex = targetTf.sku.findIndex((sku) => sku.name === skuName);
    if (existingTargetSkuIndex >= 0) {
      targetTf.sku[existingTargetSkuIndex] = combineSkuData(targetTf.sku[existingTargetSkuIndex], movingSku);
    } else {
      targetTf.sku.push(movingSku);
    }

    const recomputedConsegnas = rebuildAllConsegnaForShop(
      shopConsegnas.map((consegna) => recomputeConsegnaMetrics(consegna)),
    );
    nextData.metrics_per_consegna_per_shop[shopId] = recomputedConsegnas;
    setEditedData(nextData);
    setSearchResults([]);
    setCurrentSearchIndex(0);

    // SPJ-1865: si resta sulla tab corrente (prima si saltava alla consegna di destinazione,
    // e il buyer perdeva il punto in cui stava lavorando); basta evidenziare la riga spostata.
    setHighlightedTf(skuName);
    setTimeout(() => setHighlightedTf(null), 2000);
  }, [currentSelectedShop, editedData, isAreaView, isManualEditMode]);

  const handleUpdateConstraint = async (
    skuName: string,
    type: "lb" | "ub",
    value: number | null
  ) => {
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!threadId || !messageId || !constraints) {
        console.error("Missing required data for constraint update");
        return;
      }

      const updatedConstraints = JSON.parse(JSON.stringify(constraints));

      // Initialize objects if they don't exist
      if (!updatedConstraints.constraints) {
        updatedConstraints.constraints = {};
      }
      if (!updatedConstraints.constraints.lb_quantity) {
        updatedConstraints.constraints.lb_quantity = {};
      }
      if (!updatedConstraints.constraints.ub_quantity) {
        updatedConstraints.constraints.ub_quantity = {};
      }

      if (type === "lb") {
        if (value !== null) {
          updatedConstraints.constraints.lb_quantity[skuName] = value;
        } else {
          delete updatedConstraints.constraints.lb_quantity[skuName];
        }
      } else {
        if (value !== null) {
          updatedConstraints.constraints.ub_quantity[skuName] = value;
        } else {
          delete updatedConstraints.constraints.ub_quantity[skuName];
        }
      }

      // Update locally first for responsiveness
      setConstraints(updatedConstraints);
      
      // Clear any errors for this SKU when constraint is updated
      if (constraintErrors[skuName]) {
        const newErrors = { ...constraintErrors };
        delete newErrors[skuName];
        setConstraintErrors(newErrors);
      }

      // Then update on server.
      // For SKU lower bounds use optim-upload-skus-list so backend can persist per-shop config.
      if (type === "lb") {
        await performAction({
          threadId,
          messageId,
          actionName: "optim-upload-skus-list",
          params: {
            "optim-skus-list": [skuName],
            "optim-lb-quantity": value ?? 0,
            ...(currentSelectedShop ? { "optim-shop-id": currentSelectedShop } : {}),
          },
        });
      }

      await performAction({
        threadId,
        messageId,
        actionName: "optim-update-config",
        params: {
          "optim-config-data": updatedConstraints,
        },
      });
    } catch (err) {
      console.error("Error updating constraint:", err);
      alert("Error updating constraint");
    }
  };

  const handleExcludeSku = async (skuName: string) => {
    try {
      // Only allow in optimization mode
      if (!isOptimizationMode) {
        return;
      }

      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!threadId || !messageId || !constraints) {
        console.error("Missing required data");
        return;
      }

      const updatedConstraints = JSON.parse(JSON.stringify(constraints));

      // Initialize objects if they don't exist
      if (!updatedConstraints.constraints) {
        updatedConstraints.constraints = {};
      }
      if (!updatedConstraints.constraints.lb_quantity) {
        updatedConstraints.constraints.lb_quantity = {};
      }
      if (!updatedConstraints.constraints.ub_quantity) {
        updatedConstraints.constraints.ub_quantity = {};
      }

      // Toggle exclusion: if excluded, remove constraints; if not excluded, set both to 0
      const isCurrentlyExcluded = excludedSkus.has(skuName);
      const newExcludedSkus = new Set(excludedSkus);

      if (isCurrentlyExcluded) {
        // Remove exclusion (remove constraints)
        delete updatedConstraints.constraints.lb_quantity[skuName];
        delete updatedConstraints.constraints.ub_quantity[skuName];
        newExcludedSkus.delete(skuName);
      } else {
        // Add exclusion (set both to 0)
        updatedConstraints.constraints.lb_quantity[skuName] = 0;
        updatedConstraints.constraints.ub_quantity[skuName] = 0;
        newExcludedSkus.add(skuName);
      }

      // Update local state
      setExcludedSkus(newExcludedSkus);
      setConstraints(updatedConstraints);
      
      // Clear any errors for this SKU
      if (constraintErrors[skuName]) {
        const newErrors = { ...constraintErrors };
        delete newErrors[skuName];
        setConstraintErrors(newErrors);
      }

      // Update on server
      await performAction({
        threadId,
        messageId,
        actionName: "optim-update-config",
        params: {
          "optim-config-data": updatedConstraints,
        },
      });
    } catch (err) {
      console.error("Error excluding SKU:", err);
      alert("Error excluding product");
    }
  };

  // Helper function to parse pasted SKU text
  const parseSkuText = useCallback((text: string): string[] => {
    if (!text || !text.trim()) {
      return [];
    }
    
    // Split by newlines, commas, or spaces, then filter and trim
    const skus = text
      .split(/[\n,\s]+/)
      .map(sku => sku.trim())
      .filter(sku => sku.length > 0);
    
    return skus;
  }, []);


  const handleAddProducts = useCallback(async (skus: string[], constraintsData?: Record<string, { lb?: number; ub?: number }>) => {
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!threadId || !messageId) {
        throw new Error("Thread or message ID not available");
      }

      if (isOptimizationMode) {
        // In optimization mode, add SKUs with constraints to the config
        if (!constraints) {
          throw new Error("Constraints not available in optimization mode");
        }

        const updatedConstraints = JSON.parse(JSON.stringify(constraints));

        // Initialize objects if they don't exist
        if (!updatedConstraints.constraints) {
          updatedConstraints.constraints = {};
        }
        if (!updatedConstraints.constraints.lb_quantity) {
          updatedConstraints.constraints.lb_quantity = {};
        }
        if (!updatedConstraints.constraints.ub_quantity) {
          updatedConstraints.constraints.ub_quantity = {};
        }

        // Add constraints for each new SKU
        skus.forEach(sku => {
          const skuConstraint = constraintsData?.[sku];
          if (skuConstraint) {
            if (skuConstraint.lb !== undefined) {
              updatedConstraints.constraints.lb_quantity[sku] = skuConstraint.lb;
            }
            if (skuConstraint.ub !== undefined) {
              updatedConstraints.constraints.ub_quantity[sku] = skuConstraint.ub;
            }
          }
        });

        const lbValues = constraintsData
          ? skus.map((sku) => constraintsData[sku]?.lb ?? 1)
          : 1;

        // Update constraints
        setConstraints(updatedConstraints);
        await performAction({
          threadId,
          messageId,
          actionName: "optim-upload-skus-list",
          params: {
            "optim-skus-list": skus,
            "optim-lb-quantity": lbValues,
            ...(currentSelectedShop ? { "optim-shop-id": currentSelectedShop } : {}),
          },
        });
        await performAction({
          threadId,
          messageId,
          actionName: "optim-update-config",
          params: {
            "optim-config-data": updatedConstraints,
          },
        });

        setIsAddProductModalOpen(false);
      } else {
        // In manual edit mode, call optim-add-sku for each SKU (fork already done in handleManualEditClick)
        for (const sku of skus) {
          await performAction({
            threadId,
            messageId,
            actionName: "optim-add-sku",
            params: {
              "optim-sku": sku,
            },
          });
        }

        // Reload data by calling optim-get-optimization-result
        const result = await performAction({
          threadId,
          messageId,
          actionName: "optim-get-optimization-result",
          params: {},
        });

        setData(result as ApiResponse);
        setEditedData(result as ApiResponse);
        setIsAddProductModalOpen(false);

        // Refetch treemap and constraints since run data has changed
        await refetchVisualizationData();
      }
    } catch (err) {
      console.error("Error adding products:", err);
      alert(`Error adding products: ${(err as Error).message}`);
    }
  }, [thread.threadId, thread.threadData, refetchVisualizationData, isOptimizationMode, constraints, currentSelectedShop]);

  const handleOptimize = async () => {
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.[
        "current-message"
      ] as string;

      if (!threadId || !messageId) {
        throw new Error("Thread or message ID not available");
      }

      // Validate constraints before launching optimization
      const errors: Record<string, string> = {};
      
      if (constraints?.constraints) {
        const lbConstraints = constraints.constraints.lb_quantity || {};
        const ubConstraints = constraints.constraints.ub_quantity || {};
        
        // Get all SKUs that have constraints
        const allSkus = new Set([
          ...Object.keys(lbConstraints),
          ...Object.keys(ubConstraints),
        ]);
        
        allSkus.forEach(sku => {
          const lb = lbConstraints[sku];
          const ub = ubConstraints[sku];
          
          // Check if lb is negative
          if (lb !== undefined && lb < 0) {
            errors[sku] = `Minimum value (${lb}) cannot be negative`;
          }
          
          // Check if ub is less than lb
          if (lb !== undefined && ub !== undefined && ub < lb) {
            errors[sku] = `Maximum value (${ub}) cannot be less than minimum (${lb})`;
          }
        });
      }
      
      // If there are errors, show them and don't proceed
      if (Object.keys(errors).length > 0) {
        setConstraintErrors(errors);
        
        // Scroll to the first error
        const firstErrorSku = Object.keys(errors)[0];
        setTimeout(() => {
          if (tableBodyRef.current) {
            const errorRow = tableBodyRef.current.querySelector(`[data-sku-name="${firstErrorSku}"]`);
            if (errorRow) {
              errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 100);
        
        setAlertModal({
          isOpen: true,
          title: 'Vincoli Non Validi',
          message: `Sono presenti ${Object.keys(errors).length} vincoli non validi. Correggerli prima di procedere con l'ottimizzazione.`,
          type: 'error',
        });
        
        return;
      }
      
      // Clear any previous errors
      setConstraintErrors({});

      setIsOptimizing(true);
      setOptimizationProgress(0);

      // AUTOMATIC OPTIMIZATION: Launch optimization with solver
      await performAction({
        threadId,
        messageId,
        actionName: "optim-launch-optimization",
        params: {},
      });

      // Poll for results with progress simulation
      const startTime = Date.now();
      const totalDuration = 120000; // 120 seconds (2 minutes)
      const pollInterval = 2000; // Poll every 2 seconds
      let optimizationComplete = false;

      // Progress simulation interval
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / totalDuration) * 100, 95); // Cap at 95% until complete
        
        // Slow down progress in the final 10%
        if (progress > 85) {
          const slowProgress = 85 + ((progress - 85) * 0.5);
          setOptimizationProgress(Math.floor(slowProgress));
        } else {
          setOptimizationProgress(Math.floor(progress));
        }
      }, 500);

      const pollOptimization = async (): Promise<void> => {
        try {
          const pollResult = await performAction({
            threadId,
            messageId,
            actionName: "optim-poll-optimization",
            params: {},
          });

          // Check if optimization is complete
          const status = (pollResult as any)?.status;
          const solverStatus = (pollResult as any)?.metrics?.status;
          const message = (pollResult as any)?.message;
          
          // Check if failed
          if (status === "failed") {
            optimizationComplete = true;
            clearInterval(progressInterval);
            
            // Stop optimization and show error message
            setIsOptimizing(false);
            setOptimizationProgress(0);
            
            // Display the error message in a modal with guidance
            const backendMessage = message || '';
            setAlertModal({
              isOpen: true,
              title: 'Ottimizzazione Fallita',
              message:
                "L'ottimizzazione è fallita.\n\nSi prega di:\n• Rivedere i parametri di configurazione\n• Verificare i vincoli impostati\n• Riprovare l'ottimizzazione\n\nSe il problema persiste, contattare l'amministratore.",
              errorLog: backendMessage || undefined,
              type: 'error',
            });
            
            // Do NOT reset optimization mode or constraints - keep them so user can adjust
            // Just exit without updating data
            return;
          }
          
          // Check if succeeded
          if (status === "succeeded") {
            optimizationComplete = true;
            clearInterval(progressInterval);
            setOptimizationProgress(100);
            
            // Check if solver status is INFEASIBLE
            if (solverStatus === "INFEASIBLE") {
              // Stop optimization and show error message
              setIsOptimizing(false);
              setOptimizationProgress(0);
              
              // Display the message from the backend in a modal
              const errorMessage = message || 'Il problema di ottimizzazione è infeasible. Rivedere i vincoli e riprovare.';
              setAlertModal({
                isOpen: true,
                title: 'Ottimizzazione Non Fattibile',
                message:
                  "Il problema di ottimizzazione è infeasible. Rivedere i vincoli e riprovare.",
                errorLog: errorMessage || undefined,
                type: 'error',
              });
              
              // Do NOT reset optimization mode or constraints - keep them so user can adjust
              // Just exit without updating data
              return;
            }
            
            // Wait a moment to show 100% before updating
            await new Promise(resolve => setTimeout(resolve, 500));

            // Refresh thread to get the new optimization run in the timeline
            await dispatch(fetchThread()).unwrap();

            // Reset optimization state
            setIsOptimizing(false);
            setOptimizationProgress(0);
            setIsOptimizationMode(false);
            
            // Reset flags for next optimization cycle
            setHasConstraintsBeenForked(false);
            setConstraints(null);
            setExcludedSkus(new Set());

            // Note: The new optimization run will be automatically selected by the backend
            // and the useEffect hooks will fetch data, treemap, and constraints automatically
            return;
          }

          // Continue polling if not complete
          if (!optimizationComplete) {
            setTimeout(pollOptimization, pollInterval);
          }
        } catch (pollErr) {
          console.error('Error during polling:', pollErr);
          // Continue polling on error unless it's a critical error
          if (!optimizationComplete) {
            setTimeout(pollOptimization, pollInterval);
          }
        }
      };

      // Start polling
      setTimeout(pollOptimization, pollInterval);

    } catch (err) {
      console.error("Error optimizing:", err);
      alert("Error during optimization");
      setIsOptimizing(false);
      setOptimizationProgress(0);
    }
  };

  const renderMetricsRow = (
    label: string,
    profit: number,
    meanPrice: number,
    uniqueSkus: number,
    totalQuantity: number,
    level: number,
    meanPriceWhs?: number,
    sellThrough?: number,
    modelsUnique?: number,
    forecastQuantity?: number,
    taglieDistribution?: Record<string, Record<string, number>>,
    expandKey?: string,
    hasChildren?: boolean,
    consegnaIndex?: number,
    tfIndex?: number,
    skuIndex?: number,
    skuName?: string,
    urlImgList?: string,
    sketchUrl?: string,
    weborderTags?: string[] | string,
    orderQuantity?: number,
    taglieOrder?: Record<string | number, Record<string, number>>,
    orderQuantityVenduta?: number,
    taglieOrderVenduta?: Record<string | number, Record<string, number>>,
    isReadOnlyView: boolean = false,
    taglieReferenceDistributions?: TaglieReferenceDistribution[],
    linkedOrderQuantity?: number,
    taglieLinkedOrder?: Record<string | number, Record<string, number>>,
    descrizioneColore?: string | null,
  ) => {
    const isExpanded = expandKey ? expanded[expandKey] : false;
    const indent = level * 24;
    const sortedTaglieDistribution = sortTaglieRecord(taglieDistribution);
    const sortedTaglieOrder = sortTaglieRecord(taglieOrder);
    const sortedTaglieOrderVenduta = sortTaglieRecord(taglieOrderVenduta);
    const sortedTaglieLinkedOrder = sortTaglieRecord(taglieLinkedOrder);

    // Helper to parse and display images
    const renderImages = () => {
      const images: string[] = [];
      
      // Parse url_img_list if available
      if (urlImgList) {
        try {
          let parsedUrls = urlImgList;
          
          // Handle Python set format: "{url1,url2,url3}" -> convert to array
          if (typeof parsedUrls === 'string' && parsedUrls.startsWith('{') && parsedUrls.endsWith('}') && !parsedUrls.includes(':')) {
            // Remove braces and split by comma
            const urls = parsedUrls.slice(1, -1).split(',').map(s => s.trim()).filter(s => s.length > 0);
            images.push(...urls);
          }
          // Handle string representation of Python list: "['url1', 'url2']"
          else if (typeof parsedUrls === 'string' && parsedUrls.startsWith('[') && parsedUrls.includes("'")) {
            // Python-style list: convert to JSON-compatible format
            const jsonCompatible = parsedUrls.replace(/'/g, '"');
            const parsed = JSON.parse(jsonCompatible);
            if (Array.isArray(parsed)) {
              images.push(...parsed.filter((v): v is string => typeof v === 'string' && v.length > 0));
            }
          }
          // If it's a JSON string with array or object format
          else if (typeof parsedUrls === 'string' && (parsedUrls.startsWith('{') || parsedUrls.startsWith('['))) {
            const parsed = JSON.parse(parsedUrls);
            if (Array.isArray(parsed)) {
              images.push(...parsed.filter((v): v is string => typeof v === 'string' && v.length > 0));
            } else if (typeof parsed === 'object') {
              // If it's an object, get the values
              images.push(...Object.values(parsed).filter((v): v is string => typeof v === 'string' && v.length > 0));
            }
          } else if (typeof parsedUrls === 'string' && parsedUrls.length > 0) {
            // Single URL string
            images.push(parsedUrls);
          }
        } catch (e) {
          // Silently fail parsing
        }
      }
      
      // Add sketch_url as fallback or additional image
      if (sketchUrl && !images.length) {
        images.push(sketchUrl);
      }

      // Build full Cloudinary URL with fixed dimensions
      const buildCloudinaryImageFromPath = (fullPath: string | null | undefined, width: number, height: number): string | null => {
        if (!fullPath) {
          return null;
        }
        // Use c_fill to crop and fill the entire area, g_center to center the crop
        return `https://media.brunellocucinelli.com/image/upload/c_fill,w_${width},h_${height},g_center/${fullPath}`;
      };

      // Use consistent fixed dimensions for vertical format
      const imageWidth = 24;
      const imageHeight = 40;
      const fullImageUrl = images.length > 0 ? buildCloudinaryImageFromPath(images[0], imageWidth, imageHeight) : null;

      const handleDetailsClick = () => {
        // Only open details for SKU level (level 2)
        if (level === 2 && skuName) {
          setDetailsModalSku(skuName);
          setIsDetailsModalOpen(true);
        }
      };

      const handleExcludeClick = () => {
        if (level === 2 && skuName) {
          handleExcludeSku(skuName);
        }
      };

      const isExcluded = level === 2 && skuName && excludedSkus.has(skuName);
      const canShowButtons = level === 2 && !!skuName;
      const canShowTags = level === 2 && !!skuName && !!weborderTags;
      const sourceConsegnaName = currentConsegnaData?.name ?? '';
      const sourceTfName = tfIndex !== undefined ? currentConsegnaData?.tf?.[tfIndex]?.name : undefined;
      const moveTargetOptions = reorderedDisplayConsegnas
        .filter((consegna) => consegna.name.toLowerCase() !== 'all' && consegna.name !== sourceConsegnaName)
        .map((consegna) => consegna.name);
      const canMoveSku = Boolean(
        level === 2 &&
        skuName &&
        sourceTfName &&
        sourceConsegnaName &&
        sourceConsegnaName.toLowerCase() !== 'all' &&
        !isAreaView &&
        isManualEditMode &&
        !isReadOnlyView &&
        moveTargetOptions.length > 0,
      );
      if (!fullImageUrl && !canShowButtons && !canShowTags && !canMoveSku) {
        return null;
      }

      return (
        <div className="flex gap-2 items-center ml-2">
          {fullImageUrl && (
            <img
              src={fullImageUrl}
              alt={label}
              className="object-cover border border-gray-300 rounded cursor-zoom-in"
              style={{
                width: `${imageWidth}px`,
                height: `${imageHeight}px`,
                minWidth: `${imageWidth}px`,
                maxWidth: `${imageWidth}px`,
                minHeight: `${imageHeight}px`,
                maxHeight: `${imageHeight}px`,
              }}
              // L'anteprima si apre solo passando sulla miniatura, non su tutta la riga:
              // l'URL ingrandito si calcola qui perche' buildCloudinaryImageFromPath vive
              // in questo scope, e mantiene le proporzioni 3:5 della miniatura.
              onMouseEnter={(e) => {
                const grande = buildCloudinaryImageFromPath(images[0], 200, 334);
                if (!grande) return;
                setImagePreview({
                  url: grande,
                  label,
                  x: e.clientX,
                  y: e.clientY,
                  ...(level === 2 ? { colore: descrizioneColore, rtl: meanPrice, whs: meanPriceWhs } : {}),
                });
              }}
              onMouseMove={(e) => {
                setImagePreview((precedente) =>
                  precedente ? { ...precedente, x: e.clientX, y: e.clientY } : precedente,
                );
              }}
              onMouseLeave={() => setImagePreview(null)}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {level === 2 && skuName && weborderTags && (() => {
            // Parse tags if they're in string format (comma-separated)
            let parsedTags: string[] = [];
            
            if (typeof weborderTags === 'string') {
              try {
                // Try to parse as JSON array
                const parsed = JSON.parse(weborderTags);
                if (Array.isArray(parsed)) {
                  parsedTags = parsed;
                }
              } catch {
                // If not JSON, split by comma for comma-separated strings
                parsedTags = weborderTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
              }
            } else if (Array.isArray(weborderTags)) {
              parsedTags = weborderTags;
            }
            
            if (parsedTags.length === 0) return null;
            
            return (
              <div className="flex flex-col gap-1 flex-wrap items-start" style={{ maxHeight: '40px', overflow: 'hidden' }}>
                {parsedTags.map((tag, index) => {
                  const bgColor = getTagColor(tag);
                  return (
                    <span
                      key={index}
                      className="px-2 py-0.5 font-semibold text-white"
                      style={{ 
                        backgroundColor: bgColor,
                        borderRadius: '8px',
                        fontSize: '9px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        lineHeight: '1.2',
                        opacity: 0.9
                      }}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {level === 2 && skuName && (
            <>
              <button
                onClick={handleDetailsClick}
                className="px-2 py-1 bg-black text-white text-xs font-medium hover:opacity-80 transition-opacity"
              >
                DETTAGLI
              </button>
              {/* Order history tooltip button - hover activated via portal */}
              {(orderQuantity !== undefined || linkedOrderQuantity !== undefined) && (
                <button
                  className="px-2 py-1 text-xs font-bold border border-gray-400 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center gap-1"
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;
                    const viewportPadding = 12;
                    const popupWidth = 320;
                    const popupHeight = Math.min(350, viewportHeight - viewportPadding * 2);
                    
                    // Calculate position - prefer to the right of the button
                    let left = rect.right + 8;
                    let top = rect.top;
                    
                    // Adjust if would overflow right - show to the left instead
                    if (left + popupWidth > viewportWidth - viewportPadding) {
                      left = rect.left - popupWidth - 8;
                    }
                    if (left < viewportPadding) {
                      left = Math.min(rect.left, viewportWidth - popupWidth - viewportPadding);
                    }

                    if (top + popupHeight > viewportHeight - viewportPadding) {
                      top = viewportHeight - popupHeight - viewportPadding;
                    }
                    if (top < viewportPadding) {
                      top = viewportPadding;
                    }

                    setOrderPopup({
                      visible: true,
                      x: left,
                      y: top,
                      orderQuantity: orderQuantity ?? 0,
                      taglieOrder: sortedTaglieOrder,
                      orderQuantityVenduta: orderQuantityVenduta,
                      taglieOrderVenduta: sortedTaglieOrderVenduta,
                      linkedOrderQuantity: linkedOrderQuantity,
                      taglieLinkedOrder: sortedTaglieLinkedOrder,
                      canPin: !!skuName,
                    });
                  }}
                  onMouseLeave={() => {
                    setOrderPopup(null);
                  }}
                  onClick={(e) => {
                    if (!skuName) return;
                    e.stopPropagation();
                    setOrderPopup(null);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPinnedOrders({
                      rect,
                      sku: skuName,
                      data: {
                        orderQuantity: orderQuantity ?? 0,
                        taglieOrder: sortedTaglieOrder,
                        orderQuantityVenduta: orderQuantityVenduta,
                        taglieOrderVenduta: sortedTaglieOrderVenduta,
                        linkedOrderQuantity: linkedOrderQuantity,
                        taglieLinkedOrder: sortedTaglieLinkedOrder,
                      },
                    });
                  }}
                  title={skuName ? 'Ordini già piazzati — clic per vedere gli altri negozi' : undefined}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {orderQuantity ?? 0}{linkedOrderQuantity ? ` +${linkedOrderQuantity}` : ''}
                </button>
              )}
              {canMoveSku && sourceTfName && skuName && (
                <select
                  value=""
                  title="Sposta completamente questo SKU in un'altra consegna per il negozio selezionato"
                  className="px-2 py-1 text-xs border border-[#9F6460] bg-white text-[#9F6460] font-semibold hover:bg-[#9F6460]/10"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const targetConsegnaName = e.target.value;
                    e.currentTarget.value = '';
                    if (!targetConsegnaName) return;
                    handleMoveSkuToConsegna(sourceConsegnaName, targetConsegnaName, sourceTfName, skuName);
                  }}
                >
                  <option value="">SPOSTA…</option>
                  {moveTargetOptions.map((consegnaName) => (
                    <option key={`${skuName}-${consegnaName}`} value={consegnaName}>
                      {consegnaName.replace(/_/g, ' ').toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
              {isManualEditMode && !isAreaView && !isReadOnlyView && skuName && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSkuFromShop(skuName);
                  }}
                  title="Togli questo SKU dal negozio selezionato (tutte le consegne). Si salva con Salva."
                  aria-label={`Togli ${skuName} dal negozio`}
                  className="px-2 py-1 text-xs font-bold border border-[#9F6460] bg-white text-[#9F6460] hover:bg-[#9F6460] hover:text-white transition-colors"
                >
                  ✕
                </button>
              )}
              {isOptimizationMode && !isReadOnlyView && (
                <button
                  onClick={handleExcludeClick}
                  className="px-2 py-1 text-xs font-bold border border-black transition-opacity text-black hover:opacity-80"
                  style={{ backgroundColor: isExcluded ? '#a5d8ff' : '#ffec99' }}
                >
                  {isExcluded ? "+" : "−"}
                </button>
              )}
            </>
          )}
        </div>
      );
    };

    // Render taglie distribution based on level
    const renderTaglieDistribution = () => {
      try {
        if (
          !sortedTaglieDistribution ||
          typeof sortedTaglieDistribution !== 'object' ||
          Object.keys(sortedTaglieDistribution).length === 0
        ) {
          return <span>-</span>;
        }

        // For level 0 (TOTALE) and level 1 (TF), show two separate mini bar charts side by side
        if (level === 0 || level === 1) {
          // Get the two groups (keys) from taglieDistribution
          const groupEntries = Object.entries(sortedTaglieDistribution);
          
          if (groupEntries.length === 0) return <span>-</span>;

          const references = level === 1 && Array.isArray(taglieReferenceDistributions)
            ? taglieReferenceDistributions.filter((entry) => entry?.distribution && typeof entry.distribution === 'object')
            : [];
          const isAllShopTfRow = Boolean(
            level === 1 &&
            !isAreaView &&
            currentConsegnaData &&
            currentConsegnaData.name.toLowerCase() === 'all'
          );

          if (isAllShopTfRow && groupEntries.length <= 1 && references.length > 1) {
            const combinedSizes = groupEntries.reduce<Record<string, number>>((acc, [_, sizes]) => {
              if (!sizes || typeof sizes !== 'object') return acc;
              Object.entries(sizes).forEach(([size, count]) => {
                acc[String(size)] = (acc[String(size)] || 0) + Number(count || 0);
              });
              return acc;
            }, {});
            const referenceSizeKeys = references.flatMap((entry) => (
              Object.entries(entry.distribution || {})
                .filter(([size, value]) => !isTagliaMetaKey(size) && Number(value) > 0)
                .map(([size]) => String(size))
            ));
            const sizeKeys = Array.from(new Set([
              ...Object.entries(combinedSizes).filter(([size, count]) => !isTagliaMetaKey(size) && count > 0).map(([size]) => String(size)),
              ...referenceSizeKeys,
            ])).sort(compareTaglie);

            if (sizeKeys.length === 0) return <span>-</span>;

            const entries = sizeKeys.map((size) => [size, Number(combinedSizes[size] ?? 0)] as [string, number]);
            const maxValue = Math.max(1, ...entries.map(([_, count]) => count));
            const chartHeight = 45;
            const referenceColors = ['#40586d', '#7d6647', '#9F6460', '#56705a'];
            const referenceLines = references
              .map((entry, index) => {
                const distribution = entry.distribution || {};
                const maxDistribution = Math.max(
                  0,
                  ...sizeKeys.map((size) => Number(distribution[size] ?? 0)),
                );
                if (maxDistribution <= 0) return null;

                const points = sizeKeys.map((size, sizeIndex) => {
                  const x = sizeKeys.length === 1 ? 0.5 : sizeIndex;
                  const normalizedValue = Number(distribution[size] ?? 0) / maxDistribution;
                  const y = chartHeight - (normalizedValue * chartHeight);
                  return `${x},${y}`;
                }).join(' ');

                return {
                  key: `all-reference-${entry.cod_tipo_taglia ?? index}-${index}`,
                  points,
                  color: referenceColors[index % referenceColors.length],
                };
              })
              .filter((line): line is { key: string; points: string; color: string } => Boolean(line));

            return (
              <div className="flex flex-col gap-1" style={{ width: "170px" }}>
                <div
                  className="relative flex items-end gap-1"
                  style={{ height: "50px", width: "170px" }}
                  title={referenceLines.length > 0 ? 'Barre: quantità ottimizzata aggregata per tipo filato. Linee: distribuzioni storiche normalizzate sovrapposte.' : undefined}
                >
                  {referenceLines.length > 0 && (
                    <svg
                      className="pointer-events-none absolute inset-0 z-10 h-full w-full overflow-visible"
                      viewBox={`0 0 ${Math.max(1, sizeKeys.length - 1)} ${chartHeight}`}
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      {referenceLines.map((line) => (
                        <polyline
                          key={line.key}
                          points={line.points}
                          fill="none"
                          stroke={line.color}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          opacity="0.9"
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </svg>
                  )}
                  {entries.map(([size, count]) => {
                    const heightPx = count > 0 ? Math.max(2, (count / maxValue) * chartHeight) : 0;
                    return (
                      <div
                        key={size}
                        className="relative z-20 flex flex-1 flex-col items-center justify-end"
                        style={{ minWidth: 0 }}
                      >
                        <div
                          className="w-full rounded-t bg-[#9F6460]/85"
                          style={{ height: `${heightPx}px` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-1">
                  {entries.map(([size, _]) => (
                    <div
                      key={size}
                      className="flex-1 text-center text-[10px]"
                      style={{ minWidth: 0, color: "#9F6460" }}
                    >
                      {size}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div className="flex gap-4">
              {groupEntries.map(([groupId, sizes]) => {
                if (!sizes || typeof sizes !== 'object') return null;

                const references = level === 1 && Array.isArray(taglieReferenceDistributions)
                  ? taglieReferenceDistributions.filter((entry) => entry?.distribution && typeof entry.distribution === 'object')
                  : [];
                const normalizedGroupId = normalizeCodTipoTaglia(groupId);
                const matchingReferences = references.filter((entry) => normalizeCodTipoTaglia(entry.cod_tipo_taglia) === normalizedGroupId);
                const referencesForGroup = matchingReferences.length > 0 ? matchingReferences : (groupEntries.length === 1 ? references : []);
                const referenceSizeKeys = referencesForGroup.flatMap((entry) => (
                  Object.entries(entry.distribution || {})
                    .filter(([size, value]) => !isTagliaMetaKey(size) && Number(value) > 0)
                    .map(([size]) => String(size))
                ));
                const sizeKeys = Array.from(new Set([
                  ...Object.entries(sizes).filter(([size, count]) => !isTagliaMetaKey(size) && count > 0).map(([size]) => String(size)),
                  ...referenceSizeKeys,
                ])).sort(compareTaglie);

                if (sizeKeys.length === 0) return null;

                const entries = sizeKeys.map((size) => [size, Number(sizes[size] ?? 0)] as [string, number]);
                const maxValue = Math.max(1, ...entries.map(([_, count]) => count));
                const chartHeight = 45;
                const referenceColors = ['#40586d', '#7d6647', '#9F6460', '#56705a'];
                const referenceLines = referencesForGroup
                  .map((entry, index) => {
                    const distribution = entry.distribution || {};
                    const maxDistribution = Math.max(
                      0,
                      ...sizeKeys.map((size) => Number(distribution[size] ?? 0)),
                    );
                    if (maxDistribution <= 0) return null;

                    const points = sizeKeys.map((size, sizeIndex) => {
                      const x = sizeKeys.length === 1 ? 0.5 : sizeIndex;
                      const normalizedValue = Number(distribution[size] ?? 0) / maxDistribution;
                      const y = chartHeight - (normalizedValue * chartHeight);
                      return `${x},${y}`;
                    }).join(' ');

                    return {
                      key: `${groupId}-reference-${index}`,
                      points,
                      color: referenceColors[index % referenceColors.length],
                      label: entry.storico_totale ? `Storico ${entry.storico_totale}` : 'Distribuzione riferimento',
                    };
                  })
                  .filter((line): line is { key: string; points: string; color: string; label: string } => Boolean(line));

                return (
                  <div key={groupId} className="flex flex-col gap-1">
                    {groupEntries.length > 1 && (
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 text-left">
                        {getCodTipoTagliaLabel(groupId)}
                      </div>
                    )}
                    <div
                      className="relative flex items-end gap-1"
                      style={{ height: "50px" }}
                      title={referenceLines.length > 0 ? 'Barre: quantità ottimizzata. Linea: distribuzione storica normalizzata.' : undefined}
                    >
                      {referenceLines.length > 0 && (
                        <svg
                          className="pointer-events-none absolute inset-0 z-10 h-full w-full overflow-visible"
                          viewBox={`0 0 ${Math.max(1, sizeKeys.length - 1)} ${chartHeight}`}
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          {referenceLines.map((line) => (
                            <polyline
                              key={line.key}
                              points={line.points}
                              fill="none"
                              stroke={line.color}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              opacity="0.9"
                              vectorEffect="non-scaling-stroke"
                            />
                          ))}
                        </svg>
                      )}
                      {entries.map(([size, count]) => {
                        const heightPx = count > 0 ? Math.max(2, (count / maxValue) * chartHeight) : 0;
                        return (
                          <div
                            key={size}
                            className="relative z-20 flex flex-1 flex-col items-center justify-end"
                            style={{ minWidth: "20px" }}
                          >
                            <div
                              className="w-full rounded-t bg-[#9F6460]/85"
                              style={{
                                height: `${heightPx}px`,
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-1">
                      {entries.map(([size, _]) => (
                        <div
                          key={size}
                          className="flex-1 text-center text-xs"
                          style={{ minWidth: "20px", color: "#9F6460" }}
                        >
                          {size}
                        </div>
                      ))}
                    </div>

                  </div>
                );
              })}
            </div>
          );
        }

        // For SKU level (level 2), show editable input boxes for each size
        if (level === 2) {
          const isAllTab = currentConsegnaData && currentConsegnaData.name.toLowerCase() === 'all';
          const currentShopIdForEdit =
            currentSelectedShop ||
            Object.keys((editedData || data || { metrics_per_consegna_per_shop: {} }).metrics_per_consegna_per_shop || {})[0] ||
            '';
          const tfNameForSku =
            tfIndex !== undefined &&
            currentConsegnaData &&
            Array.isArray(currentConsegnaData.tf) &&
            currentConsegnaData.tf[tfIndex]
              ? currentConsegnaData.tf[tfIndex].name
              : undefined;

          const targetConsegnaOptions = reorderedDisplayConsegnas
            .filter((c: ConsegnaData) => c.name.toLowerCase() !== 'all')
            .map((c: ConsegnaData) => c.name);
          const allTabTargetKey =
            currentShopIdForEdit && tfNameForSku && skuName
              ? `${currentShopIdForEdit}||${tfNameForSku}||${skuName}`
              : '';
          const selectedTargetConsegna =
            (allTabTargetKey && allTabSkuTargetConsegna[allTabTargetKey]) ||
            targetConsegnaOptions[0] ||
            '';

          // Get the actual distribution from editedData if in manual edit mode
          let actualDistribution: Record<string, Record<string, number>> | undefined = sortedTaglieDistribution;
          if (
            isManualEditMode &&
            editedData &&
            editedData.metrics_per_consegna_per_shop &&
            consegnaIndex !== undefined &&
            tfIndex !== undefined &&
            skuIndex !== undefined
          ) {
            // Get the shop ID for the current view
            const shopId = currentSelectedShop || Object.keys(editedData.metrics_per_consegna_per_shop)[0];
            if (shopId && editedData.metrics_per_consegna_per_shop[shopId]) {
              if (isAllTab && selectedTargetConsegna && tfNameForSku && skuName) {
                const targetConsegna = editedData.metrics_per_consegna_per_shop[shopId].find(
                  (c) => c.name === selectedTargetConsegna
                );
                const targetTf = targetConsegna?.tf.find((tf) => tf.name === tfNameForSku);
                const targetSku = targetTf?.sku.find((sku) => sku.name === skuName);
                const editedDistribution = targetSku?.metrics?.taglie_distribution;
                if (editedDistribution && Object.keys(editedDistribution).length > 0) {
                  actualDistribution = sortTaglieRecord(editedDistribution);
                }
              } else {
                const sourceConsegna = editedData.metrics_per_consegna_per_shop[shopId][consegnaIndex];
                const sourceTf = tfNameForSku
                  ? sourceConsegna?.tf.find((tf) => tf.name === tfNameForSku)
                  : sourceConsegna?.tf[tfIndex];
                const sourceSku = skuName
                  ? sourceTf?.sku.find((sku) => sku.name === skuName)
                  : sourceTf?.sku[skuIndex];
                const editedDistribution = sourceSku?.metrics?.taglie_distribution;
                // Only use editedData if it's not empty
                if (editedDistribution && Object.keys(editedDistribution).length > 0) {
                  actualDistribution = sortTaglieRecord(editedDistribution);
                }
              }
            }
          }

          // SPJ-1714: nella tab All, in modifica manuale, una riga per consegna (una sotto
          // l'altra) con le taglie modificabili di ciascuna e lo switch rapido "sposta in".
          const showAllTabExploded =
            isAllTab &&
            !isAreaView &&
            isManualEditMode &&
            !isReadOnlyView &&
            Boolean(tfNameForSku && skuName) &&
            targetConsegnaOptions.length > 0 &&
            Boolean(editedData?.metrics_per_consegna_per_shop?.[currentShopIdForEdit]);
          if (showAllTabExploded && tfNameForSku && skuName) {
            const shopConsegnasForEdit = editedData!.metrics_per_consegna_per_shop[currentShopIdForEdit];
            const templateDistribution = zeroTaglieDistributionRecord(sortedTaglieDistribution);
            return (
              <div className="flex flex-col gap-1">
                {targetConsegnaOptions.map((consegnaName) => {
                  const consegnaSku = shopConsegnasForEdit
                    .find((consegna) => consegna.name === consegnaName)
                    ?.tf.find((tf) => tf.name === tfNameForSku)
                    ?.sku.find((sku) => sku.name === skuName);
                  const consegnaDistribution = sortTaglieRecord(
                    consegnaSku?.metrics?.taglie_distribution && Object.keys(consegnaSku.metrics.taglie_distribution).length > 0
                      ? consegnaSku.metrics.taglie_distribution
                      : templateDistribution,
                  );
                  const sizes: Array<{ size: string; count: number; groupId: string }> = [];
                  Object.entries(consegnaDistribution || {}).forEach(([groupId, groupSizes]) => {
                    Object.entries(groupSizes || {}).forEach(([size, count]) => {
                      if (!isTagliaMetaKey(size)) sizes.push({ size, count: Number(count) || 0, groupId });
                    });
                  });
                  sizes.sort((a, b) => compareTaglie(a.size, b.size) || a.groupId.localeCompare(b.groupId, 'en', { numeric: true }));
                  const consegnaTotal = sizes.reduce((sum, item) => sum + normalizzaQuantitaTaglia(item.count), 0);
                  const moveTargets = targetConsegnaOptions.filter((name) => name !== consegnaName);
                  return (
                    <div key={`${skuName}-${consegnaName}`} className="flex items-center gap-2">
                      <span
                        className="w-20 shrink-0 truncate text-[10px] font-semibold uppercase tracking-wide"
                        style={{ color: consegnaTotal > 0 ? '#9F6460' : '#9ca3af' }}
                        title={consegnaName.replace(/_/g, ' ')}
                      >
                        {consegnaName.replace(/_/g, ' ')}
                      </span>
                      <div className="flex gap-2">
                        {sizes.map(({ size, count, groupId }) => (
                          <div key={`${consegnaName}-${groupId}-${size}`} className="flex items-center gap-1" title={getCodTipoTagliaLabel(groupId)}>
                            <span className="text-sm font-medium" style={{ color: '#9F6460' }}>{size}</span>
                            <input
                              type="number"
                              min="0"
                              value={count}
                              aria-label={`${skuName} ${consegnaName} taglia ${size}`}
                              onChange={(e) => handleAllTabConsegnaTagliaChange(
                                consegnaName, tfNameForSku, skuName, groupId, size, e.target.value,
                              )}
                              style={{
                                color: '#9F6460',
                                ...(normalizzaQuantitaTaglia(count) > 0 ? { backgroundColor: '#F5E6C8' } : {}),
                              }}
                              className="w-6 px-0.5 py-1 text-sm text-center border border-black focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        ))}
                      </div>
                      <span className="w-8 text-right text-xs tabular-nums text-gray-600">{consegnaTotal}</span>
                      {consegnaTotal > 0 && moveTargets.length > 0 && (
                        <select
                          value=""
                          title={`Sposta tutti i pezzi di ${consegnaName.replace(/_/g, ' ')} in un'altra consegna`}
                          className="px-1 py-0.5 text-[11px] border border-[#9F6460] bg-white text-[#9F6460] font-semibold"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const target = e.target.value;
                            e.currentTarget.value = '';
                            if (target) handleMoveSkuToConsegna(consegnaName, target, tfNameForSku, skuName);
                          }}
                        >
                          <option value="">⇄ sposta in…</option>
                          {moveTargets.map((name) => (
                            <option key={`${skuName}-${consegnaName}-to-${name}`} value={name}>
                              {name.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }

          // Collect all sizes with their group IDs in original order
          const allSizesWithGroup: Array<{size: string; count: number; groupId: string}> = [];
          Object.entries(actualDistribution || {}).forEach(([groupId, sizes]) => {
            Object.entries(sizes).forEach(([size, count]) => {
              // In manual edit mode, show all sizes; otherwise only show sizes with count > 0
              if (!isTagliaMetaKey(size) && (isManualEditMode || count > 0)) {
                allSizesWithGroup.push({ size, count, groupId });
              }
            });
          });

          if (allSizesWithGroup.length === 0) return <span>-</span>;

          allSizesWithGroup.sort((a, b) => {
            const sizeOrder = compareTaglie(a.size, b.size);
            if (sizeOrder !== 0) {
              return sizeOrder;
            }
            return a.groupId.localeCompare(b.groupId, "en", { numeric: true });
          });

          const isEditDisabled = !isManualEditMode || isReadOnlyView;
          const showAllTabTargetSelector =
            isAllTab &&
            !isAreaView &&
            isManualEditMode &&
            !isReadOnlyView &&
            level === 2 &&
            Boolean(tfNameForSku && skuName) &&
            targetConsegnaOptions.length > 0;

          return (
            <div className="flex flex-col gap-1">
              {showAllTabTargetSelector && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">Consegna target</span>
                  <select
                    value={selectedTargetConsegna}
                    onChange={(e) => {
                      const nextTarget = e.target.value;
                      if (!allTabTargetKey) return;
                      setAllTabSkuTargetConsegna((prev) => ({
                        ...prev,
                        [allTabTargetKey]: nextTarget,
                      }));
                    }}
                    className="text-[11px] border border-gray-300 rounded px-1 py-0.5 bg-white"
                  >
                    {targetConsegnaOptions.map((consegnaName) => (
                      <option key={`${allTabTargetKey}-${consegnaName}`} value={consegnaName}>
                        {consegnaName.replace(/_/g, " ").toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
              {allSizesWithGroup.map(({ size, count, groupId }) => {
                return (
                  <div key={`${groupId}-${size}`} className="flex items-center gap-1" title={getCodTipoTagliaLabel(groupId)}>
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#9F6460" }}
                    >
                      {size}
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={count}
                      disabled={isEditDisabled}
                      title={showAllTabTargetSelector ? "Modifica applicata alla consegna target selezionata" : ""}
                      onChange={(e) => {
                        // Le quantita' negative non hanno significato e a valle rompono
                        // i calcoli: si riportano a zero subito, cosi' il campo mostra 0
                        // e zero e' anche quello che parte verso il backend.
                        // `parseInt(...) || 0` da solo non basta: -1 e' truthy e passava.
                        const newValue = normalizzaQuantitaTaglia(e.target.value);
                        if (
                          consegnaIndex !== undefined &&
                          tfIndex !== undefined &&
                          skuIndex !== undefined
                        ) {
                          handleTagliaChange(
                            consegnaIndex,
                            tfIndex,
                            skuIndex,
                            groupId,
                            size,
                            newValue,
                            showAllTabTargetSelector ? selectedTargetConsegna : undefined,
                            tfNameForSku,
                            skuName
                          );
                        }
                      }}
                      style={{
                        color: "#9F6460",
                        // In modifica manuale le taglie valorizzate si distinguono a colpo
                        // d'occhio da quelle a zero. Stesso giallo del pulsante "Modifica",
                        // cosi' il colore lega la modalita' alle celle che ne sono toccate.
                        // Fuori dalla modifica manuale la griglia resta com'era.
                        ...(!isEditDisabled && normalizzaQuantitaTaglia(count) > 0
                          ? { backgroundColor: "#F5E6C8" }
                          : {}),
                      }}
                      className={`w-6 px-0.5 py-1 text-sm text-center border border-black focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        isEditDisabled
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                );
              })}
              </div>
            </div>
          );
        }

        return <span>-</span>;
      } catch (error) {
        console.error(
          "Error rendering taglie distribution:",
          error,
          taglieDistribution
        );
        return <span>-</span>;
      }
    };

    const highlightedNames = Array.isArray(highlightedTf) ? highlightedTf : highlightedTf ? [highlightedTf] : [];
    const isHighlightedTf = level === 1 && highlightedNames.includes(label);
    const isHighlightedSku = level === 2 && skuName && highlightedNames.includes(skuName);
    const isHighlighted = isHighlightedTf || isHighlightedSku;
    const hasConstraintError = level === 2 && skuName && constraintErrors[skuName];

    return (
      <>
      <tr
        key={expandKey || label}
        className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
          isHighlighted ? 'animate-pulse-highlight' : ''
        } ${hasConstraintError ? 'bg-red-50' : ''} ${hasChildren ? 'cursor-pointer' : ''}`}
        style={{ position: 'relative', zIndex: 1 }}
        data-tf-name={level === 1 ? label : undefined}
        data-sku-name={level === 2 && skuName ? skuName : undefined}
        onClick={() => {
          if (hasChildren && expandKey) {
            toggleExpand(expandKey);
          }
        }}
      >
        <td
          className="px-4 py-3 sticky left-0 bg-white min-w-[350px]"
          style={{ 
            paddingLeft: `${indent + 16}px`, 
            position: 'sticky',
            left: 0,
            zIndex: 10,
            backgroundColor: hasConstraintError ? '#fef2f2' : '#ffffff',
            boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)'
          }}
        >
          <div className="flex items-center gap-2" style={{ overflow: 'hidden' }}>
            {hasChildren && expandKey && (
              <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleExpand(expandKey);
              }}
              className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 border border-black"
            >
              {isExpanded ? "−" : "+"}
            </button>
            )}
            <span
              className={`font-${
                level === 0 ? "bold" : "medium"
              } text-gray-900 whitespace-nowrap`}
            >
              {label}
            </span>
            {renderImages()}
          </div>
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {profit !== undefined ? (
            <span style={{ color: "#9F6460" }}>{`€${profit.toLocaleString(
              "it-IT",
              { minimumFractionDigits: 2 }
            )}`}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {meanPrice !== undefined ? (
            <span style={{ color: "#9F6460" }}>{`€${meanPrice.toLocaleString(
              "it-IT",
              { minimumFractionDigits: 2 }
            )}`}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {meanPriceWhs !== undefined ? (
            <span style={{ color: "#9F6460" }}>{`€${meanPriceWhs.toLocaleString(
              "it-IT",
              { minimumFractionDigits: 2 }
            )}`}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {sellThrough !== undefined ? (
            <span style={{ color: "#9F6460" }}>{`${(sellThrough).toFixed(1)}%`}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {modelsUnique !== undefined ? (
            <span style={{ color: "#9F6460" }}>{modelsUnique}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {uniqueSkus !== undefined ? (
            <span style={{ color: "#9F6460" }}>{uniqueSkus}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 text-center">
          {totalQuantity !== undefined ? (
            <div className="flex items-center justify-center gap-2">
          {isOptimizationMode && !isReadOnlyView && level === 2 && skuName ? (
              <>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-600">Min:</label>
                  <input
                    type="number"
                    min="0"
                    max={constraints?.constraints?.ub_quantity?.[skuName] || undefined}
                    value={constraints?.constraints?.lb_quantity?.[skuName] || ""}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseInt(e.target.value) : null;
                      handleUpdateConstraint(skuName, "lb", newValue);
                    }}
                    placeholder="0"
                    className={`w-16 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      (() => {
                        const minValue = constraints?.constraints?.lb_quantity?.[skuName];
                        const maxValue = constraints?.constraints?.ub_quantity?.[skuName];
                        const isInvalid = minValue !== undefined && maxValue !== undefined && minValue > maxValue;
                        return isInvalid 
                          ? 'border-red-500 bg-red-50 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500';
                      })()
                    }`}
                  />
                  {(() => {
                    const minValue = constraints?.constraints?.lb_quantity?.[skuName];
                    const maxValue = constraints?.constraints?.ub_quantity?.[skuName];
                    const isInvalid = minValue !== undefined && maxValue !== undefined && minValue > maxValue;
                    return isInvalid && (
                      <span className="text-xs text-red-500" title="Min cannot exceed Max">⚠️</span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-600">Max:</label>
                  <input
                    type="number"
                    min={constraints?.constraints?.lb_quantity?.[skuName] || 0}
                    value={constraints?.constraints?.ub_quantity?.[skuName] || ""}
                    onChange={(e) => {
                      const newValue = e.target.value ? parseInt(e.target.value) : null;
                      handleUpdateConstraint(skuName, "ub", newValue);
                    }}
                    placeholder="∞"
                    className={`w-16 px-2 py-1 text-xs text-center border rounded focus:outline-none focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      (() => {
                        const minValue = constraints?.constraints?.lb_quantity?.[skuName];
                        const maxValue = constraints?.constraints?.ub_quantity?.[skuName];
                        const isInvalid = minValue !== undefined && maxValue !== undefined && minValue > maxValue;
                        return isInvalid 
                          ? 'border-red-500 bg-red-50 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500';
                      })()
                    }`}
                  />
                  {(() => {
                    const minValue = constraints?.constraints?.lb_quantity?.[skuName];
                    const maxValue = constraints?.constraints?.ub_quantity?.[skuName];
                    const isInvalid = minValue !== undefined && maxValue !== undefined && minValue > maxValue;
                    return isInvalid && (
                      <span className="text-xs text-red-500" title="Max cannot be below Min">⚠️</span>
                    );
                  })()}
                </div>
              </>
              ) : (
                <span style={{ color: "#9F6460" }}>{totalQuantity}</span>
              )}
            </div>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 whitespace-nowrap text-center">
          {level === 2 && totalQuantity === 0 ? (
            "-"
          ) : forecastQuantity !== undefined && level === 2 && skuName && !isAreaView ? (
            // SPJ-1864: il forecast dello SKU apre i prodotti usati per calcolarlo.
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-1 hover:bg-[#9F6460]/10"
              style={{ color: "#9F6460" }}
              title="Prodotti usati per il forecast"
              onClick={(e) => {
                e.stopPropagation();
                setPinnedForecast({ rect: e.currentTarget.getBoundingClientRect(), sku: skuName, forecast: forecastQuantity });
              }}
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l6-6 4 4 8-8m0 0v5m0-5h-5" />
              </svg>
              {forecastQuantity}
            </button>
          ) : forecastQuantity !== undefined ? (
            <span style={{ color: "#9F6460" }}>{forecastQuantity}</span>
          ) : (
            "-"
          )}
        </td>
        <td className="text-gray-900 px-4 py-3 text-center">
          {renderTaglieDistribution()}
        </td>
      </tr>
      {hasConstraintError && (
        <tr className="bg-red-50">
          <td colSpan={10} className="px-4 py-2">
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <span className="font-semibold">⚠️ Errore:</span>
              <span>{constraintErrors[skuName!]}</span>
            </div>
          </td>
        </tr>
      )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-gray-500">Caricamento dati ottimizzazione...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-red-500">Errore: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-gray-500">Nessun dato disponibile</p>
      </div>
    );
  }

  const allConsegnas = getConsegnasForCurrentView(data);

  // Reorder consegnas: put "all" first, then the rest
  const consegnas = [
    ...allConsegnas.filter((c) => c.name.toLowerCase() === "all"),
    ...allConsegnas.filter((c) => c.name.toLowerCase() !== "all"),
  ];

  // Get current consegna data (use editedData if in manual edit mode, otherwise use data)
  // Fallback to data if editedData is not available
  const displayData = isManualEditMode ? (editedData || data) : data;
  const displayConsegnas = getConsegnasForCurrentView(displayData);
  const reorderedDisplayConsegnas: ConsegnaData[] = [
    ...displayConsegnas.filter((c: ConsegnaData) => c.name.toLowerCase() === "all"),
    ...displayConsegnas.filter((c: ConsegnaData) => c.name.toLowerCase() !== "all"),
  ];
  // SPJ-888: match helpers — modello/colore cercati nel nome SKU, tessuto nel tipo filato (tutti in AND)
  const modelTerm = modelFilter.trim().toLowerCase();
  const colorTerm = colorFilter.trim().toLowerCase();
  const tipoFilatoTerm = tipoFilatoFilter.trim().toLowerCase();
  const rowFilterActive = !!(modelTerm || colorTerm || tipoFilatoTerm);
  const skuMatchesFilter = (skuName?: string, tfName?: string): boolean => {
    const sku = String(skuName ?? '').toLowerCase();
    const tf = String(tfName ?? '').toLowerCase();
    if (modelTerm && !sku.includes(modelTerm)) return false;
    if (colorTerm && !sku.includes(colorTerm)) return false;
    if (tipoFilatoTerm && !tf.includes(tipoFilatoTerm)) return false;
    return true;
  };
  const tfMatchesFilter = (tfData: any): boolean => {
    if (!rowFilterActive) return true;
    const tfName = String(tfData?.name ?? '').toLowerCase();
    if (tipoFilatoTerm && !tfName.includes(tipoFilatoTerm)) return false;
    if (!modelTerm && !colorTerm) return true;
    return Array.isArray(tfData?.sku) && tfData.sku.some((s: any) => skuMatchesFilter(s?.name, tfData?.name));
  };

  const currentConsegnaData = reorderedDisplayConsegnas[activeTab];
  const currentSkuSortKey = `${currentSelectedShop ?? 'area'}:${currentConsegnaData?.name ?? activeTab}`;
  const currentSkuSort = skuSortByView[currentSkuSortKey] ?? DEFAULT_SKU_SORT;

  const getSkuSortValue = (skuData: SkuData, column: SkuSortColumn): number => {
    const metrics = skuData.metrics || ({} as MetricsData);
    switch (column) {
      case 'profit':
        return Number(metrics.profit ?? 0);
      case 'mean_price':
        return Number(metrics.mean_price ?? 0);
      case 'mean_weight':
        return Number(metrics.mean_weight ?? 0);
      case 'sellout':
        return Number(metrics.total_sellout_value_mean ?? 0);
      case 'models_unique':
        return Number(metrics.models_unique ?? 0);
      case 'unique_skus':
        return Number(metrics.unique_skus ?? 0);
      case 'forecast_quantity':
        return Number(metrics.forecast_quantity ?? 0);
      case 'order_quantity':
        return Number(metrics.order_quantity ?? 0);
      case 'linked_order_quantity':
        return Number(metrics.linked_order_quantity ?? 0);
      case 'total_quantity':
      default:
        return Number(metrics.total_quantity ?? 0);
    }
  };

  const sortSkusForCurrentView = (skus: SkuData[]): SkuData[] => {
    return [...skus].sort((a, b) => {
      const aValue = getSkuSortValue(a, currentSkuSort.column);
      const bValue = getSkuSortValue(b, currentSkuSort.column);
      const primary = aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
      const directed = currentSkuSort.direction === 'asc' ? primary : -primary;
      if (directed !== 0) return directed;
      return String(a.name).localeCompare(String(b.name), 'it-IT', { numeric: true });
    });
  };

  const setSkuSortForCurrentView = (column: SkuSortColumn) => {
    setSkuSortByView((prev) => {
      const previous = prev[currentSkuSortKey] ?? DEFAULT_SKU_SORT;
      const nextDirection: SortDirection = previous.column === column && previous.direction === 'desc' ? 'asc' : 'desc';
      return {
        ...prev,
        [currentSkuSortKey]: {
          column,
          direction: nextDirection,
        },
      };
    });
  };

  const renderSortableHeader = (label: string, column: SkuSortColumn, extraTitle?: string) => {
    const active = currentSkuSort.column === column;
    return (
      <button
        type="button"
        className={`inline-flex items-center justify-center gap-1 text-inherit transition-colors hover:text-[#9F6460] ${active ? 'text-[#9F6460]' : ''}`}
        title={extraTitle ?? 'Ordina gli SKU in questo tab'}
        onClick={() => setSkuSortForCurrentView(column)}
      >
        <span>{label}</span>
        <span className={`text-[10px] ${active ? 'opacity-100' : 'opacity-30'}`}>{active ? (currentSkuSort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    );
  };

  // If no data for current tab and not in Area view, show message
  if (!currentConsegnaData && !isAreaView) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-gray-500">
          Seleziona una consegna per visualizzare i dati
        </p>
      </div>
    );
  }

  // Find the original index of the current consegna in the unordered array
  // This is needed because activeTab indexes into reorderedDisplayConsegnas,
  // but handleTagliaChange needs the index in the original displayConsegnas for the current shop
  const originalConsegnaIndex = currentConsegnaData 
    ? displayConsegnas.findIndex((c) => c.name === currentConsegnaData.name)
    : -1;
  // Helper function to start resize
  const startResize = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingCard(true);
    setResizeDirection(direction);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: cardWidth,
      height: cardHeight,
      cardX: cardPosition.x,
      cardY: cardPosition.y
    });
  };
  return (
    <>
      <style>
        {`
          @keyframes pulse-highlight {
            0%, 100% {
              background-color: transparent;
            }
            50% {
              background-color: #fef08a;
            }
          }
          .animate-pulse-highlight {
            animation: pulse-highlight 0.5s ease-in-out 3;
          }
          .cursor-ns-resize {
            cursor: ns-resize;
          }
        `}
      </style>
      <div className="flex flex-col h-full relative">
        {/* Store/Area selector tabs - at the very top (only show if multi-shop) */}
        {isMultiShopOptimization && storesList.length > 0 && (
        <div className="flex items-center gap-3 border-b border-gray-300 bg-gray-50 px-4 py-1">
          {storesList.filter((st) => !st.isArea).length > 1 && (
            <div className="w-72 shrink-0">
              <ShopListFilter
                search={storeTabSearch}
                onSearch={setStoreTabSearch}
                nazione={storeTabNazione}
                onNazione={setStoreTabNazione}
                nazioni={nazioniOf(storesList.filter((st) => !st.isArea).map((st) => ({ nazione: nazioneOfShopId(st.id, shopNazione) })))}
                visible={storesList.filter((st, i) => st.isArea || i === selectedStoreIndex || matchesShop({ code: st.id.split('|,|').join(', '), name: st.name, nazione: nazioneOfShopId(st.id, shopNazione) }, storeTabSearch, storeTabNazione)).length}
                total={storesList.length}
              />
            </div>
          )}
          <div className="flex items-center overflow-x-auto">
            {storesList.map((store, index) => (store.isArea || index === selectedStoreIndex
              || matchesShop({ code: store.id.split('|,|').join(', '), name: store.name, nazione: nazioneOfShopId(store.id, shopNazione) }, storeTabSearch, storeTabNazione)) && (
              <button
                key={store.id}
                onClick={() => {
                  setSelectedStoreIndex(index);
                  // If switching to area view, disable manual edit mode
                  if (store.isArea && isManualEditMode) {
                    setIsManualEditMode(false);
                  }
                  // Reset active tab when switching stores
                  setActiveTab(0);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-b-2 mx-0.5 ${
                  selectedStoreIndex === index
                    ? "border-[#9F6460] text-[#9F6460] bg-[#9F6460]/10"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {store.isArea ? (
                  <span className="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                    </svg>
                    {store.name}
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                    {store.name}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        )}
        
        {/* Top section - Resizable */}
        <div 
          ref={topContainerRef} 
          className="flex flex-row items-center overflow-x-auto gap-2 px-1" 
          style={{
            boxSizing: 'border-box',
            height: isTableFullscreen ? '0%' : `${topSectionHeight}%`,
            minHeight: isTableFullscreen ? '0' : '20%',
            maxHeight: isTableFullscreen ? '0' : '80%',
            display: isTableFullscreen ? 'none' : undefined,
          }}
        >
        {/* Timeline Widget */}
        <div ref={timelineContainerRef} className="flex flex-col flex-shrink-0" style={{ height: '100%', maxHeight: '100%' }}>
          <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide text-center mt-3 mb-1">Timeline</span>
          <div className="flex-1 overflow-hidden">
            <Timeline
              nodes={timelineNodes}
              maxHeight={topContainerHeight > 0 ? Math.min(topContainerHeight, 500) : 300}
              selectedNodeId={currentSelectedRunId}
              onNodeClick={handleTimelineNodeClick}
              onNodeUpdate={handleTimelineNodeUpdate}
            />
          </div>
        </div>

        {/* Treemap/Selection Widget */}
        <div className="flex flex-row flex-shrink-0" style={{ height: '100%', maxHeight: '100%' }}>
        <div className="flex flex-col" style={{ height: '100%', maxHeight: '100%' }}>
          <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide text-center mt-3 mb-1">Selezione prodotti</span>
          <div className="flex-1 flex items-center justify-center overflow-hidden">
          {treemapLoading ? (
            <p className="text-gray-500">Caricamento treemap...</p>
          ) : tsneLoading ? (
            <div 
              className="flex flex-col items-center justify-center gap-4 transition-colors duration-500"
              style={{ 
                width: `${treemapWidth}px`,
                height: `${treemapHeight}px`,
                backgroundColor: selectedBlockColor || '#f9fafb'
              }}
            >
              <div className="relative">
                <div 
                  className="w-16 h-16 border-4 rounded-full animate-spin-in"
                  style={{
                    borderColor: `${getContrastColor(selectedBlockColor)}30`,
                    borderTopColor: getContrastColor(selectedBlockColor)
                  }}
                ></div>
              </div>
              <p 
                className="font-semibold text-lg"
                style={{ color: getContrastColor(selectedBlockColor) }}
              >
                Caricamento {selectedBlockLabel.toUpperCase()}...
              </p>
            </div>
          ) : showTsneView && tsneData ? (
            <TsneInlineView
              entries={tsneData.entries || []}
              title={`Prodotti Scelti ${selectedBlockLabel.toUpperCase()}`}
              onBack={() => {
                setShowTsneView(false);
                setTsneData(null);
              }}
              onProductClick={handleTsneProductClick}
              width={treemapWidth}
              height={treemapHeight}
              backgroundColor={selectedBlockColor}
            />
          ) : treemapBlocks.length > 0 ? (
            <Treemap 
              data={treemapBlocks} 
              width={treemapWidth}
              height={treemapHeight}
              onBlockClick={handleTreemapBlockClick}
            />
          ) : (
            <p className="text-gray-500">
              Nessun dato treemap disponibile
            </p>
          )}
          </div>
        </div>
        {/* Maniglia per la larghezza del treemap (doppio clic: torna alla larghezza predefinita) */}
        <div
          className="w-1 self-center h-1/2 bg-gray-300 hover:bg-blue-500 cursor-ew-resize transition-colors flex items-center justify-center ml-1"
          onMouseDown={(e) => {
            e.preventDefault();
            setTreemapResizeStart({ x: e.clientX, width: treemapWidth });
          }}
          onDoubleClick={() => {
            setTreemapUserWidth(null);
            try {
              localStorage.removeItem(TREEMAP_WIDTH_STORAGE_KEY);
            } catch {
              // storage non disponibile
            }
          }}
          title="Trascina per cambiare la larghezza (doppio clic per ripristinare)"
          style={{ userSelect: 'none', position: 'relative' }}
        >
          <div className="absolute h-12 w-1 bg-gray-400 rounded-full"></div>
        </div>
        </div>

        {/* Constraint Swatches Widget - SPJ-1716: occupa tutto lo spazio che resta, cosi'
            restringendo il treemap i vincoli si allargano invece di lasciare vuoto a destra. */}
        <div ref={vincoliContainerRef} className="flex flex-col flex-1" style={{ height: '100%', maxHeight: '100%', overflow: 'visible', minWidth: VINCOLI_MIN_WIDTH }}>
          <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide text-center mt-3 mb-1">Vincoli</span>
          <div className="flex-1 flex items-center justify-center" style={{ overflow: 'visible' }}>
          {(() => {
            if (generalMetricsLoading) {
              return <p className="text-gray-500">Caricamento vincoli...</p>;
            }
            
            // AREA VIEW: Display constraints from generalMetrics.area.mp_constraint
            if (isAreaView) {
              const areaArray: MetricsArea[] = generalMetrics?.area
                ? Array.isArray(generalMetrics.area)
                  ? generalMetrics.area
                  : [generalMetrics.area]
                : [];
              
              const areaWithConstraints = areaArray.find((a: MetricsArea) => a.mp_constraint && Object.keys(a.mp_constraint).length > 0);
              
              if (areaWithConstraints?.mp_constraint) {
                // Area mp_constraint format: { capacity: {TIPO: BudgetConstraint, ...}, quantity: {...}, ... }
                // This matches the MpConstraints interface expected by ConstraintSwatches
                const mpConstraintData = areaWithConstraints.mp_constraint;
                
                if (mpConstraintData) {
                  return (
                    <ConstraintSwatches
                      budgetConstraints={{}}
                      mpConstraints={mpConstraintData as { capacity: Record<string, BudgetConstraint>; quantity: Record<string, BudgetConstraint>; models_unique: Record<string, BudgetConstraint>; sku_count?: Record<string, BudgetConstraint>; avg_price: Record<string, BudgetConstraint> }}
                      width={vincoliWidth}
                      height={topContainerHeight > 0 ? Math.min(topContainerHeight, 500) : 300}
                      onTipoFilatoClick={handleTipoFilatoClick}
                      onExplainConstraint={handleExplainConstraint}
                      explanations={constraintExplanations}
                    />
                  );
                }
              }
              
              return <p className="text-gray-500">Nessun dato vincoli area disponibile</p>;
            }
            
            // SHOP VIEW: Display constraints from generalMetrics.lines[currentSelectedShop]
            if (currentSelectedShop && generalMetrics?.lines?.[currentSelectedShop]) {
              const shopMetrics = generalMetrics.lines[currentSelectedShop];
              const shopBudget = shopMetrics.budget;
              const shopMpConstraint = shopMetrics.mp_constraint;
              
              if (shopBudget || shopMpConstraint) {
                // Transform mp_constraint if needed (same format as area)
                let transformedMpConstraints: Record<string, { activity: number; capacity: number; slack: number; percentage_remaining: number }> = {};
                
                if (shopMpConstraint) {
                  const mpConstraintData = shopMpConstraint as unknown as Record<string, Record<string, number>>;
                  const allCategories = new Set<string>();
                  Object.values(mpConstraintData).forEach(categoryMap => {
                    if (categoryMap && typeof categoryMap === 'object') {
                      Object.keys(categoryMap).forEach(cat => allCategories.add(cat));
                    }
                  });
                  
                  allCategories.forEach(category => {
                    const capacity = mpConstraintData['capacity']?.[category] ?? 0;
                    const activity = mpConstraintData['activity']?.[category] ?? mpConstraintData['avg_price']?.[category] ?? 0;
                    const slack = mpConstraintData['slack']?.[category] ?? 0;
                    const percentageRemaining = capacity > 0 
                      ? ((capacity - activity) / capacity) * 100 
                      : 0;
                    
                    transformedMpConstraints[category] = {
                      activity,
                      capacity,
                      slack,
                      percentage_remaining: percentageRemaining,
                    };
                  });
                }
                
                // Transform budget if needed
                let transformedBudget: Record<string, BudgetConstraint | BudgetConstraintWithItems> = {};
                
                if (shopBudget) {
                  const budgetData = shopBudget as unknown as Record<string, Record<string, number>>;
                  Object.entries(budgetData).forEach(([consegnaName, consegnaData]) => {
                    if (consegnaData && typeof consegnaData === 'object') {
                      const rawConsegnaData = consegnaData as unknown as Record<string, any>;
                      const valueData = rawConsegnaData.value && typeof rawConsegnaData.value === 'object'
                        ? rawConsegnaData.value
                        : rawConsegnaData;

                      const valueCapacity = Number(valueData['capacity'] ?? 0);
                      const valueActivity = Number(valueData['activity'] ?? 0);
                      const valueSlack = Number(valueData['slack'] ?? (valueCapacity - valueActivity));
                      const valuePercentageRemaining =
                        valueData['percentage_remaining'] !== undefined
                          ? Number(valueData['percentage_remaining'])
                          : (valueCapacity > 0 ? ((valueCapacity - valueActivity) / valueCapacity) * 100 : 0);

                      const valueConstraint: BudgetConstraint = {
                        activity: valueActivity,
                        capacity: valueCapacity,
                        slack: valueSlack,
                        percentage_remaining: valuePercentageRemaining,
                      };

                      const itemsData = rawConsegnaData.items && typeof rawConsegnaData.items === 'object'
                        ? rawConsegnaData.items
                        : null;

                      if (itemsData) {
                        const itemsCapacity = Number(itemsData['capacity'] ?? 0);
                        const itemsActivity = Number(itemsData['activity'] ?? 0);
                        const itemsSlack = Number(itemsData['slack'] ?? (itemsCapacity - itemsActivity));
                        const itemsPercentageRemaining =
                          itemsData['percentage_remaining'] !== undefined
                            ? Number(itemsData['percentage_remaining'])
                            : (itemsCapacity > 0 ? ((itemsCapacity - itemsActivity) / itemsCapacity) * 100 : 0);

                        transformedBudget[consegnaName] = {
                          value: valueConstraint,
                          items: {
                            activity: itemsActivity,
                            capacity: itemsCapacity,
                            slack: itemsSlack,
                            percentage_remaining: itemsPercentageRemaining,
                          },
                        };
                      } else {
                        transformedBudget[consegnaName] = valueConstraint;
                      }
                    }
                  });
                }
                
                return (
                  <ConstraintSwatches
                    budgetConstraints={transformedBudget}
                    mpConstraints={shopMpConstraint}
                    width={vincoliWidth}
                    height={topContainerHeight > 0 ? Math.min(topContainerHeight, 500) : 300}
                    onTipoFilatoClick={handleTipoFilatoClick}
                    onExplainConstraint={handleExplainConstraint}
                    explanations={constraintExplanations}
                  />
                );
              }
              
              return <p className="text-gray-500">Nessun dato vincoli negozio disponibile</p>;
            }
            
            // Fallback: use top-level budget and mp_constraint (legacy)
            if (generalMetrics && generalMetrics.budget && generalMetrics.mp_constraint) {
              return (
                <ConstraintSwatches
                  budgetConstraints={generalMetrics.budget}
                  mpConstraints={generalMetrics.mp_constraint}
                  width={vincoliWidth}
                  height={topContainerHeight > 0 ? Math.min(topContainerHeight, 500) : 300}
                  onTipoFilatoClick={handleTipoFilatoClick}
                  onExplainConstraint={handleExplainConstraint}
                  explanations={constraintExplanations}
                />
              );
            }
            
            return <p className="text-gray-500">Nessun dato vincoli disponibile</p>;
          })()}
          </div>
        </div>

        {/* Area optimization action - top right, next to Vincoli */}
        {isAreaView && (
          <div className="flex flex-col flex-shrink-0" style={{ height: '100%', maxHeight: '100%' }}>
            <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide text-center mt-3 mb-1">Negozi</span>
            <div className="flex-1 overflow-auto" style={{ maxWidth: '520px' }}>
              <div className="flex flex-col gap-2 items-end pr-1">
                {(() => {
                  const areaAllConsegna = getAreaConsegnas(data).find(c => c.name.toLowerCase() === 'all');
                  const badgeEntries = [
                    { id: 'area', name: 'Area', isArea: true, allConsegna: areaAllConsegna },
                    ...storesList
                      .filter(s => !s.isArea)
                      .map((store) => {
                        const shopData = data?.metrics_per_consegna_per_shop?.[store.id];
                        const allConsegna = shopData?.find(c => c.name.toLowerCase() === 'all');
                        return { id: store.id, name: store.name, isArea: false, allConsegna };
                      })
                  ];

                  return badgeEntries.map((entry) => (
                    <button
                      key={`top-badge-${entry.id}`}
                      type="button"
                      className="bg-white border border-gray-400 p-2 hover:border-black transition-all text-left"
                      style={{ width: '150px', minHeight: '96px' }}
                      onClick={() => {
                        if (entry.isArea) {
                          setSelectedStoreIndex(0);
                        } else {
                          const storeIndex = storesList.findIndex(s => s.id === entry.id);
                          setSelectedStoreIndex(storeIndex);
                        }
                        setActiveTab(0);
                      }}
                    >
                      <div className="flex items-center gap-1.5 mb-2 border-b border-gray-300 pb-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                          {entry.isArea ? (
                            <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                          )}
                        </svg>
                        <h4 className="font-bold text-gray-900 text-xs truncate">{entry.name}</h4>
                      </div>
                      <div className="space-y-0.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Profitto:</span>
                          <span className="font-medium" style={{ color: '#9F6460' }}>
                            {entry.allConsegna?.all?.profit !== undefined
                              ? `€${entry.allConsegna.all.profit.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`
                              : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SKU:</span>
                          <span className="font-medium" style={{ color: '#9F6460' }}>
                            {entry.allConsegna?.all?.unique_skus ?? '-'}
                          </span>
                        </div>
                      </div>
                    </button>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Draggable divider */}
      <div
        className="h-1 bg-gray-300 hover:bg-blue-500 cursor-ns-resize transition-colors flex items-center justify-center"
        onMouseDown={() => setIsDragging(true)}
        style={{
          userSelect: 'none',
          position: 'relative',
          display: isTableFullscreen ? 'none' : undefined,
        }}
      >
        <div className="absolute w-12 h-1 bg-gray-400 rounded-full"></div>
      </div>

      {/* Bottom section - Resizable */}
      <div
        className="flex flex-col min-h-0 overflow-hidden"
        style={{
          height: isTableFullscreen ? '100%' : `${100 - topSectionHeight}%`,
          minHeight: isTableFullscreen ? '100%' : '20%',
          maxHeight: isTableFullscreen ? '100%' : '80%'
        }}
      >
        {/* Area View Summary - shown when viewing Area in multi-shop optimization */}
        {isAreaView ? (
          <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
              {/* Area listed results - same structure as shop pages, read-only */}
              {(() => {
                const areaConsegnasRaw = getAreaConsegnas(data);
                if (areaConsegnasRaw.length === 0) return null;

                const areaConsegnas = [
                  ...areaConsegnasRaw.filter((c) => c.name.toLowerCase() === 'all'),
                  ...areaConsegnasRaw.filter((c) => c.name.toLowerCase() !== 'all'),
                ];
                const safeAreaTabIndex = Math.min(activeTab, Math.max(0, areaConsegnas.length - 1));
                const areaCurrentConsegnaData = areaConsegnas[safeAreaTabIndex];
                const areaOriginalConsegnaIndex = areaCurrentConsegnaData
                  ? areaConsegnasRaw.findIndex((c) => c.name === areaCurrentConsegnaData.name)
                  : -1;

                return (
                  <div className="flex min-h-0 flex-1 flex-col bg-white">
                        <div className="flex border-b border-gray-300 bg-white justify-between w-full overflow-x-auto">
                          <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="flex">
                            {/* SPJ-889: toggle tabella a tutto schermo (solo icona), a sinistra delle consegne */}
                            <button
                              type="button"
                              onClick={() => setIsTableFullscreen((v) => !v)}
                              title={isTableFullscreen ? 'Mostra il pannello superiore' : 'Tabella a tutto schermo'}
                              className="px-3 py-3 text-lg leading-none text-gray-500 transition-colors hover:text-gray-900"
                            >
                              {isTableFullscreen ? '↕' : '⛶'}
                            </button>
                            {areaConsegnas.map((consegna, index) => (
                              <button
                                key={`area-tab-${consegna.name}-${index}`}
                                onClick={() => setActiveTab(index)}
                                className={`px-6 py-3 font-medium transition-colors ${
                                  safeAreaTabIndex === index
                                    ? 'border-b-2 border-black text-black'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                {consegna.name.replace(/_/g, ' ').toUpperCase()}
                              </button>
                            ))}
                          </div>
                          {/* Search Bar */}
                          <div className="flex items-center gap-2 px-4 border-l border-gray-300 min-w-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              placeholder="Cerca SKU..."
                              value={searchQuery}
                              onChange={(e) => handleSearch(e.target.value)}
                              className="px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-36 min-w-[4rem]"
                            />
                            <input
                              type="text"
                              placeholder="Modello"
                              value={modelFilter}
                              onChange={(e) => setModelFilter(e.target.value)}
                              title="Filtra per modello (contenuto nel codice SKU). Il tessuto è una substring del modello, quindi qui filtri anche per tessuto."
                              className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-24 min-w-[3.5rem]"
                            />
                            <input
                              type="text"
                              placeholder="Colore"
                              value={colorFilter}
                              onChange={(e) => setColorFilter(e.target.value)}
                              title="Filtra per colore (contenuto nel codice SKU)"
                              className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-20 min-w-[3.5rem]"
                            />
                            <input
                              type="text"
                              placeholder="Tipo filato"
                              value={tipoFilatoFilter}
                              onChange={(e) => setTipoFilatoFilter(e.target.value)}
                              title="Filtra per tipo filato (gruppo: CASHMERE, T-SHIRT, …)"
                              className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-36 min-w-[3.5rem]"
                            />
                            {searchResults.length > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                  {currentSearchIndex + 1} / {searchResults.length}
                                </span>
                                <button
                                  onClick={handlePreviousResult}
                                  className="p-1 hover:bg-gray-200 rounded"
                                  title="Risultato precedente"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={handleNextResult}
                                  className="p-1 hover:bg-gray-200 rounded"
                                  title="Risultato successivo"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                          </div>
                          <div className="flex items-center px-4 flex-shrink-0">
                            <button
                              onClick={handleOptimizationClick}
                              title="Nuova ottimizzazione"
                              className="px-2 xl:px-3 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5"
                              style={{ backgroundColor: '#D4DEE8' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                              </svg>
                              <span className="hidden xl:inline">NUOVA OTTIMIZZAZIONE</span>
                              <input
                                type="checkbox"
                                checked={isOptimizationMode}
                                readOnly
                                className="hidden xl:inline-block w-4 h-4 text-black border-gray-300 focus:ring-black cursor-pointer"
                              />
                            </button>
                          </div>
                        </div>

                        <div className="relative min-h-0 flex-1 overflow-auto bg-white">
                          <table className="w-full border-collapse" style={{ tableLayout: 'auto' }}>
                            <thead className="sticky top-0 bg-gray-100 border-b-2 border-gray-300 z-20">
                              <tr>
                                <th className="font-semibold text-gray-700 text-left px-4 py-3 whitespace-nowrap sticky left-0 bg-gray-100 z-30 min-w-[350px]" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>
                  <div className="flex flex-col gap-1">
                    <span>TIPO FILATO</span>
                    {/* <div className="flex flex-wrap gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      {renderSortableHeader('Ordini passati', 'order_quantity')}
                      {renderSortableHeader('Run collegata', 'linked_order_quantity')}
                    </div> */}
                  </div>
                </th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PROFITTABILITÀ', 'profit')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PREZZO MEDIO', 'mean_price')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PREZZO MEDIO WHS', 'mean_weight')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('SELL-THROUGH', 'sellout')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('MODELLI UNICI', 'models_unique')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PRODOTTI UNICI', 'unique_skus')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PRODOTTI TOTALI', 'total_quantity', 'Ordinamento iniziale: quantità ottimizzata decrescente per questo tab')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('FORECAST QUANTITY', 'forecast_quantity')}</th>
                                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">APERTURA TAGLIE</th>
                              </tr>
                            </thead>
                            <tbody ref={tableBodyRef}>
                              {areaCurrentConsegnaData && (
                                <>
                                  {renderMetricsRow(
                                    'TOTALE',
                                    areaCurrentConsegnaData.all.profit,
                                    areaCurrentConsegnaData.all.mean_price,
                                    areaCurrentConsegnaData.all.unique_skus,
                                    areaCurrentConsegnaData.all.total_quantity,
                                    0,
                                    areaCurrentConsegnaData.all.mean_weight,
                                    areaCurrentConsegnaData.all.total_sellout_value_mean,
                                    areaCurrentConsegnaData.all.models_unique,
                                    areaCurrentConsegnaData.all.forecast_quantity,
                                    areaCurrentConsegnaData.all.taglie_distribution,
                                    `area_totale_${safeAreaTabIndex}`,
                                    areaCurrentConsegnaData.tf.length > 0,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    areaCurrentConsegnaData.all.url_img_list,
                                    areaCurrentConsegnaData.all.sketch_url,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    true
                                  )}

                                  {expanded[`area_totale_${safeAreaTabIndex}`] &&
                                    areaCurrentConsegnaData.tf.map((tfData, tfIndex) => {
                                      const tfExpandKey = `area_tf_${safeAreaTabIndex}_${tfIndex}`;
                                      if (!tfMatchesFilter(tfData)) return null;
                                      return (
                                        <Fragment key={`area-tf-${tfData.name}-${tfIndex}`}>
                                          {renderMetricsRow(
                                            tfData.name,
                                            tfData.all.profit,
                                            tfData.all.mean_price,
                                            tfData.all.unique_skus,
                                            tfData.all.total_quantity,
                                            1,
                                            tfData.all.mean_weight,
                                            tfData.all.total_sellout_value_mean,
                                            tfData.all.models_unique,
                                            tfData.all.forecast_quantity,
                                            tfData.all.taglie_distribution,
                                            tfExpandKey,
                                            tfData.sku.length > 0,
                                            undefined,
                                            undefined,
                                            undefined,
                                            undefined,
                                            tfData.all.url_img_list,
                                            tfData.all.sketch_url,
                                            undefined,
                                            undefined,
                                            undefined,
                                            undefined,
                                            undefined,
                                            true,
                                            tfData.taglie_reference_distributions
                                          )}

                                          {expanded[tfExpandKey] &&
                                            sortSkusForCurrentView(tfData.sku).filter((skuData) => skuMatchesFilter(skuData.name, tfData.name)).map((skuData, skuIndex) => (
                                              <Fragment key={`area-sku-${skuData.name}-${skuIndex}`}>
                                                {renderMetricsRow(
                                                  skuData.name,
                                                  skuData.metrics.profit,
                                                  skuData.metrics.mean_price,
                                                  skuData.metrics.unique_skus,
                                                  skuData.metrics.total_quantity,
                                                  2,
                                                  skuData.metrics.mean_weight,
                                                  skuData.metrics.total_sellout_value_mean,
                                                  skuData.metrics.models_unique,
                                                  skuData.metrics.forecast_quantity,
                                                  skuData.metrics.taglie_distribution,
                                                  undefined,
                                                  false,
                                                  areaOriginalConsegnaIndex,
                                                  tfIndex,
                                                  skuIndex,
                                                  skuData.name,
                                                  skuData.metrics.url_img_list,
                                                  skuData.metrics.sketch_url,
                                                  skuData.metrics.weborder_tags,
                                                  skuData.metrics.order_quantity,
                                                  skuData.metrics.taglie_order,
                                                  skuData.metrics.order_quantity_venduta,
                                                  skuData.metrics.taglie_order_venduta,
                                                  true,
                                                  undefined,
                                                  skuData.metrics.linked_order_quantity,
                                                  skuData.metrics.taglie_linked_order,
                                                  skuData.metrics.descrizione_colore
                                                )}
                                              </Fragment>
                                            ))}
                                        </Fragment>
                                      );
                                    })}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                  </div>
                );
              })()}
            
            {/* Config card for Area view - renders when optimization mode is active */}
            {(isOptimizationMode || isConfigViewMode) && (
                            <div 
                            className="fixed border border-gray-400 shadow-2xl bg-white"
                            style={{ 
                              zIndex: 999999, 
                              left: `${cardPosition.x}px`,
                              top: `${cardPosition.y}px`,
                              width: `${cardWidth}px`,
                              height: `${cardHeight}px`,
                              borderRadius: '8px',
                              overflow: 'hidden'
                            }}
                          >
                            {/* Title bar for dragging */}
                            <div 
                              className="flex items-center justify-between px-3 py-2 border-b border-gray-300 cursor-move select-none"
                              style={{ backgroundColor: '#E8EDF2' }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setIsDraggingCard(true);
                                setDragStart({
                                  x: e.clientX,
                                  y: e.clientY,
                                  cardX: cardPosition.x,
                                  cardY: cardPosition.y
                                });
                              }}
                            >
                              <h3 className="text-sm font-semibold text-gray-900">
                                {isConfigViewMode ? 'CONFIGURAZIONE CORRENTE' : 'NUOVI VINCOLI'}
                              </h3>
                              <div className="flex space-x-1">
                                {/* <button 
                                  className="w-3 h-3 bg-yellow-400 rounded-full hover:bg-yellow-500"
                                  title="Minimize"
                                />
                                <button 
                                  className="w-3 h-3 bg-green-400 rounded-full hover:bg-green-500"
                                  title="Maximize"
                                /> */}
                                <button 
                                // "text-gray-400 hover:text-gray-600 text-2xl leading-none"
                                  className="text-gray-400 text-2xl leading-none hover:text-gray-400"
                                  title="Close"
                                  onClick={() => {
                                    if (isConfigViewMode) {
                                      setIsConfigViewMode(false);
                                    } else {
                                      setIsOptimizationMode(false);
                                    }
                                  }}
                                >
                                ×
                              </button>
                              </div>
                            </div>
            
                            {/* Scrollable content area */}
                            <div className="h-full overflow-auto p-3" style={{ backgroundColor: '#E8EDF2', height: 'calc(100% - 40px)' }}>
                            
                            {/* Loading indicator while fetching constraints */}
                            {isLoadingConstraints && (
                              <div className="flex flex-col items-center justify-center h-full">
                                <div className="relative">
                                  <svg className="animate-spin-in h-16 w-16 text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                                <p className="mt-4 text-sm font-medium text-gray-900">Caricamento vincoli...</p>
                                <p className="mt-1 text-xs text-gray-600">Attendere prego</p>
                              </div>
                            )}
                            
                            {/* Riquadro CONFIGURAZIONI con parametri aggiuntivi */}
                            {!isLoadingConstraints && constraints && (() => {
                              const objective = constraints.objective || {};
                              const preparation = constraints.preparation || {};
                              const solver = constraints.solver || {};
                              
                              const hasConfigs = objective.lambda_plus !== undefined || 
                                                objective.lambda_minus !== undefined || 
                                                objective.rho_profit !== undefined ||
                                                objective.lambda_avg_price !== undefined ||
                                                objective.lambda_price_slack !== undefined ||
                                                // objective.lambda_tf_mix !== undefined ||
                                                preparation.expected_sellout !== undefined ||
                                                preparation.force_first_three_taglie !== undefined ||
                                                solver.type !== undefined ||
                                                solver.time_limit !== undefined;
                              
                              return hasConfigs ? (
                                <div className="mb-2 relative">
                                  <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>CONFIGURAZIONI</h4>
                                  <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                                    
                                    <div className="space-y-1">
                                      {/* Objective Section */}
                                      {(objective.lambda_plus !== undefined || objective.lambda_minus !== undefined || objective.rho_profit !== undefined || objective.lambda_avg_price !== undefined || objective.lambda_price_slack !== undefined || objective.lambda_tf_mix !== undefined) && (
                                        <div>
                                          <div className="text-[9px] font-bold text-gray-700 uppercase mb-0.5">Objective</div>
                                          <div className="grid grid-cols-3 gap-1.5">
                                            {objective.lambda_plus !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                                <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ+</div>
                                                <CommitInput
                                                  type="number"
                                                  step="0.1"
                                                  value={objective.lambda_plus}
                                                  disabled={isConfigViewMode}
                                                  onCommit={async (e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    if (isNaN(newValue)) return;
                                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                    if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                    updatedConstraints.objective.lambda_plus = newValue;
                                                    setConstraints(updatedConstraints);
                                                    
                                                    try {
                                                      const threadId = thread.threadId;
                                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                      if (threadId && messageId) {
                                                        await performAction({
                                                          threadId,
                                                          messageId,
                                                          actionName: "optim-update-config",
                                                          params: { "optim-config-data": updatedConstraints },
                                                        });
                                                      }
                                                    } catch (err) {
                                                      console.error("Error updating lambda_plus:", err);
                                                    }
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            )}
                                            {objective.lambda_minus !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                                <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ−</div>
                                                <CommitInput
                                                  type="number"
                                                  step="0.1"
                                                  value={objective.lambda_minus}
                                                  disabled={isConfigViewMode}
                                                  onCommit={async (e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    if (isNaN(newValue)) return;
                                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                    if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                    updatedConstraints.objective.lambda_minus = newValue;
                                                    setConstraints(updatedConstraints);
                                                    
                                                    try {
                                                      const threadId = thread.threadId;
                                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                      if (threadId && messageId) {
                                                        await performAction({
                                                          threadId,
                                                          messageId,
                                                          actionName: "optim-update-config",
                                                          params: { "optim-config-data": updatedConstraints },
                                                        });
                                                      }
                                                    } catch (err) {
                                                      console.error("Error updating lambda_minus:", err);
                                                    }
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            )}
                                            {objective.rho_profit !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                                <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">ρ Profit</div>
                                                <CommitInput
                                                  type="number"
                                                  step="0.1"
                                                  value={objective.rho_profit}
                                                  disabled={isConfigViewMode}
                                                  onCommit={async (e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    if (isNaN(newValue)) return;
                                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                    if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                    updatedConstraints.objective.rho_profit = newValue;
                                                    setConstraints(updatedConstraints);
                                                    
                                                    try {
                                                      const threadId = thread.threadId;
                                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                      if (threadId && messageId) {
                                                        await performAction({
                                                          threadId,
                                                          messageId,
                                                          actionName: "optim-update-config",
                                                          params: { "optim-config-data": updatedConstraints },
                                                        });
                                                      }
                                                    } catch (err) {
                                                      console.error("Error updating rho_profit:", err);
                                                    }
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            )}
                                            {objective.lambda_avg_price !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                              <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ Avg Price</div>
                                              <CommitInput
                                                type="number"
                                                step="0.1"
                                                value={objective.lambda_avg_price}
                                                disabled={isConfigViewMode}
                                                onCommit={async (e) => {
                                                  const newValue = parseFloat(e.target.value);
                                                  if (isNaN(newValue)) return;
                                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                  if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                  updatedConstraints.objective.lambda_avg_price = newValue;
                                                  setConstraints(updatedConstraints);
                                                  
                                                  try {
                                                    const threadId = thread.threadId;
                                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                    if (threadId && messageId) {
                                                      await performAction({
                                                        threadId,
                                                        messageId,
                                                        actionName: "optim-update-config",
                                                        params: { "optim-config-data": updatedConstraints },
                                                      });
                                                    }
                                                  } catch (err) {
                                                    console.error("Error updating lambda_avg_price:", err);
                                                  }
                                                }}
                                                className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                              />
                                            </div>
                                            )}
                                            {objective.lambda_price_slack !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                                <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ Price Slack</div>
                                                <CommitInput
                                                  type="number"
                                                  step="0.1"
                                                  value={objective.lambda_price_slack}
                                                  disabled={isConfigViewMode}
                                                  onCommit={async (e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    if (isNaN(newValue)) return;
                                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                    if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                    updatedConstraints.objective.lambda_price_slack = newValue;
                                                    setConstraints(updatedConstraints);

                                                    try {
                                                      const threadId = thread.threadId;
                                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                      if (threadId && messageId) {
                                                        await performAction({
                                                          threadId,
                                                          messageId,
                                                          actionName: "optim-update-config",
                                                          params: { "optim-config-data": updatedConstraints },
                                                        });
                                                      }
                                                    } catch (err) {
                                                      console.error("Error updating lambda_price_slack:", err);
                                                    }
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            )}
                                            {objective.lambda_tf_mix !== undefined && (
                                              <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                                <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ TF Mix</div>
                                                <CommitInput
                                                  type="number"
                                                  step="0.1"
                                                  value={objective.lambda_tf_mix}
                                                  disabled={isConfigViewMode}
                                                  onCommit={async (e) => {
                                                    const newValue = parseFloat(e.target.value);
                                                    if (isNaN(newValue)) return;
                                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                    if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                                    updatedConstraints.objective.lambda_tf_mix = newValue;
                                                    setConstraints(updatedConstraints);

                                                    try {
                                                      const threadId = thread.threadId;
                                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                      if (threadId && messageId) {
                                                        await performAction({
                                                          threadId,
                                                          messageId,
                                                          actionName: "optim-update-config",
                                                          params: { "optim-config-data": updatedConstraints },
                                                        });
                                                      }
                                                    } catch (err) {
                                                      console.error("Error updating lambda_tf_mix:", err);
                                                    }
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Preparation and Solver Section - On same row */}
                                      {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined || solver.type !== undefined || solver.time_limit !== undefined) && (
                                        <div className="grid grid-cols-2 gap-2">
                                          {/* Preparation Section */}
                                          {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined) && (
                                            <div>
                                              <div className="text-[8px] font-bold text-gray-700 uppercase mb-0.5">Preparation</div>
                                              <div className="border border-gray-300 rounded px-1.5 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                                {preparation.expected_sellout !== undefined && (
                                                  <>
                                                    {/* Global Expected Sellout */}
                                                    <div className="flex items-center justify-between mb-0.5">
                                                      <div className="text-[8px] font-semibold text-gray-600 uppercase">Expected Sellout (Globale)</div>
                                                      <div className="text-[10px] font-bold text-gray-900">
                                                        {preparation.expected_sellout.toFixed(2)}
                                                      </div>
                                                    </div>
                                                    <input
                                                      type="range"
                                                      min="0"
                                                      max="1"
                                                      step="0.01"
                                                      value={preparation.expected_sellout}
                                                      disabled={isConfigViewMode}
                                                      onChange={async (e) => {
                                                        const newValue = parseFloat(e.target.value);
                                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                        if (!updatedConstraints.preparation) {
                                                          updatedConstraints.preparation = {};
                                                        }
                                                        updatedConstraints.preparation.expected_sellout = newValue;
                                                        setConstraints(updatedConstraints);
                                                        
                                                        try {
                                                          const threadId = thread.threadId;
                                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                          if (threadId && messageId) {
                                                            await performAction({
                                                              threadId,
                                                              messageId,
                                                              actionName: "optim-update-config",
                                                              params: {
                                                                "optim-config-data": updatedConstraints,
                                                              },
                                                            });
                                                          }
                                                        } catch (err) {
                                                          console.error("Error updating expected_sellout:", err);
                                                        }
                                                      }}
                                                      className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                                      style={{
                                                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${preparation.expected_sellout * 100}%, #d1d5db ${preparation.expected_sellout * 100}%, #d1d5db 100%)`
                                                      }}
                                                    />
                                                  </>
                                                )}
                                                
                                                {/* Per-Tipo-Filato Overrides */}
                                                {tipoFilatoOverrides.map((override) => (
                                                  <div key={override.id} className="mt-2 pt-2 border-t border-gray-200">
                                                    <div className="flex items-center justify-between mb-1 gap-1">
                                                      <select
                                                        value={override.tipo_filato}
                                                        disabled={isConfigViewMode}
                                                        onChange={(e) => {
                                                          const newOverrides = tipoFilatoOverrides.map(o =>
                                                            o.id === override.id ? { ...o, tipo_filato: e.target.value } : o
                                                          );
                                                          setTipoFilatoOverrides(newOverrides);
                                                          updateConstraintsWithOverrides(newOverrides);
                                                        }}
                                                        className="text-[8px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                                      >
                                                        <option value="">(seleziona tipo filato)</option>
                                                        {tipoFilatoEnum.map(tipo => (
                                                          <option key={tipo} value={tipo}>{tipo}</option>
                                                        ))}
                                                      </select>
                                                      <button
                                                        type="button"
                                                        disabled={isConfigViewMode}
                                                        onClick={() => {
                                                          const newOverrides = tipoFilatoOverrides.filter(o => o.id !== override.id);
                                                          setTipoFilatoOverrides(newOverrides);
                                                          updateConstraintsWithOverrides(newOverrides);
                                                        }}
                                                        className="text-[8px] px-1.5 py-0.5 text-red-600 hover:text-red-800 disabled:opacity-60 border border-red-300 rounded"
                                                      >
                                                        ✕
                                                      </button>
                                                    </div>
                                                    <div className="flex items-center justify-between mb-0.5">
                                                      <div className="text-[8px] font-semibold text-gray-600">Sellout</div>
                                                      <div className="text-[10px] font-bold text-gray-900">
                                                        {override.value.toFixed(2)}
                                                      </div>
                                                    </div>
                                                    <input
                                                      type="range"
                                                      min="0"
                                                      max="1"
                                                      step="0.01"
                                                      value={override.value}
                                                      disabled={isConfigViewMode}
                                                      onChange={(e) => {
                                                        const newValue = parseFloat(e.target.value);
                                                        const newOverrides = tipoFilatoOverrides.map(o =>
                                                          o.id === override.id ? { ...o, value: newValue } : o
                                                        );
                                                        setTipoFilatoOverrides(newOverrides);
                                                        updateConstraintsWithOverrides(newOverrides);
                                                      }}
                                                      className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60"
                                                      style={{
                                                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${override.value * 100}%, #d1d5db ${override.value * 100}%, #d1d5db 100%)`
                                                      }}
                                                    />
                                                  </div>
                                                ))}
                                                
                                                {/* Add Override Button */}
                                                <button
                                                  type="button"
                                                  disabled={isConfigViewMode}
                                                  onClick={() => {
                                                    const nextId = tipoFilatoOverrides.length > 0 
                                                      ? Math.max(...tipoFilatoOverrides.map(o => o.id)) + 1 
                                                      : 1;
                                                    setTipoFilatoOverrides([
                                                      ...tipoFilatoOverrides,
                                                      { id: nextId, tipo_filato: '', value: 0.5 }
                                                    ]);
                                                  }}
                                                  className="mt-2 w-full text-[8px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                                >
                                                  + Specifica per Tipo Filato
                                                </button>
                                                
                                                {/* Scale Orders Toggle */}
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                  <label className={`flex items-center justify-between ${isConfigViewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                    <span className="text-[8px] font-semibold text-gray-600 uppercase">Scale Orders</span>
                                                    <div className="relative">
                                                      <input
                                                        type="checkbox"
                                                        checked={preparation.scale_orders || false}
                                                        disabled={isConfigViewMode}
                                                        onChange={async (e) => {
                                                          const newValue = e.target.checked;
                                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                          if (!updatedConstraints.preparation) {
                                                            updatedConstraints.preparation = {};
                                                          }
                                                          updatedConstraints.preparation.scale_orders = newValue;
                                                          setConstraints(updatedConstraints);
                                                          
                                                          try {
                                                            const threadId = thread.threadId;
                                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                            if (threadId && messageId) {
                                                              await performAction({
                                                                threadId,
                                                                messageId,
                                                                actionName: "optim-update-config",
                                                                params: { "optim-config-data": updatedConstraints },
                                                              });
                                                            }
                                                          } catch (err) {
                                                            console.error("Error updating scale_orders:", err);
                                                          }
                                                        }}
                                                        className="sr-only peer"
                                                      />
                                                      <div className={`w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500 ${isConfigViewMode ? 'opacity-60' : ''}`}></div>
                                                    </div>
                                                  </label>
                                                </div>

                                                {preparation.force_first_three_taglie !== undefined && (
                                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <label className={`flex items-center justify-between ${isConfigViewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                      <span className="text-[8px] font-semibold text-gray-600 uppercase">Force Three Taglie</span>
                                                      <div className="relative">
                                                        <input
                                                          type="checkbox"
                                                          checked={preparation.force_first_three_taglie}
                                                          disabled={isConfigViewMode}
                                                          onChange={async (e) => {
                                                            const newValue = e.target.checked;
                                                            const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                            if (!updatedConstraints.preparation) {
                                                              updatedConstraints.preparation = {};
                                                            }
                                                            updatedConstraints.preparation.force_first_three_taglie = newValue;
                                                            setConstraints(updatedConstraints);
                                                            
                                                            try {
                                                              const threadId = thread.threadId;
                                                              const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                              if (threadId && messageId) {
                                                                await performAction({
                                                                  threadId,
                                                                  messageId,
                                                                  actionName: "optim-update-config",
                                                                  params: { "optim-config-data": updatedConstraints },
                                                                });
                                                              }
                                                            } catch (err) {
                                                              console.error("Error updating force_first_three_taglie:", err);
                                                            }
                                                          }}
                                                          className="sr-only peer"
                                                        />
                                                        <div className={`w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500 ${isConfigViewMode ? 'opacity-60' : ''}`}></div>
                                                      </div>
                                                    </label>
                                                  </div>
                                                )}

                                                {preparation.taglie_sampling_method !== undefined && (
                                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <div className="flex items-center justify-between gap-2">
                                                      <span className="text-[8px] font-semibold text-gray-600 uppercase">Taglie Sampling</span>
                                                      <select
                                                        value={preparation.taglie_sampling_method}
                                                        disabled={isConfigViewMode}
                                                        onChange={async (e) => {
                                                          const newValue = e.target.value;
                                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                          if (!updatedConstraints.preparation) {
                                                            updatedConstraints.preparation = {};
                                                          }
                                                          updatedConstraints.preparation.taglie_sampling_method = newValue;
                                                          setConstraints(updatedConstraints);

                                                          try {
                                                            const threadId = thread.threadId;
                                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                            if (threadId && messageId) {
                                                              await performAction({
                                                                threadId,
                                                                messageId,
                                                                actionName: "optim-update-config",
                                                                params: { "optim-config-data": updatedConstraints },
                                                              });
                                                            }
                                                          } catch (err) {
                                                            console.error("Error updating taglie_sampling_method:", err);
                                                          }
                                                        }}
                                                        className="text-[8px] px-1.5 py-0.5 border border-gray-300 rounded bg-white disabled:opacity-60"
                                                      >
                                                        <option value="legacy">legacy</option>
                                                        <option value="hare_niemeyer">hare_niemeyer</option>
                                                      </select>
                                                    </div>
                                                  </div>
                                                )}
                                                
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Solver Section */}
                                          {(solver.type !== undefined || solver.time_limit !== undefined) && (
                                            <div>
                                              <div className="text-[8px] font-bold text-gray-700 uppercase mb-0.5">Solver</div>
                                              <div className="flex gap-1.5">
                                                {solver.type !== undefined && (
                                                  <div className="border border-gray-300 rounded px-1.5 py-0.5 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">Type</div>
                                                    <select
                                                      value={solver.type}
                                                      disabled={isConfigViewMode}
                                                      onChange={async (e) => {
                                                        const newValue = e.target.value;
                                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                        if (!updatedConstraints.solver) {
                                                          updatedConstraints.solver = {};
                                                        }
                                                        updatedConstraints.solver.type = newValue;
                                                        setConstraints(updatedConstraints);
                                                        
                                                        try {
                                                          const threadId = thread.threadId;
                                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                          if (threadId && messageId) {
                                                            await performAction({
                                                              threadId,
                                                              messageId,
                                                              actionName: "optim-update-config",
                                                              params: {
                                                                "optim-config-data": updatedConstraints,
                                                              },
                                                            });
                                                          }
                                                        } catch (err) {
                                                          console.error("Error updating solver type:", err);
                                                        }
                                                      }}
                                                      className="w-full text-[10px] font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    >
                                                      <option value="CBC">CBC</option>
                                                      <option value="CPSAT">CPSAT</option>
                                                    </select>
                                                  </div>
                                                )}
                                                {solver.time_limit !== undefined && (
                                                  <div className="border border-gray-300 rounded px-1.5 py-0.5 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">Time Limit (s)</div>
                                                    <CommitInput
                                                      type="number"
                                                      step="1"
                                                      min="0"
                                                      value={solver.time_limit}
                                                      disabled={isConfigViewMode}
                                                      onCommit={async (e) => {
                                                        const newValue = parseInt(e.target.value);
                                                        if (isNaN(newValue) || newValue < 0) return;
                                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                        if (!updatedConstraints.solver) updatedConstraints.solver = {};
                                                        updatedConstraints.solver.time_limit = newValue;
                                                        setConstraints(updatedConstraints);
                                                        
                                                        try {
                                                          const threadId = thread.threadId;
                                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                          if (threadId && messageId) {
                                                            await performAction({
                                                              threadId,
                                                              messageId,
                                                              actionName: "optim-update-config",
                                                              params: { "optim-config-data": updatedConstraints },
                                                            });
                                                          }
                                                        } catch (err) {
                                                          console.error("Error updating time_limit:", err);
                                                        }
                                                      }}
                                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                            
                            {/* Riquadro PARAMETRI AGGIUNTIVI */}
                            {constraints && (() => {
                              const constraintsData = constraints.constraints || {};
                              
                              const hasTotalPieces = constraintsData.total_pieces !== undefined;
                              const hasNonMasterShare = constraintsData.non_master_share !== undefined;
                              const hasMinAllowedValue = constraintsData.min_allowed_value !== undefined;
                              const hasMinAllowedValuePerTipoFilato = constraintsData.min_allowed_value_per_tipo_filato !== undefined;
                              const hasMinActiveSkus = constraintsData.min_active_skus !== undefined;
                              const hasActiveConstraints = constraintsData.active_constraints !== undefined;
                              const hasForecastBias = constraintsData.forecast_bias !== undefined;
                              const hasPercentageColored = constraintsData.percentage_colored !== undefined;
                              const hasMinTotalQuantitiesForSplit = constraintsData.min_total_quantities_for_split !== undefined;
                              const hasParams = hasTotalPieces || hasNonMasterShare || hasMinAllowedValue || hasMinAllowedValuePerTipoFilato || hasMinActiveSkus || hasActiveConstraints || hasForecastBias || hasPercentageColored || hasMinTotalQuantitiesForSplit;
                              
                              return hasParams ? (
                                <div className="mb-2 relative">
                                  <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>PARAMETRI AGGIUNTIVI</h4>
                                  <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="grid grid-cols-3 gap-2">
                                      {/* Total Pieces - Checkbox */}
                                      {hasTotalPieces && (
                                        <div className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <label className="text-[10px] font-semibold text-gray-700 uppercase cursor-pointer">
                                            Total Pieces
                                          </label>
                                          <input
                                            type="checkbox"
                                            checked={constraintsData.total_pieces ?? false}
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const newValue = e.target.checked;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              updatedConstraints.constraints.total_pieces = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating total_pieces:", err);
                                              }
                                            }}
                                            className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Non Master Share - Slider 0-100 */}
                                      {hasNonMasterShare && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="text-[10px] font-semibold text-gray-700 uppercase">Non Master Share</div>
            
                                            {/* display as percentage */}
                                            <div className="text-xs font-bold text-gray-900">
                                              {((constraintsData.non_master_share ?? 0) * 100).toFixed(0)}%
                                            </div>
                                          </div>
            
                                          <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="1"
            
                                            // SLIDER ALWAYS USES PERCENTAGES
                                            value={(constraintsData.non_master_share ?? 0) * 100}
            
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const percentValue = parseFloat(e.target.value);  // 0–100
                                              const decimalValue = percentValue / 100;          // 0–1
            
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
            
                                              // update local UI state as decimal
                                              updatedConstraints.constraints.non_master_share = decimalValue;
                                              setConstraints(updatedConstraints);
            
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
            
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating non_master_share:", err);
                                              }
                                            }}
            
                                            className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                            style={{
                                              background: `linear-gradient(
                                                to right,
                                                #3b82f6 0%,
                                                #3b82f6 ${(constraintsData.non_master_share ?? 0) * 100}%,
                                                #d1d5db ${(constraintsData.non_master_share ?? 0) * 100}%,
                                                #d1d5db 100%
                                              )`
                                            }}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Min Allowed Value - Integer Input */}
                                      {hasMinAllowedValue && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Allowed Value</div>
                                          <CommitInput
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={constraintsData.min_allowed_value ?? 3}
                                            disabled={isConfigViewMode}
                                            onCommit={async (e) => {
                                              const newValue = parseInt(e.target.value);
                                              if (isNaN(newValue) || newValue < 0) return;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              updatedConstraints.constraints.min_allowed_value = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating min_allowed_value:", err);
                                              }
                                            }}
                                            className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      )}
                                      {hasMinAllowedValuePerTipoFilato && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Value per Tipo Filato</div>
                                          <div className="max-h-80 overflow-y-auto pr-1">
                                            {minAllowedValueTipoFilatoOverrides.map((override) => (
                                              <div key={override.id} className="mt-2 pt-2 first:mt-0 first:pt-0 border-t first:border-t-0 border-gray-200">
                                                <div className="flex items-center justify-between mb-1 gap-1">
                                                  <select
                                                    value={override.tipo_filato}
                                                    disabled={isConfigViewMode}
                                                    onChange={(e) => {
                                                      const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                                        item.id === override.id ? { ...item, tipo_filato: e.target.value } : item
                                                      );
                                                      setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                                      updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                                    }}
                                                    className="text-[8px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                                  >
                                                    <option value="">(seleziona tipo filato)</option>
                                                    {tipoFilatoEnum.map((tipo) => (
                                                      <option key={tipo} value={tipo}>{tipo}</option>
                                                    ))}
                                                  </select>
                                                  <button
                                                    type="button"
                                                    disabled={isConfigViewMode}
                                                    onClick={() => {
                                                      const newOverrides = minAllowedValueTipoFilatoOverrides.filter((item) => item.id !== override.id);
                                                      setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                                      updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                                    }}
                                                    className="text-[8px] px-1.5 py-0.5 text-red-600 hover:text-red-800 disabled:opacity-60 border border-red-300 rounded"
                                                  >
                                                    ✕
                                                  </button>
                                                </div>
                                                <input
                                                  type="number"
                                                  step="1"
                                                  min="0"
                                                  value={override.value}
                                                  disabled={isConfigViewMode}
                                                  onChange={(e) => {
                                                    const newValue = parseInt(e.target.value, 10);
                                                    if (isNaN(newValue) || newValue < 0) return;
                                                    const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                                      item.id === override.id ? { ...item, value: newValue } : item
                                                    );
                                                    setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                                    updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                                  }}
                                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                          <button
                                            type="button"
                                            disabled={isConfigViewMode}
                                            onClick={() => {
                                              const nextId = minAllowedValueTipoFilatoOverrides.length > 0
                                                ? Math.max(...minAllowedValueTipoFilatoOverrides.map((item) => item.id)) + 1
                                                : 1;
                                              setMinAllowedValueTipoFilatoOverrides([
                                                ...minAllowedValueTipoFilatoOverrides,
                                                { id: nextId, tipo_filato: '', value: constraintsData.min_allowed_value ?? 3 },
                                              ]);
                                            }}
                                            className="mt-2 w-full text-[8px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            + Specifica per Tipo Filato
                                          </button>
                                        </div>
                                      )}
                                      {hasMinActiveSkus && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Active SKUs</div>
                                          <CommitInput
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={constraintsData.min_active_skus ?? 0}
                                            disabled={isConfigViewMode}
                                            onCommit={async (e) => {
                                              const newValue = parseInt(e.target.value);
                                              if (isNaN(newValue) || newValue < 0) return;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              updatedConstraints.constraints.min_active_skus = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating min_active_skus:", err);
                                              }
                                            }}
                                            className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      )}
                                      {hasForecastBias && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Forecast Bias</div>
                                          <CommitInput
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={constraintsData.forecast_bias ?? 0}
                                            disabled={isConfigViewMode}
                                            onCommit={async (e) => {
                                              const newValue = parseInt(e.target.value);
                                              if (isNaN(newValue) || newValue < 0) return;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              updatedConstraints.constraints.forecast_bias = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating forecast_bias:", err);
                                              }
                                            }}
                                            className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      )}
                                      {hasPercentageColored && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="text-[10px] font-semibold text-gray-700 uppercase">Percentage Colored</div>
            
                                            {/* display as percentage */}
                                            <div className="text-xs font-bold text-gray-900">
                                              {((constraintsData.percentage_colored ?? 0) * 100).toFixed(0)}%
                                            </div>
                                          </div>
            
                                          <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="1"
            
                                            // SLIDER ALWAYS USES PERCENTAGES
                                            value={(constraintsData.percentage_colored ?? 0) * 100}
            
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const percentValue = parseFloat(e.target.value);  // 0–100
                                              const decimalValue = percentValue / 100;          // 0–1
            
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
            
                                              // update local UI state as decimal
                                              updatedConstraints.constraints.percentage_colored = decimalValue;
                                              setConstraints(updatedConstraints);
            
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
            
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating percentage_colored:", err);
                                              }
                                            }}
            
                                            className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                            style={{
                                              background: `linear-gradient(
                                                to right,
                                                #3b82f6 0%,
                                                #3b82f6 ${(constraintsData.percentage_colored ?? 0) * 100}%,
                                                #d1d5db ${(constraintsData.percentage_colored ?? 0) * 100}%,
                                                #d1d5db 100%
                                              )`
                                            }}
                                          />
                                        </div>
                                      )}
                                      {hasMinTotalQuantitiesForSplit && (
                                        <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Active SKUs</div>
                                          <CommitInput
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={constraintsData.min_total_quantities_for_split ?? 0}
                                            disabled={isConfigViewMode}
                                            onCommit={async (e) => {
                                              const newValue = parseInt(e.target.value);
                                              if (isNaN(newValue) || newValue < 0) return;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              updatedConstraints.constraints.min_total_quantities_for_split = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating min_total_quantities_for_split:", err);
                                              }
                                            }}
                                            className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                            
                            {/* Riquadro ACTIVE CONSTRAINTS */}
                            {constraints && (() => {
                              const constraintsData = constraints.constraints || {};
                              const activeConstraints = constraintsData.active_constraints || {};
                              
                              const hasActiveConstraints = constraintsData.active_constraints !== undefined;
                              const activeConstraintsEntries = Object.entries(activeConstraints);
                              
                              return hasActiveConstraints && activeConstraintsEntries.length > 0 ? (
                                <div className="mb-2 relative">
                                  <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>VINCOLI ATTIVI</h4>
                                  <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="space-y-2">
                                      {activeConstraintsEntries.map(([constraintName, isActive]) => (
                                        <div key={constraintName} className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                          <label className="text-[10px] font-semibold text-gray-700 uppercase cursor-pointer flex-1">
                                            {constraintName.replace(/_/g, ' ')}
                                          </label>
                                          <input
                                            type="checkbox"
                                            checked={!!isActive}
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const newValue = e.target.checked;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                              if (!updatedConstraints.constraints.active_constraints) {
                                                updatedConstraints.constraints.active_constraints = {};
                                              }
                                              updatedConstraints.constraints.active_constraints[constraintName] = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating active_constraints:", err);
                                              }
                                            }}
                                            className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })()}
            
                            {/* Riquadro SKU con vincoli attivi */}
                            {!isLoadingConstraints && (
                            <div className="mb-6 relative">
                              <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>SKU</h4>
                              <div className="border border-black p-4 pt-5" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {constraints && (
                                    <>
                                      {/* Mostra vincoli lower bound e upper bound per ogni SKU */}
                                      {(() => {
                                        const lbConstraints = constraints.constraints?.lb_quantity || {};
                                        const ubConstraints = constraints.constraints?.ub_quantity || {};
                                        
                                        // Crea un set di tutte le SKU che hanno almeno un vincolo
                                        const allSkusWithConstraints = new Set<string>();
                                        [...Object.keys(lbConstraints), ...Object.keys(ubConstraints)].forEach(sku => {
                                          allSkusWithConstraints.add(sku);
                                        });
            
                                        if (allSkusWithConstraints.size === 0) {
                                          return (
                                            <p className="text-sm text-gray-500 italic">
                                              Nessun vincolo attivo
                                            </p>
                                          );
                                        }
            
                                        return (
                                          <>
                                            {/* SKU con vincoli (inclusi esclusi) */}
                                            {Array.from(allSkusWithConstraints).map(skuName => {
                                              const lb = lbConstraints[skuName];
                                              const ub = ubConstraints[skuName];
                                              const isExcluded = lb === 0 && ub === 0;
                                              
                                              const handleRemoveConstraint = async () => {
                                                try {
                                                  const threadId = thread.threadId;
                                                  const messageId = thread.threadData?.local_memory?.thread_state?.[
                                                    "current-message"
                                                  ] as string;
            
                                                  if (!threadId || !messageId || !constraints) {
                                                    console.error("Missing required data for constraint removal");
                                                    return;
                                                  }
            
                                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
            
                                                  // Remove both lb and ub constraints for this SKU
                                                  if (updatedConstraints.constraints?.lb_quantity) {
                                                    delete updatedConstraints.constraints.lb_quantity[skuName];
                                                  }
                                                  if (updatedConstraints.constraints?.ub_quantity) {
                                                    delete updatedConstraints.constraints.ub_quantity[skuName];
                                                  }
            
                                                  // Update local state
                                                  setConstraints(updatedConstraints);
                                                  
                                                  // If it was excluded, remove from excluded set
                                                  if (isExcluded) {
                                                    const newExcludedSkus = new Set(excludedSkus);
                                                    newExcludedSkus.delete(skuName);
                                                    setExcludedSkus(newExcludedSkus);
                                                  }
            
                                                  // Update on server
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: {
                                                      "optim-config-data": updatedConstraints,
                                                    },
                                                  });
                                                } catch (err) {
                                                  console.error("Error removing constraint:", err);
                                                  alert("Errore durante la rimozione del vincolo");
                                                }
                                              };
                                              
                                              return (
                                                <div key={skuName} className="text-sm text-gray-700 pb-2 border-b border-gray-200 last:border-b-0 flex items-center justify-between gap-2">
                                                  <div className="font-medium flex-shrink-0">{skuName}</div>
                                                  <div className="flex items-center gap-2">
                                                    <div className="text-xs text-gray-600">
                                                      {isExcluded ? (
                                                        <span className="text-red-600 font-medium">Escluso</span>
                                                      ) : lb !== undefined && ub !== undefined ? (
                                                        <span>≥ {lb} e ≤ {ub}</span>
                                                      ) : lb !== undefined ? (
                                                        <span>≥ {lb}</span>
                                                      ) : ub !== undefined ? (
                                                        <span>≤ {ub}</span>
                                                      ) : null}
                                                    </div>
                                                    {!isConfigViewMode && (
                                                      <button
                                                        onClick={handleRemoveConstraint}
                                                        className="px-2 py-1 text-xs font-bold border border-black text-red-600 hover:bg-red-50 transition-colors"
                                                        title="Rimuovi vincolo"
                                                      >
                                                        ✕
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </>
                                        );
                                      })()}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            )}
            
                            {/* Optimization Progress */}
                            {!isLoadingConstraints && isOptimizing && !isConfigViewMode && (
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-blue-900">Ottimizzazione in corso...</span>
                                  <span className="text-sm font-bold text-blue-900">{optimizationProgress}%</span>
                                </div>
                                <div className="w-full bg-blue-200 h-2.5">
                                  <div 
                                    className="bg-blue-600 h-2.5 transition-all duration-500"
                                    style={{ width: `${optimizationProgress}%` }}
                                  ></div>
                                </div>
                                <p className="text-xs text-blue-700 mt-2">Stiamo elaborando i dati, attendere prego...</p>
                              </div>
                            )}
            
                            {!isLoadingConstraints && !isConfigViewMode && (
                              <div className="flex justify-end">
                                <button
                                  onClick={handleOptimize}
                                  disabled={isOptimizing}
                                  className="px-6 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{ backgroundColor: '#D4DEE8' }}
                                >
                                  {isOptimizing ? `OTTIMIZZAZIONE ${optimizationProgress}%` : "OTTIMIZZA"}
                                </button>
                              </div>
                            )}
                            </div>
                
                            {/* Resize handles */}
                            {/* Corner handles */}
                            <div className="absolute top-0 left-0 w-2 h-2 cursor-nw-resize" 
                                onMouseDown={(e) => startResize(e, 'nw')} />
                            <div className="absolute top-0 right-0 w-2 h-2 cursor-ne-resize" 
                                onMouseDown={(e) => startResize(e, 'ne')} />
                            <div className="absolute bottom-0 left-0 w-2 h-2 cursor-sw-resize" 
                                onMouseDown={(e) => startResize(e, 'sw')} />
                            <div className="absolute bottom-0 right-0 w-2 h-2 cursor-se-resize" 
                                onMouseDown={(e) => startResize(e, 'se')} />
                            
                            {/* Edge handles */}
                            <div className="absolute top-0 left-2 right-2 h-1 cursor-n-resize" 
                                onMouseDown={(e) => startResize(e, 'n')} />
                            <div className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize" 
                                onMouseDown={(e) => startResize(e, 's')} />
                            <div className="absolute left-0 top-2 bottom-2 w-1 cursor-w-resize" 
                                onMouseDown={(e) => startResize(e, 'w')} />
                            <div className="absolute right-0 top-2 bottom-2 w-1 cursor-e-resize" 
                                onMouseDown={(e) => startResize(e, 'e')} />
                          </div>
                        )}
          </div>
        ) : (
        <>
        {/* Tabs - "ALL" first, then consegnas (MAIN 1, MAIN 2, etc.) */}
        <div className="flex border-b border-gray-300 bg-white justify-between w-full overflow-x-auto">
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex">
              {/* SPJ-889: toggle tabella a tutto schermo (solo icona), a sinistra delle consegne */}
              <button
                type="button"
                onClick={() => setIsTableFullscreen((v) => !v)}
                title={isTableFullscreen ? 'Mostra il pannello superiore' : 'Tabella a tutto schermo'}
                className="px-3 py-3 text-lg leading-none text-gray-500 transition-colors hover:text-gray-900"
              >
                {isTableFullscreen ? '↕' : '⛶'}
              </button>
              {consegnas.map((consegna, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`px-6 py-3 font-medium transition-colors ${
                    activeTab === index
                      ? "border-b-2 border-black text-black"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {consegna.name.replace(/_/g, " ").toUpperCase()}
                </button>
              ))}
            </div>
            
            {/* Search Bar */}
            <div className="flex items-center gap-2 px-4 border-l border-gray-300 min-w-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Cerca SKU..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-36 min-w-[4rem]"
              />
              <input
                type="text"
                placeholder="Modello"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                title="Filtra per modello (contenuto nel codice SKU). Il tessuto è una substring del modello, quindi qui filtri anche per tessuto."
                className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-24 min-w-[3.5rem]"
              />
              <input
                type="text"
                placeholder="Colore"
                value={colorFilter}
                onChange={(e) => setColorFilter(e.target.value)}
                title="Filtra per colore (contenuto nel codice SKU)"
                className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-20 min-w-[3.5rem]"
              />
              <input
                type="text"
                placeholder="Tipo filato"
                value={tipoFilatoFilter}
                onChange={(e) => setTipoFilatoFilter(e.target.value)}
                title="Filtra per tipo filato (gruppo: CASHMERE, T-SHIRT, …)"
                className="px-3 py-1.5 border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 w-36 min-w-[3.5rem]"
              />
              {searchResults.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {currentSearchIndex + 1} / {searchResults.length}
                  </span>
                  <button
                    onClick={handlePreviousResult}
                    className="p-1 hover:bg-gray-200 rounded"
                    title="Risultato precedente"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextResult}
                    className="p-1 hover:bg-gray-200 rounded"
                    title="Risultato successivo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center px-4 gap-3 relative flex-shrink-0">
            {/* Yellow EDIT button for manual editing - hidden when optimization is active or in area view */}
            {!isOptimizationMode && !isAreaView && (
              <button
                onClick={handleManualEditClick}
                title="Modifica"
                className="px-2 xl:px-3 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5"
                style={{ backgroundColor: '#F5E6C8' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                <span className="hidden xl:inline">MODIFICA</span>
                <input
                  type="checkbox"
                  checked={isManualEditMode}
                  readOnly
                  className="hidden xl:inline-block w-4 h-4 text-black border-gray-300 focus:ring-black cursor-pointer"
                />
              </button>
            )}
            
            {/* Area view indicator - shown when in area view */}
            {isAreaView && !isOptimizationMode && (
              <div className="px-4 py-2 bg-purple-100 border border-purple-300 text-purple-700 font-medium flex items-center gap-2 rounded">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">Vista Area (sola lettura)</span>
              </div>
            )}
            
            {/* Blue CREATE NEW OPTIM. button for automatic optimization - hidden when manual edit is active */}
            {!isManualEditMode && !isAreaView && (
              <>
                <button
                  onClick={handleOptimizationClick}
                  title="Nuova ottimizzazione"
                  className="px-2 xl:px-3 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5"
                  style={{ backgroundColor: '#D4DEE8' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden xl:inline">NUOVA OTTIMIZZAZIONE</span>
                  <input
                    type="checkbox"
                    checked={isOptimizationMode}
                    readOnly
                    className="hidden xl:inline-block w-4 h-4 text-black border-gray-300 focus:ring-black cursor-pointer"
                  />
                </button>
                
                {/* Info button - displays config in read-only mode - hidden when in edit or optimization mode */}
                {!isManualEditMode && !isOptimizationMode && (
                  <button
                    onClick={handleConfigViewClick}
                    className="px-2 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center justify-center"
                    style={{ backgroundColor: '#E8EDF2' }}
                    title="View configuration"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </>
            )}
            
            {/* Tasto AGGIUNGI PRODOTTO giallo - appare in modalità modifica manuale */}
            {isManualEditMode && (
              <button
                onClick={() => setIsAddProductModalOpen(true)}
                className="px-4 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center gap-2"
                style={{ backgroundColor: '#F5E6C8' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                AGGIUNGI PRODOTTO
              </button>
            )}
            
            {/* Tasto AGGIUNGI PRODOTTO azzurro - appare in modalità ottimizzazione */}
            {isOptimizationMode && !isLoadingConstraints && (
              <button
                onClick={() => setIsAddProductModalOpen(true)}
                className="px-4 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity flex items-center gap-2"
                style={{ backgroundColor: '#D4DEE8' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                AGGIUNGI PRODOTTO
              </button>
            )}
            
            {/* Tasto SALVA - appare solo in modalità modifica manuale */}
            {isManualEditMode && (
              <button
                onClick={handleSaveManualEdit}
                className="px-4 py-2 bg-black text-white font-medium border border-black hover:opacity-80 transition-opacity"
              >
                SALVA
              </button>
            )}

            {/* Card ottimizzazione - appare quando isOptimizationMode o isConfigViewMode è attivo */}
            {(isOptimizationMode || isConfigViewMode) && (
              <div 
                className="fixed border border-gray-400 shadow-2xl bg-white"
                style={{ 
                  zIndex: 999999, 
                  left: `${cardPosition.x}px`,
                  top: `${cardPosition.y}px`,
                  width: `${cardWidth}px`,
                  height: `${cardHeight}px`,
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                {/* Title bar for dragging */}
                <div 
                  className="flex items-center justify-between px-3 py-2 border-b border-gray-300 cursor-move select-none"
                  style={{ backgroundColor: '#E8EDF2' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDraggingCard(true);
                    setDragStart({
                      x: e.clientX,
                      y: e.clientY,
                      cardX: cardPosition.x,
                      cardY: cardPosition.y
                    });
                  }}
                >
                  <h3 className="text-sm font-semibold text-gray-900">
                    {isConfigViewMode ? 'CONFIGURAZIONE CORRENTE' : 'NUOVI VINCOLI'}
                  </h3>
                  <div className="flex space-x-1">
                    {/* <button 
                      className="w-3 h-3 bg-yellow-400 rounded-full hover:bg-yellow-500"
                      title="Minimize"
                    />
                    <button 
                      className="w-3 h-3 bg-green-400 rounded-full hover:bg-green-500"
                      title="Maximize"
                    /> */}
                    <button 
                    // "text-gray-400 hover:text-gray-600 text-2xl leading-none"
                      className="text-gray-400 text-2xl leading-none hover:text-gray-400"
                      title="Close"
                      onClick={() => {
                        if (isConfigViewMode) {
                          setIsConfigViewMode(false);
                        } else {
                          setIsOptimizationMode(false);
                        }
                      }}
                    >
                    ×
                  </button>
                  </div>
                </div>

                {/* Scrollable content area */}
                <div className="h-full overflow-auto p-3" style={{ backgroundColor: '#E8EDF2', height: 'calc(100% - 40px)' }}>
                
                {/* Loading indicator while fetching constraints */}
                {isLoadingConstraints && (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="relative">
                      <svg className="animate-spin-in h-16 w-16 text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <p className="mt-4 text-sm font-medium text-gray-900">Caricamento vincoli...</p>
                    <p className="mt-1 text-xs text-gray-600">Attendere prego</p>
                  </div>
                )}
                
                {/* Riquadro CONFIGURAZIONI con parametri aggiuntivi */}
                {!isLoadingConstraints && constraints && (() => {
                  const objective = constraints.objective || {};
                  const preparation = constraints.preparation || {};
                  const solver = constraints.solver || {};
                  
                  const hasConfigs = objective.lambda_plus !== undefined || 
                                    objective.lambda_minus !== undefined || 
                                    objective.rho_profit !== undefined ||
                                    objective.lambda_avg_price !== undefined ||
                                    objective.lambda_price_slack !== undefined ||
                                    objective.lambda_tf_mix !== undefined ||
                                    preparation.expected_sellout !== undefined ||
                                    solver.type !== undefined ||
                                    solver.time_limit !== undefined;
                  
                  return hasConfigs ? (
                    <div className="mb-2 relative">
                      <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>CONFIGURAZIONI</h4>
                      <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                        
                        <div className="space-y-1">
                          {/* Objective Section */}
                          {(objective.lambda_plus !== undefined || objective.lambda_minus !== undefined || objective.rho_profit !== undefined || objective.lambda_avg_price !== undefined || objective.lambda_price_slack !== undefined || objective.lambda_tf_mix !== undefined) && (
                            <div>
                              <div className="text-[9px] font-bold text-gray-700 uppercase mb-0.5">Objective</div>
                              <div className="grid grid-cols-3 gap-1.5">
                                {objective.lambda_plus !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ+</div>
                                    <CommitInput
                                      type="number"
                                      step="0.1"
                                      value={objective.lambda_plus}
                                      disabled={isConfigViewMode}
                                      onCommit={async (e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (isNaN(newValue)) return;
                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                        if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                        updatedConstraints.objective.lambda_plus = newValue;
                                        setConstraints(updatedConstraints);
                                        
                                        try {
                                          const threadId = thread.threadId;
                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                          if (threadId && messageId) {
                                            await performAction({
                                              threadId,
                                              messageId,
                                              actionName: "optim-update-config",
                                              params: { "optim-config-data": updatedConstraints },
                                            });
                                          }
                                        } catch (err) {
                                          console.error("Error updating lambda_plus:", err);
                                        }
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                )}
                                {objective.lambda_minus !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ−</div>
                                    <CommitInput
                                      type="number"
                                      step="0.1"
                                      value={objective.lambda_minus}
                                      disabled={isConfigViewMode}
                                      onCommit={async (e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (isNaN(newValue)) return;
                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                        if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                        updatedConstraints.objective.lambda_minus = newValue;
                                        setConstraints(updatedConstraints);
                                        
                                        try {
                                          const threadId = thread.threadId;
                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                          if (threadId && messageId) {
                                            await performAction({
                                              threadId,
                                              messageId,
                                              actionName: "optim-update-config",
                                              params: { "optim-config-data": updatedConstraints },
                                            });
                                          }
                                        } catch (err) {
                                          console.error("Error updating lambda_minus:", err);
                                        }
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                )}
                                {objective.rho_profit !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">ρ Profit</div>
                                    <CommitInput
                                      type="number"
                                      step="0.1"
                                      value={objective.rho_profit}
                                      disabled={isConfigViewMode}
                                      onCommit={async (e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (isNaN(newValue)) return;
                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                        if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                        updatedConstraints.objective.rho_profit = newValue;
                                        setConstraints(updatedConstraints);
                                        
                                        try {
                                          const threadId = thread.threadId;
                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                          if (threadId && messageId) {
                                            await performAction({
                                              threadId,
                                              messageId,
                                              actionName: "optim-update-config",
                                              params: { "optim-config-data": updatedConstraints },
                                            });
                                          }
                                        } catch (err) {
                                          console.error("Error updating rho_profit:", err);
                                        }
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                )}
                                {objective.lambda_avg_price !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                  <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ Avg Price</div>
                                  <CommitInput
                                    type="number"
                                    step="0.1"
                                    value={objective.lambda_avg_price}
                                    disabled={isConfigViewMode}
                                    onCommit={async (e) => {
                                      const newValue = parseFloat(e.target.value);
                                      if (isNaN(newValue)) return;
                                      const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                      if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                      updatedConstraints.objective.lambda_avg_price = newValue;
                                      setConstraints(updatedConstraints);
                                      
                                      try {
                                        const threadId = thread.threadId;
                                        const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                        if (threadId && messageId) {
                                          await performAction({
                                            threadId,
                                            messageId,
                                            actionName: "optim-update-config",
                                            params: { "optim-config-data": updatedConstraints },
                                          });
                                        }
                                      } catch (err) {
                                        console.error("Error updating lambda_avg_price:", err);
                                      }
                                    }}
                                    className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                  />
                                </div>
                                )}
                                {objective.lambda_price_slack !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ Price Slack</div>
                                    <CommitInput
                                      type="number"
                                      step="0.1"
                                      value={objective.lambda_price_slack}
                                      disabled={isConfigViewMode}
                                      onCommit={async (e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (isNaN(newValue)) return;
                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                        if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                        updatedConstraints.objective.lambda_price_slack = newValue;
                                        setConstraints(updatedConstraints);

                                        try {
                                          const threadId = thread.threadId;
                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                          if (threadId && messageId) {
                                            await performAction({
                                              threadId,
                                              messageId,
                                              actionName: "optim-update-config",
                                              params: { "optim-config-data": updatedConstraints },
                                            });
                                          }
                                        } catch (err) {
                                          console.error("Error updating lambda_price_slack:", err);
                                        }
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                )}
                                {objective.lambda_tf_mix !== undefined && (
                                  <div className="border border-gray-300 rounded px-1.5 py-0.5" style={{ backgroundColor: '#E8EDF2' }}>
                                    <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">λ TF Mix</div>
                                    <CommitInput
                                      type="number"
                                      step="0.1"
                                      value={objective.lambda_tf_mix}
                                      disabled={isConfigViewMode}
                                      onCommit={async (e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (isNaN(newValue)) return;
                                        const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                        if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                        updatedConstraints.objective.lambda_tf_mix = newValue;
                                        setConstraints(updatedConstraints);

                                        try {
                                          const threadId = thread.threadId;
                                          const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                          if (threadId && messageId) {
                                            await performAction({
                                              threadId,
                                              messageId,
                                              actionName: "optim-update-config",
                                              params: { "optim-config-data": updatedConstraints },
                                            });
                                          }
                                        } catch (err) {
                                          console.error("Error updating lambda_tf_mix:", err);
                                        }
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Preparation and Solver Section - On same row */}
                          {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined || solver.type !== undefined || solver.time_limit !== undefined) && (
                            <div className="grid grid-cols-2 gap-2">
                              {/* Preparation Section */}
                              {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined) && (
                                <div>
                                  <div className="text-[8px] font-bold text-gray-700 uppercase mb-0.5">Preparation</div>
                                  <div className="border border-gray-300 rounded px-1.5 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                    {preparation.expected_sellout !== undefined && (
                                      <>
                                        {/* Global Expected Sellout */}
                                        <div className="flex items-center justify-between mb-0.5">
                                          <div className="text-[8px] font-semibold text-gray-600 uppercase">Expected Sellout (Globale)</div>
                                          <div className="text-[10px] font-bold text-gray-900">
                                            {preparation.expected_sellout.toFixed(2)}
                                          </div>
                                        </div>
                                        <input
                                          type="range"
                                          min="0"
                                          max="1"
                                          step="0.01"
                                          value={preparation.expected_sellout}
                                          disabled={isConfigViewMode}
                                          onChange={async (e) => {
                                            const newValue = parseFloat(e.target.value);
                                            const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                            if (!updatedConstraints.preparation) {
                                              updatedConstraints.preparation = {};
                                            }
                                            updatedConstraints.preparation.expected_sellout = newValue;
                                            setConstraints(updatedConstraints);
                                            
                                            try {
                                              const threadId = thread.threadId;
                                              const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                              if (threadId && messageId) {
                                                await performAction({
                                                  threadId,
                                                  messageId,
                                                  actionName: "optim-update-config",
                                                  params: {
                                                    "optim-config-data": updatedConstraints,
                                                  },
                                                });
                                              }
                                            } catch (err) {
                                              console.error("Error updating expected_sellout:", err);
                                            }
                                          }}
                                          className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                          style={{
                                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${preparation.expected_sellout * 100}%, #d1d5db ${preparation.expected_sellout * 100}%, #d1d5db 100%)`
                                          }}
                                        />
                                      </>
                                    )}
                                    
                                    {/* Per-Tipo-Filato Overrides */}
                                    {tipoFilatoOverrides.map((override) => (
                                      <div key={override.id} className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-1 gap-1">
                                          <select
                                            value={override.tipo_filato}
                                            disabled={isConfigViewMode}
                                            onChange={(e) => {
                                              const newOverrides = tipoFilatoOverrides.map(o =>
                                                o.id === override.id ? { ...o, tipo_filato: e.target.value } : o
                                              );
                                              setTipoFilatoOverrides(newOverrides);
                                              updateConstraintsWithOverrides(newOverrides);
                                            }}
                                            className="text-[8px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                          >
                                            <option value="">(seleziona tipo filato)</option>
                                            {tipoFilatoEnum.map(tipo => (
                                              <option key={tipo} value={tipo}>{tipo}</option>
                                            ))}
                                          </select>
                                          <button
                                            type="button"
                                            disabled={isConfigViewMode}
                                            onClick={() => {
                                              const newOverrides = tipoFilatoOverrides.filter(o => o.id !== override.id);
                                              setTipoFilatoOverrides(newOverrides);
                                              updateConstraintsWithOverrides(newOverrides);
                                            }}
                                            className="text-[8px] px-1.5 py-0.5 text-red-600 hover:text-red-800 disabled:opacity-60 border border-red-300 rounded"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                        <div className="flex items-center justify-between mb-0.5">
                                          <div className="text-[8px] font-semibold text-gray-600">Sellout</div>
                                          <div className="text-[10px] font-bold text-gray-900">
                                            {override.value.toFixed(2)}
                                          </div>
                                        </div>
                                        <input
                                          type="range"
                                          min="0"
                                          max="1"
                                          step="0.01"
                                          value={override.value}
                                          disabled={isConfigViewMode}
                                          onChange={(e) => {
                                            const newValue = parseFloat(e.target.value);
                                            const newOverrides = tipoFilatoOverrides.map(o =>
                                              o.id === override.id ? { ...o, value: newValue } : o
                                            );
                                            setTipoFilatoOverrides(newOverrides);
                                            updateConstraintsWithOverrides(newOverrides);
                                          }}
                                          className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60"
                                          style={{
                                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${override.value * 100}%, #d1d5db ${override.value * 100}%, #d1d5db 100%)`
                                          }}
                                        />
                                      </div>
                                    ))}
                                    
                                    {/* Add Override Button */}
                                    <button
                                      type="button"
                                      disabled={isConfigViewMode}
                                      onClick={() => {
                                        const nextId = tipoFilatoOverrides.length > 0 
                                          ? Math.max(...tipoFilatoOverrides.map(o => o.id)) + 1 
                                          : 1;
                                        setTipoFilatoOverrides([
                                          ...tipoFilatoOverrides,
                                          { id: nextId, tipo_filato: '', value: 0.5 }
                                        ]);
                                      }}
                                      className="mt-2 w-full text-[8px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      + Specifica per Tipo Filato
                                    </button>
                                    
                                    {/* Scale Orders Toggle */}
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <label className={`flex items-center justify-between ${isConfigViewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <span className="text-[8px] font-semibold text-gray-600 uppercase">Scale Orders</span>
                                        <div className="relative">
                                          <input
                                            type="checkbox"
                                            checked={preparation.scale_orders || false}
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const newValue = e.target.checked;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.preparation) {
                                                updatedConstraints.preparation = {};
                                              }
                                              updatedConstraints.preparation.scale_orders = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating scale_orders:", err);
                                              }
                                            }}
                                            className="sr-only peer"
                                          />
                                          <div className={`w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500 ${isConfigViewMode ? 'opacity-60' : ''}`}></div>
                                        </div>
                                      </label>
                                    </div>

                                    {preparation.force_first_three_taglie !== undefined && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <label className={`flex items-center justify-between ${isConfigViewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                          <span className="text-[8px] font-semibold text-gray-600 uppercase">Force Three Taglie</span>
                                          <div className="relative">
                                            <input
                                              type="checkbox"
                                              checked={preparation.force_first_three_taglie}
                                              disabled={isConfigViewMode}
                                              onChange={async (e) => {
                                                const newValue = e.target.checked;
                                                const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                if (!updatedConstraints.preparation) {
                                                  updatedConstraints.preparation = {};
                                                }
                                                updatedConstraints.preparation.force_first_three_taglie = newValue;
                                                setConstraints(updatedConstraints);
                                                
                                                try {
                                                  const threadId = thread.threadId;
                                                  const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                  if (threadId && messageId) {
                                                    await performAction({
                                                      threadId,
                                                      messageId,
                                                      actionName: "optim-update-config",
                                                      params: { "optim-config-data": updatedConstraints },
                                                    });
                                                  }
                                                } catch (err) {
                                                  console.error("Error updating force_first_three_taglie:", err);
                                                }
                                              }}
                                              className="sr-only peer"
                                            />
                                            <div className={`w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500 ${isConfigViewMode ? 'opacity-60' : ''}`}></div>
                                          </div>
                                        </label>
                                      </div>
                                    )}

                                    {preparation.taglie_sampling_method !== undefined && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-[8px] font-semibold text-gray-600 uppercase">Taglie Sampling</span>
                                          <select
                                            value={preparation.taglie_sampling_method}
                                            disabled={isConfigViewMode}
                                            onChange={async (e) => {
                                              const newValue = e.target.value;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.preparation) {
                                                updatedConstraints.preparation = {};
                                              }
                                              updatedConstraints.preparation.taglie_sampling_method = newValue;
                                              setConstraints(updatedConstraints);

                                              try {
                                                const threadId = thread.threadId;
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (threadId && messageId) {
                                                  await performAction({
                                                    threadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating taglie_sampling_method:", err);
                                              }
                                            }}
                                            className="text-[8px] px-1.5 py-0.5 border border-gray-300 rounded bg-white disabled:opacity-60"
                                          >
                                            <option value="legacy">legacy</option>
                                            <option value="hare_niemeyer">hare_niemeyer</option>
                                          </select>
                                        </div>
                                      </div>
                                    )}
                                    
                                  </div>
                                </div>
                              )}
                              
                              {/* Solver Section */}
                              {(solver.type !== undefined || solver.time_limit !== undefined) && (
                                <div>
                                  <div className="text-[8px] font-bold text-gray-700 uppercase mb-0.5">Solver</div>
                                  <div className="flex gap-1.5">
                                    {solver.type !== undefined && (
                                      <div className="border border-gray-300 rounded px-1.5 py-0.5 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                        <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">Type</div>
                                        <select
                                          value={solver.type}
                                          disabled={isConfigViewMode}
                                          onChange={async (e) => {
                                            const newValue = e.target.value;
                                            const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                            if (!updatedConstraints.solver) {
                                              updatedConstraints.solver = {};
                                            }
                                            updatedConstraints.solver.type = newValue;
                                            setConstraints(updatedConstraints);
                                            
                                            try {
                                              const threadId = thread.threadId;
                                              const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                              if (threadId && messageId) {
                                                await performAction({
                                                  threadId,
                                                  messageId,
                                                  actionName: "optim-update-config",
                                                  params: {
                                                    "optim-config-data": updatedConstraints,
                                                  },
                                                });
                                              }
                                            } catch (err) {
                                              console.error("Error updating solver type:", err);
                                            }
                                          }}
                                          className="w-full text-[10px] font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                          <option value="CBC">CBC</option>
                                          <option value="CPSAT">CPSAT</option>
                                        </select>
                                      </div>
                                    )}
                                    {solver.time_limit !== undefined && (
                                      <div className="border border-gray-300 rounded px-1.5 py-0.5 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                        <div className="text-[8px] font-semibold text-gray-600 uppercase mb-0.5">Time Limit (s)</div>
                                        <CommitInput
                                          type="number"
                                          step="1"
                                          min="0"
                                          value={solver.time_limit}
                                          disabled={isConfigViewMode}
                                          onCommit={async (e) => {
                                            const newValue = parseInt(e.target.value);
                                            if (isNaN(newValue) || newValue < 0) return;
                                            const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                            if (!updatedConstraints.solver) updatedConstraints.solver = {};
                                            updatedConstraints.solver.time_limit = newValue;
                                            setConstraints(updatedConstraints);
                                            
                                            try {
                                              const threadId = thread.threadId;
                                              const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                              if (threadId && messageId) {
                                                await performAction({
                                                  threadId,
                                                  messageId,
                                                  actionName: "optim-update-config",
                                                  params: { "optim-config-data": updatedConstraints },
                                                });
                                              }
                                            } catch (err) {
                                              console.error("Error updating time_limit:", err);
                                            }
                                          }}
                                          className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
                
                {/* Riquadro PARAMETRI AGGIUNTIVI */}
                {constraints && (() => {
                  const constraintsData = constraints.constraints || {};
                  
                  const hasTotalPieces = constraintsData.total_pieces !== undefined;
                  const hasNonMasterShare = constraintsData.non_master_share !== undefined;
                  const hasMinAllowedValue = constraintsData.min_allowed_value !== undefined;
                  const hasMinAllowedValuePerTipoFilato = constraintsData.min_allowed_value_per_tipo_filato !== undefined;
                  const hasMinActiveSkus = constraintsData.min_active_skus !== undefined;
                  const hasActiveConstraints = constraintsData.active_constraints !== undefined;
                  const hasForecastBias = constraintsData.forecast_bias !== undefined;
                  const hasPercentageColored = constraintsData.percentage_colored !== undefined;
                  const hasMinTotalQuantitiesForSplit = constraintsData.min_total_quantities_for_split !== undefined;
                  const hasParams = hasTotalPieces || hasNonMasterShare || hasMinAllowedValue || hasMinAllowedValuePerTipoFilato || hasMinActiveSkus || hasActiveConstraints || hasForecastBias || hasPercentageColored || hasMinTotalQuantitiesForSplit;
                  
                  return hasParams ? (
                    <div className="mb-2 relative">
                      <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>PARAMETRI AGGIUNTIVI</h4>
                      <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                        <div className="grid grid-cols-3 gap-2">
                          {/* Total Pieces - Checkbox */}
                          {hasTotalPieces && (
                            <div className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <label className="text-[10px] font-semibold text-gray-700 uppercase cursor-pointer">
                                Total Pieces
                              </label>
                              <input
                                type="checkbox"
                                checked={constraintsData.total_pieces ?? false}
                                disabled={isConfigViewMode}
                                onChange={async (e) => {
                                  const newValue = e.target.checked;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  updatedConstraints.constraints.total_pieces = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating total_pieces:", err);
                                  }
                                }}
                                className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                          
                          {/* Non Master Share - Slider 0-100 */}
                          {hasNonMasterShare && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-[10px] font-semibold text-gray-700 uppercase">Non Master Share</div>

                                {/* display as percentage */}
                                <div className="text-xs font-bold text-gray-900">
                                  {((constraintsData.non_master_share ?? 0) * 100).toFixed(0)}%
                                </div>
                              </div>

                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"

                                // SLIDER ALWAYS USES PERCENTAGES
                                value={(constraintsData.non_master_share ?? 0) * 100}

                                disabled={isConfigViewMode}
                                onChange={async (e) => {
                                  const percentValue = parseFloat(e.target.value);  // 0–100
                                  const decimalValue = percentValue / 100;          // 0–1

                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};

                                  // update local UI state as decimal
                                  updatedConstraints.constraints.non_master_share = decimalValue;
                                  setConstraints(updatedConstraints);

                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;

                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating non_master_share:", err);
                                  }
                                }}

                                className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{
                                  background: `linear-gradient(
                                    to right,
                                    #3b82f6 0%,
                                    #3b82f6 ${(constraintsData.non_master_share ?? 0) * 100}%,
                                    #d1d5db ${(constraintsData.non_master_share ?? 0) * 100}%,
                                    #d1d5db 100%
                                  )`
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Min Allowed Value - Integer Input */}
                          {hasMinAllowedValue && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Allowed Value</div>
                              <CommitInput
                                type="number"
                                step="1"
                                min="0"
                                value={constraintsData.min_allowed_value ?? 3}
                                disabled={isConfigViewMode}
                                onCommit={async (e) => {
                                  const newValue = parseInt(e.target.value);
                                  if (isNaN(newValue) || newValue < 0) return;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  updatedConstraints.constraints.min_allowed_value = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating min_allowed_value:", err);
                                  }
                                }}
                                className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                          {hasMinAllowedValuePerTipoFilato && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Value per Tipo Filato</div>
                              <div className="max-h-80 overflow-y-auto pr-1">
                                {minAllowedValueTipoFilatoOverrides.map((override) => (
                                  <div key={override.id} className="mt-2 pt-2 first:mt-0 first:pt-0 border-t first:border-t-0 border-gray-200">
                                    <div className="flex items-center justify-between mb-1 gap-1">
                                      <select
                                        value={override.tipo_filato}
                                        disabled={isConfigViewMode}
                                        onChange={(e) => {
                                          const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                            item.id === override.id ? { ...item, tipo_filato: e.target.value } : item
                                          );
                                          setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                          updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                        }}
                                        className="text-[8px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                      >
                                        <option value="">(seleziona tipo filato)</option>
                                        {tipoFilatoEnum.map((tipo) => (
                                          <option key={tipo} value={tipo}>{tipo}</option>
                                        ))}
                                      </select>
                                      <button
                                        type="button"
                                        disabled={isConfigViewMode}
                                        onClick={() => {
                                          const newOverrides = minAllowedValueTipoFilatoOverrides.filter((item) => item.id !== override.id);
                                          setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                          updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                        }}
                                        className="text-[8px] px-1.5 py-0.5 text-red-600 hover:text-red-800 disabled:opacity-60 border border-red-300 rounded"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                    <input
                                      type="number"
                                      step="1"
                                      min="0"
                                      value={override.value}
                                      disabled={isConfigViewMode}
                                      onChange={(e) => {
                                        const newValue = parseInt(e.target.value, 10);
                                        if (isNaN(newValue) || newValue < 0) return;
                                        const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                          item.id === override.id ? { ...item, value: newValue } : item
                                        );
                                        setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                        updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                      }}
                                      className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                ))}
                              </div>
                              <button
                                type="button"
                                disabled={isConfigViewMode}
                                onClick={() => {
                                  const nextId = minAllowedValueTipoFilatoOverrides.length > 0
                                    ? Math.max(...minAllowedValueTipoFilatoOverrides.map((item) => item.id)) + 1
                                    : 1;
                                  setMinAllowedValueTipoFilatoOverrides([
                                    ...minAllowedValueTipoFilatoOverrides,
                                    { id: nextId, tipo_filato: '', value: constraintsData.min_allowed_value ?? 3 },
                                  ]);
                                }}
                                className="mt-2 w-full text-[8px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                + Specifica per Tipo Filato
                              </button>
                            </div>
                          )}
                          {hasMinActiveSkus && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Active SKUs</div>
                              <CommitInput
                                type="number"
                                step="1"
                                min="0"
                                value={constraintsData.min_active_skus ?? 0}
                                disabled={isConfigViewMode}
                                onCommit={async (e) => {
                                  const newValue = parseInt(e.target.value);
                                  if (isNaN(newValue) || newValue < 0) return;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  updatedConstraints.constraints.min_active_skus = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating min_active_skus:", err);
                                  }
                                }}
                                className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                          {hasForecastBias && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Forecast Bias</div>
                              <CommitInput
                                type="number"
                                step="1"
                                min="0"
                                value={constraintsData.forecast_bias ?? 0}
                                disabled={isConfigViewMode}
                                onCommit={async (e) => {
                                  const newValue = parseInt(e.target.value);
                                  if (isNaN(newValue) || newValue < 0) return;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  updatedConstraints.constraints.forecast_bias = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating forecast_bias:", err);
                                  }
                                }}
                                className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                          {hasPercentageColored && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-[10px] font-semibold text-gray-700 uppercase">Percentage Colored</div>

                                {/* display as percentage */}
                                <div className="text-xs font-bold text-gray-900">
                                  {((constraintsData.percentage_colored ?? 0) * 100).toFixed(0)}%
                                </div>
                              </div>

                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"

                                // SLIDER ALWAYS USES PERCENTAGES
                                value={(constraintsData.percentage_colored ?? 0) * 100}

                                disabled={isConfigViewMode}
                                onChange={async (e) => {
                                  const percentValue = parseFloat(e.target.value);  // 0–100
                                  const decimalValue = percentValue / 100;          // 0–1

                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};

                                  // update local UI state as decimal
                                  updatedConstraints.constraints.percentage_colored = decimalValue;
                                  setConstraints(updatedConstraints);

                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;

                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating percentage_colored:", err);
                                  }
                                }}

                                className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{
                                  background: `linear-gradient(
                                    to right,
                                    #3b82f6 0%,
                                    #3b82f6 ${(constraintsData.percentage_colored ?? 0) * 100}%,
                                    #d1d5db ${(constraintsData.percentage_colored ?? 0) * 100}%,
                                    #d1d5db 100%
                                  )`
                                }}
                              />
                            </div>
                          )}
                          {hasMinTotalQuantitiesForSplit && (
                            <div className="border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">Min Active SKUs</div>
                              <CommitInput
                                type="number"
                                step="1"
                                min="0"
                                value={constraintsData.min_total_quantities_for_split ?? 0}
                                disabled={isConfigViewMode}
                                onCommit={async (e) => {
                                  const newValue = parseInt(e.target.value);
                                  if (isNaN(newValue) || newValue < 0) return;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  updatedConstraints.constraints.min_total_quantities_for_split = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating min_total_quantities_for_split:", err);
                                  }
                                }}
                                className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
                
                {/* Riquadro ACTIVE CONSTRAINTS */}
                {constraints && (() => {
                  const constraintsData = constraints.constraints || {};
                  const activeConstraints = constraintsData.active_constraints || {};
                  
                  const hasActiveConstraints = constraintsData.active_constraints !== undefined;
                  const activeConstraintsEntries = Object.entries(activeConstraints);
                  
                  return hasActiveConstraints && activeConstraintsEntries.length > 0 ? (
                    <div className="mb-2 relative">
                      <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>VINCOLI ATTIVI</h4>
                      <div className="border border-black p-2 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                        <div className="space-y-2">
                          {activeConstraintsEntries.map(([constraintName, isActive]) => (
                            <div key={constraintName} className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <label className="text-[10px] font-semibold text-gray-700 uppercase cursor-pointer flex-1">
                                {constraintName.replace(/_/g, ' ')}
                              </label>
                              <input
                                type="checkbox"
                                checked={!!isActive}
                                disabled={isConfigViewMode}
                                onChange={async (e) => {
                                  const newValue = e.target.checked;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  if (!updatedConstraints.constraints.active_constraints) {
                                    updatedConstraints.constraints.active_constraints = {};
                                  }
                                  updatedConstraints.constraints.active_constraints[constraintName] = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const threadId = thread.threadId;
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (threadId && messageId) {
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error("Error updating active_constraints:", err);
                                  }
                                }}
                                className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Riquadro SKU con vincoli attivi */}
                {!isLoadingConstraints && (
                <div className="mb-6 relative">
                  <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>SKU</h4>
                  <div className="border border-black p-4 pt-5" style={{ backgroundColor: '#E8EDF2' }}>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {constraints && (
                        <>
                          {/* Mostra vincoli lower bound e upper bound per ogni SKU */}
                          {(() => {
                            const lbConstraints = constraints.constraints?.lb_quantity || {};
                            const ubConstraints = constraints.constraints?.ub_quantity || {};
                            
                            // Crea un set di tutte le SKU che hanno almeno un vincolo
                            const allSkusWithConstraints = new Set<string>();
                            [...Object.keys(lbConstraints), ...Object.keys(ubConstraints)].forEach(sku => {
                              allSkusWithConstraints.add(sku);
                            });

                            if (allSkusWithConstraints.size === 0) {
                              return (
                                <p className="text-sm text-gray-500 italic">
                                  Nessun vincolo attivo
                                </p>
                              );
                            }

                            return (
                              <>
                                {/* SKU con vincoli (inclusi esclusi) */}
                                {Array.from(allSkusWithConstraints).map(skuName => {
                                  const lb = lbConstraints[skuName];
                                  const ub = ubConstraints[skuName];
                                  const isExcluded = lb === 0 && ub === 0;
                                  
                                  const handleRemoveConstraint = async () => {
                                    try {
                                      const threadId = thread.threadId;
                                      const messageId = thread.threadData?.local_memory?.thread_state?.[
                                        "current-message"
                                      ] as string;

                                      if (!threadId || !messageId || !constraints) {
                                        console.error("Missing required data for constraint removal");
                                        return;
                                      }

                                      const updatedConstraints = JSON.parse(JSON.stringify(constraints));

                                      // Remove both lb and ub constraints for this SKU
                                      if (updatedConstraints.constraints?.lb_quantity) {
                                        delete updatedConstraints.constraints.lb_quantity[skuName];
                                      }
                                      if (updatedConstraints.constraints?.ub_quantity) {
                                        delete updatedConstraints.constraints.ub_quantity[skuName];
                                      }

                                      // Update local state
                                      setConstraints(updatedConstraints);
                                      
                                      // If it was excluded, remove from excluded set
                                      if (isExcluded) {
                                        const newExcludedSkus = new Set(excludedSkus);
                                        newExcludedSkus.delete(skuName);
                                        setExcludedSkus(newExcludedSkus);
                                      }

                                      // Update on server
                                      await performAction({
                                        threadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: {
                                          "optim-config-data": updatedConstraints,
                                        },
                                      });
                                    } catch (err) {
                                      console.error("Error removing constraint:", err);
                                      alert("Errore durante la rimozione del vincolo");
                                    }
                                  };
                                  
                                  return (
                                    <div key={skuName} className="text-sm text-gray-700 pb-2 border-b border-gray-200 last:border-b-0 flex items-center justify-between gap-2">
                                      <div className="font-medium flex-shrink-0">{skuName}</div>
                                      <div className="flex items-center gap-2">
                                        <div className="text-xs text-gray-600">
                                          {isExcluded ? (
                                            <span className="text-red-600 font-medium">Escluso</span>
                                          ) : lb !== undefined && ub !== undefined ? (
                                            <span>≥ {lb} e ≤ {ub}</span>
                                          ) : lb !== undefined ? (
                                            <span>≥ {lb}</span>
                                          ) : ub !== undefined ? (
                                            <span>≤ {ub}</span>
                                          ) : null}
                                        </div>
                                        {!isConfigViewMode && (
                                          <button
                                            onClick={handleRemoveConstraint}
                                            className="px-2 py-1 text-xs font-bold border border-black text-red-600 hover:bg-red-50 transition-colors"
                                            title="Rimuovi vincolo"
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                )}

                {/* Optimization Progress */}
                {!isLoadingConstraints && isOptimizing && !isConfigViewMode && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-900">Ottimizzazione in corso...</span>
                      <span className="text-sm font-bold text-blue-900">{optimizationProgress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 transition-all duration-500"
                        style={{ width: `${optimizationProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">Stiamo elaborando i dati, attendere prego...</p>
                  </div>
                )}

                {!isLoadingConstraints && !isConfigViewMode && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleOptimize}
                      disabled={isOptimizing}
                      className="px-6 py-2 border border-black text-black font-medium hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#D4DEE8' }}
                    >
                      {isOptimizing ? `OTTIMIZZAZIONE ${optimizationProgress}%` : "OTTIMIZZA"}
                    </button>
                  </div>
                )}
                </div>
    
                {/* Resize handles */}
                {/* Corner handles */}
                <div className="absolute top-0 left-0 w-2 h-2 cursor-nw-resize" 
                    onMouseDown={(e) => startResize(e, 'nw')} />
                <div className="absolute top-0 right-0 w-2 h-2 cursor-ne-resize" 
                    onMouseDown={(e) => startResize(e, 'ne')} />
                <div className="absolute bottom-0 left-0 w-2 h-2 cursor-sw-resize" 
                    onMouseDown={(e) => startResize(e, 'sw')} />
                <div className="absolute bottom-0 right-0 w-2 h-2 cursor-se-resize" 
                    onMouseDown={(e) => startResize(e, 'se')} />
                
                {/* Edge handles */}
                <div className="absolute top-0 left-2 right-2 h-1 cursor-n-resize" 
                    onMouseDown={(e) => startResize(e, 'n')} />
                <div className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize" 
                    onMouseDown={(e) => startResize(e, 's')} />
                <div className="absolute left-0 top-2 bottom-2 w-1 cursor-w-resize" 
                    onMouseDown={(e) => startResize(e, 'w')} />
                <div className="absolute right-0 top-2 bottom-2 w-1 cursor-e-resize" 
                    onMouseDown={(e) => startResize(e, 'e')} />
              </div>
            )}
          </div>
        </div>

        {/* Table content */}
        <div className="flex-1 overflow-auto bg-white">
          <table className="w-full border-collapse" style={{ tableLayout: 'auto' }}>
            <thead className="sticky top-0 bg-gray-100 border-b-2 border-gray-300 z-20">
              <tr>
                <th className="font-semibold text-gray-700 text-left px-4 py-3 whitespace-nowrap sticky left-0 bg-gray-100 z-30 min-w-[350px]" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>
                  <div className="flex flex-col gap-1">
                    <span>TIPO FILATO</span>
                    {/* <div className="flex flex-wrap gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      {renderSortableHeader('Ordini passati', 'order_quantity')}
                      {renderSortableHeader('Run collegata', 'linked_order_quantity')}
                    </div> */}
                  </div>
                </th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PROFITTABILITÀ', 'profit')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PREZZO MEDIO', 'mean_price')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PREZZO MEDIO WHS', 'mean_weight')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('SELL-THROUGH', 'sellout')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('MODELLI UNICI', 'models_unique')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PRODOTTI UNICI', 'unique_skus')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('PRODOTTI TOTALI', 'total_quantity', 'Ordinamento iniziale: quantità ottimizzata decrescente per questo tab')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">{renderSortableHeader('FORECAST QUANTITY', 'forecast_quantity')}</th>
                <th className="font-semibold text-gray-700 text-center px-4 py-3 whitespace-nowrap text-center">APERTURA TAGLIE</th>
              </tr>
            </thead>

            {/* Table rows */}
            <tbody ref={tableBodyRef}>
            {currentConsegnaData && (
            <>
            {/* TOTALE row - expandable */}
            {renderMetricsRow(
              "TOTALE",
              currentConsegnaData.all.profit,
              currentConsegnaData.all.mean_price,
              currentConsegnaData.all.unique_skus,
              currentConsegnaData.all.total_quantity,
              0,
              currentConsegnaData.all.mean_weight,
              currentConsegnaData.all.total_sellout_value_mean,
              currentConsegnaData.all.models_unique,
              currentConsegnaData.all.forecast_quantity,
              currentConsegnaData.all.taglie_distribution,
              `totale_${activeTab}`,
              currentConsegnaData.tf.length > 0,
              undefined,
              undefined,
              undefined,
              undefined,
              currentConsegnaData.all.url_img_list,
              currentConsegnaData.all.sketch_url,
              undefined
            )}

            {/* TF rows (CABAN, JEANS, etc.) - shown when TOTALE is expanded */}
            {expanded[`totale_${activeTab}`] &&
              currentConsegnaData.tf.map((tfData, tfIndex) => {
                const tfExpandKey = `tf_${activeTab}_${tfIndex}`;
                if (!tfMatchesFilter(tfData)) return null;

                return (
                  <>
                    {renderMetricsRow(
                      tfData.name,
                      tfData.all.profit,
                      tfData.all.mean_price,
                      tfData.all.unique_skus,
                      tfData.all.total_quantity,
                      1,
                      tfData.all.mean_weight,
                      tfData.all.total_sellout_value_mean,
                      tfData.all.models_unique,
                      tfData.all.forecast_quantity,
                      tfData.all.taglie_distribution,
                      tfExpandKey,
                      tfData.sku.length > 0, // Enable expansion if there are SKUs
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      tfData.all.url_img_list,
                      tfData.all.sketch_url,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      false,
                      tfData.taglie_reference_distributions
                    )}

                    {/* SKU-level rows - shown when TF is expanded */}
                    {expanded[tfExpandKey] &&
                      sortSkusForCurrentView(tfData.sku).filter((skuData) => skuMatchesFilter(skuData.name, tfData.name)).map((skuData, skuIndex) => {
                        return (
                          <>
                            {renderMetricsRow(
                              skuData.name,
                              skuData.metrics.profit,
                              skuData.metrics.mean_price,
                              skuData.metrics.unique_skus,
                              skuData.metrics.total_quantity,
                              2, // Level 2 indentation for SKUs
                              skuData.metrics.mean_weight,
                              skuData.metrics.total_sellout_value_mean,
                              skuData.metrics.models_unique,
                              skuData.metrics.forecast_quantity,
                              skuData.metrics.taglie_distribution,
                              undefined,
                              false,
                              originalConsegnaIndex,
                              tfIndex,
                              skuIndex,
                              skuData.name,
                              skuData.metrics.url_img_list,
                              skuData.metrics.sketch_url,
                              skuData.metrics.weborder_tags,
                              skuData.metrics.order_quantity,
                              skuData.metrics.taglie_order,
                              skuData.metrics.order_quantity_venduta,
                              skuData.metrics.taglie_order_venduta,
                              false,
                              undefined,
                              skuData.metrics.linked_order_quantity,
                              skuData.metrics.taglie_linked_order,
                              skuData.metrics.descrizione_colore
                            )}
                          </>
                        );
                      })}
                  </>
                );
              })}
            </>
            )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      {/* Details Modal */}
      <DetailsModal
        sku={detailsModalSku}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setDetailsModalSku(null);
        }}
        onSkuChange={(newSku) => {
          setDetailsModalSku(newSku);
          setIsDetailsModalOpen(true);
        }}
        projectType="optimization"
      />

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => setIsAddProductModalOpen(false)}
        onAddProducts={handleAddProducts}
        existingSkus={existingSkus}
        isOptimizationMode={isOptimizationMode}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        // on close set alert modal to false and call handleOptimizationClick
        onClose={async () => {
          setAlertModal({ ...alertModal, isOpen: false });
          if (thread.threadId) {
            const forkResult = await performAction({
              threadId: thread.threadId,
              messageId: thread.threadData?.local_memory?.thread_state?.["current-message"] as string || '',
              actionName: "optim-fork-config",
              params: {},
            });
          }
        }}
        title={alertModal.title}
        message={alertModal.message}
        errorLog={alertModal.errorLog}
        type={alertModal.type}
      />

      </div>

      {/* Anteprima immagine: nel portale come l'order popup, cosi' non la ritaglia
          l'overflow della tabella e non se la mangia lo stacking context. */}
      {imagePreview && imagePreviewPortalContainer && createPortal(
        <div
          className="pointer-events-none fixed"
          style={{
            zIndex: 999999,
            left: Math.min(imagePreview.x + 16, window.innerWidth - 232),
            top: Math.min(imagePreview.y + 16, window.innerHeight - 400),
          }}
        >
          <div className="flex flex-col items-center gap-1 rounded border border-gray-300 bg-white p-2 shadow-2xl">
            <img
              src={imagePreview.url}
              alt={imagePreview.label}
              className="h-[334px] w-[200px] rounded object-cover"
            />
            <div className="max-w-[200px] truncate text-center text-xs font-semibold text-gray-900">
              {imagePreview.label}
            </div>
            {imagePreview.colore && (
              <div className="max-w-[200px] truncate text-center text-[11px] text-gray-600">{imagePreview.colore}</div>
            )}
            {(imagePreview.rtl !== undefined || imagePreview.whs !== undefined) && (
              <div className="flex gap-3 text-[11px] text-gray-700">
                {imagePreview.rtl !== undefined && <span>RTL <span className="font-semibold tabular-nums">{formatPreviewPrice(imagePreview.rtl)}</span></span>}
                {imagePreview.whs !== undefined && <span>WHS <span className="font-semibold tabular-nums">{formatPreviewPrice(imagePreview.whs)}</span></span>}
              </div>
            )}
          </div>
        </div>,
        imagePreviewPortalContainer,
      )}

      {pinnedForecast && orderPopupPortalContainer && createPortal(
        <ForecastProductsPanel
          anchorRect={pinnedForecast.rect}
          sku={pinnedForecast.sku}
          shopIds={parseShopIds(currentSelectedShop)}
          forecast={pinnedForecast.forecast}
          load={loadForecastProducts}
          onClose={() => setPinnedForecast(null)}
        />,
        orderPopupPortalContainer
      )}

      {/* Order Popup Portal - rendered at body level to avoid z-index/stacking issues */}
      {pinnedOrders && orderPopupPortalContainer && createPortal(
        <OrdersByShopPanel
          anchorRect={pinnedOrders.rect}
          sku={pinnedOrders.sku}
          baseData={pinnedOrders.data}
          defaultShopIds={
            isAreaView
              ? storesList.filter((store) => !store.isArea).flatMap((store) => parseShopIds(store.id))
              : parseShopIds(currentSelectedShop)
          }
          load={loadSkuOrdersByShop}
          compareTaglie={compareTaglie}
          onClose={() => setPinnedOrders(null)}
        />,
        orderPopupPortalContainer
      )}
      {orderPopup && orderPopup.visible && !pinnedOrders && orderPopupPortalContainer && createPortal(
        <TooltipShell x={orderPopup.x} y={orderPopup.y}>
          <OrderTooltipContent
            data={{
              orderQuantity: orderPopup.orderQuantity,
              taglieOrder: orderPopup.taglieOrder,
              orderQuantityVenduta: orderPopup.orderQuantityVenduta,
              taglieOrderVenduta: orderPopup.taglieOrderVenduta,
              linkedOrderQuantity: orderPopup.linkedOrderQuantity,
              taglieLinkedOrder: orderPopup.taglieLinkedOrder,
            }}
            compareTaglie={compareTaglie}
          />
          {orderPopup.canPin && <PinOrdersHint />}
        </TooltipShell>,
        orderPopupPortalContainer
      )}
    </>
  );
}
